/**
 * <pp-assistant> — custom element del shopping assistant "Paco".
 *
 * UX:
 *   - FAB (floating action button) bottom-right en todas las páginas
 *   - Click → abre overlay full-screen (mobile) o card lateral (desktop)
 *   - Streaming SSE de /apps/assistant/api/chat con cada text_delta
 *     apareciendo letra a letra
 *   - Conversation state en localStorage (session_id + conversation_id)
 *
 * Activación cross-section:
 *   Cualquier elemento del theme puede abrir el assistant con un mensaje
 *   inicial despachando:
 *     document.dispatchEvent(new CustomEvent('pp-assistant:open', {
 *       detail: { message: 'necesito cajas para mudanza' }
 *     }));
 *
 *   El form del pp-home-hero-ai.liquid lo usa para que el typewriter
 *   search bar abra Paco con la query escrita.
 *
 * Seguridad: NO usa innerHTML con contenido del usuario. Toda interpolación
 * va vía createElement + textContent. El único innerHTML es para el SVG
 * del icono del FAB que es totalmente estático.
 */
(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────────
     Sticky-bar awareness (PDP): cuando aparece la sticky buy-bar del
     producto, subimos el FAB para que no quede encima del CTA.

     Self-contained: MutationObserver directo sobre el sticky bar — no
     depende del body class de pp-main-product.js (que puede estar
     cacheado en CDN). Aplica style.bottom inline.
     ──────────────────────────────────────────────────────────────── */
  /**
   * ¿Cuándo responde un asesor? (3-oct-2026, captura del dueño, sábado 13:02:
   * el chat decía «en un momento» en fin de semana). Horario de atención:
   * lunes a viernes 9:00-18:00, hora de Ciudad de México (sin horario de
   * verano). Misma regla que pp-assistant/lib/business-hours.ts nextOpenLabel.
   */
  function cuandoRespondeAsesor(fecha) {
    var mx = new Date((fecha || new Date()).getTime() - 6 * 60 * 60 * 1000);
    var dia = mx.getUTCDay();
    var hora = mx.getUTCHours();
    if (dia >= 1 && dia <= 5 && hora >= 9 && hora < 18) return 'en un momento';
    if (dia >= 1 && dia <= 5 && hora < 9) return 'hoy a las 9:00';
    if (dia >= 1 && dia <= 4) return 'mañana a las 9:00';
    if (dia === 0) return 'mañana lunes a las 9:00';
    return 'el lunes a las 9:00';
  }

  /** El mensaje de respaldo cuando no hay respuesta del servidor. */
  function respaldoAsesor() {
    var cuando = cuandoRespondeAsesor();
    return cuando === 'en un momento'
      ? 'Un asesor te atiende aquí mismo en un momento. ¿Me dejas tu nombre y un WhatsApp para confirmarte cuando entre?'
      : 'Ahorita estamos fuera de horario (lunes a viernes de 9:00 a 18:00). Un asesor te contacta ' + cuando + ', aquí mismo en el chat. ¿Me dejas tu nombre y un WhatsApp para avisarte?';
  }

  /** «Chat» de la barra de abajo con su punto de «mensaje nuevo» (4-oct-2026):
   * en celular el flotante ya no se pinta donde está la barra, así que el
   * aviso del asesor también va ahí. */
  function marcarChatNuevo(si) {
    document.querySelectorAll('[data-pp-mobile-tab-chat]').forEach((b) => b.classList.toggle('pp-mtb__item--nuevo', !!si));
  }

  function bindStickyAwareness() {
    const stickyBar = document.querySelector('.pp-main-product__sticky');
    const fab = document.querySelector('.pp-assistant__fab');
    if (!stickyBar || !fab) return;

    const sync = () => {
      const visible = stickyBar.classList.contains('is-visible');
      // 78px fijo por preferencia del user — el cálculo dinámico (height
      // + 20) dejaba demasiado breath y se veía despegado del sticky.
      fab.style.bottom = visible ? '78px' : '';
    };

    const obs = new MutationObserver(sync);
    obs.observe(stickyBar, { attributes: true, attributeFilter: ['class'] });
    sync();
  }
  /* ─────────────────────────────────────────────────────────────────
     Footer awareness: la burbuja cambia de color al llegar al footer.
     El footer del sitio comparte el navy del FAB, así que se funden.
     Con IntersectionObserver detectamos cuando el footer entra en la
     zona del FAB (parte baja del viewport) y togglamos --on-dark.
     ──────────────────────────────────────────────────────────────── */
  function bindFooterAwareness() {
    const footer = document.querySelector('.pp-footer');
    const fab = document.querySelector('.pp-assistant__fab');
    if (!footer || !fab || !('IntersectionObserver' in window)) return;

    // Con la cajita no aplica: este efecto interpola el FONDO de la píldora
    // para que no se pierda contra el footer oscuro, y la cajita no tiene
    // fondo — es un dibujo que se lee igual sobre cualquier color. Dejarlo
    // correr sería gastar un rAF por scroll para no pintar nada.
    if (fab.classList.contains('pp-assistant__fab--cajita')) return;

    // El fondo del FAB se interpola navy → cyan según CUÁNTO lo cubre el footer,
    // para que el color "se meta" en vivo con el scroll (no un toggle seco).
    const NAVY = [14, 58, 92];   // #0E3A5C (base)
    const CYAN = [8, 145, 178];  // #0891b2 (sobre el footer)
    const mix = (a, b, t) => Math.round(a + (b - a) * t);

    let rafId = null;
    let active = false;

    const paint = () => {
      rafId = null;
      const fr = fab.getBoundingClientRect();
      const footerTop = footer.getBoundingClientRect().top;
      // 0 = el footer apenas toca el borde inferior del FAB; 1 = ya lo cubre.
      const p = Math.max(0, Math.min(1, (fr.bottom - footerTop) / fr.height));
      fab.style.background =
        'rgb(' + mix(NAVY[0], CYAN[0], p) + ',' + mix(NAVY[1], CYAN[1], p) + ',' + mix(NAVY[2], CYAN[2], p) + ')';
      // El glow cyan entra pasada la mitad del recorrido.
      fab.classList.toggle('pp-assistant__fab--on-dark', p > 0.5);
    };

    const onScroll = () => {
      if (rafId == null) rafId = requestAnimationFrame(paint);
    };

    const io = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting && !active) {
        active = true;
        fab.classList.add('pp-assistant__fab--scrubbing');
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        paint();
      } else if (!entries[0].isIntersecting && active) {
        active = false;
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
        if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
        fab.classList.remove('pp-assistant__fab--scrubbing', 'pp-assistant__fab--on-dark');
        fab.style.background = ''; // vuelve suave al navy del CSS
      }
    }, { threshold: 0 });
    io.observe(footer);
  }

  function initFabAwareness() {
    bindStickyAwareness();
    bindFooterAwareness();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFabAwareness);
  } else {
    // Pequeño delay para asegurar que el FAB ya se renderizó por el
    // custom element constructor.
    setTimeout(initFabAwareness, 100);
  }

  const STORAGE_KEY_SESSION = 'pp_assistant_session_id';
  const STORAGE_KEY_CONV = 'pp_assistant_conversation_id';
  const STORAGE_KEY_LEAD = 'pp_assistant_lead_state'; // 'saved' | 'dismissed' | null
  const STORAGE_KEY_RECOMMENDED = 'pp_assistant_recommended_products';
  // Productos recomendados se persisten en localStorage para que sobrevivan
  // a page navigations. Key contiene array serializado de ProductData. TTL
  // implícito: hasta que el cliente resetee la conversación (ese flujo
  // limpia este key también, ver _resetConversation).
  const LEAD_PROMPT_AFTER_TURNS = 3;

  // ── Modo prueba (4-oct-2026) ──────────────────────────────────────────
  // Las pruebas automáticas en vivo (puppeteer) llenaban la bandeja de los
  // vendedores con conversaciones de prueba. Un script de prueba pone
  // `localStorage.pp_paco_prueba = '1'` ANTES de abrir Paco y cada POST del
  // motor a /apps/assistant/api/* lleva `prueba: true` en el cuerpo (no
  // header: el App Proxy no garantiza reenviar headers propios). El backend
  // (pp-assistant, docs/MODO-PRUEBA.md) contesta igual pero NADA llega al
  // CRM. No hay UI para activarlo. La bandera sólo QUITA efectos: un cliente
  // que la ponga a mano sólo consigue que su plática no le llegue a nadie.
  const STORAGE_KEY_PRUEBA = 'pp_paco_prueba';
  function enModoPrueba() {
    try {
      return window.localStorage.getItem(STORAGE_KEY_PRUEBA) === '1';
    } catch (e) {
      return false;
    }
  }
  // `undefined` fuera de modo prueba: JSON.stringify omite la llave y el
  // cuerpo queda idéntico al de siempre.
  function banderaPrueba() {
    return enModoPrueba() ? true : undefined;
  }
  // «Paco emite cotizaciones» EN PRUEBA (12-oct-2026): con modo prueba Y
  // `localStorage.pp_paco_cotiza = 'si'`, el chat pide `cotiza: 'si'`. El
  // backend sólo lo lee en modo prueba (fuera de él ni lo mira) y ahí la
  // cotización sale SIMULADA: es para ver el flujo completo con el freno
  // PACO_COTIZA apagado, sin crear nada real.
  function cotizaDePrueba() {
    try {
      return enModoPrueba() && window.localStorage.getItem('pp_paco_cotiza') === 'si' ? 'si' : undefined;
    } catch (e) {
      return undefined;
    }
  }
  // Para assets/pp-paco3.js (formas del panel → /api/panel-form): un solo
  // dueño de la llave y de la regla.
  window.PPPacoPrueba = { activo: enModoPrueba, bandera: banderaPrueba };

  // ── Atribución de origen (primer touch de la SESIÓN) ──────────────────
  // El 94% de los leads de chat web llegan «sin origen» al CRM: el dato
  // existe en el navegador (UTMs del anuncio, gclid/fbclid, página de
  // aterrizaje) pero nunca viajaba. Aquí se captura UNA vez por sesión —
  // sessionStorage a propósito: la atribución es del touch que trajo a la
  // persona AHORA, no la campaña de hace tres semanas — y viaja en cada
  // turno de /api/chat para que el backend la persista en la conversación
  // (chat_conversations.atribucion en CRM Main; de ahí un trigger la copia
  // al lead cuando Ventas lo crea). Todo best-effort: si storage truena,
  // el chat sigue igual.
  const STORAGE_KEY_ATRIB = 'pp_assistant_atribucion';
  const ATRIB_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'gclid',
    'fbclid',
  ];

  function capturarAtribucion() {
    try {
      // Primer touch manda: una navegación interna posterior no lo pisa.
      if (sessionStorage.getItem(STORAGE_KEY_ATRIB)) return;
      const params = new URLSearchParams(location.search);
      const at = {};
      for (const k of ATRIB_PARAMS) {
        const val = params.get(k);
        if (val) at[k] = val.slice(0, 512);
      }
      // landing_url SIEMPRE: sin campaña, la página de aterrizaje es lo que
      // separa «Sitio web (sin campaña)» de «Sin identificar» en marketing.
      at.landing_url = (location.origin + location.pathname + location.search).slice(0, 1024);
      const ref = document.referrer || '';
      if (ref && ref.indexOf(location.origin) !== 0) {
        at.referrer = ref.slice(0, 1024);
      }
      sessionStorage.setItem(STORAGE_KEY_ATRIB, JSON.stringify(at));
    } catch {
      // best-effort: sin storage no hay atribución, pero el chat vive.
    }
  }
  capturarAtribucion();

  function getAtribucion() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY_ATRIB);
      return raw ? JSON.parse(raw) : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Herramientas cuyo uso significa "esta persona viene a comprar".
   *
   * El disparador del formulario pedía carrito con algo o productos PINTADOS
   * en el panel. Eso deja fuera al cliente que más lo necesita: el que busca
   * algo que el catálogo no tiene.
   *
   * Caso real del 25-ago-2026: una clienta pidió empacar un cuadro de óleo de
   * 70×60×10 cm. Paco buscó TRES veces, no encontró caja y se lo dijo. Cero
   * recomendaciones pintadas, cero carrito → el formulario nunca salió, y su
   * lead se perdió. Justo el caso más valioso: producto que no existe en
   * catálogo = caja a la medida = cotización.
   *
   * Buscar en el catálogo YA es intención, encuentre o no. Y pedir cotización
   * o handoff, más.
   */
  /**
   * Paco ya tomó el contacto en el chat (o hizo el pase): el modal de lead
   * capture sobra. QA 4-oct-2026: salía «¿Te guardamos esta cotización?»
   * justo después de que el cliente dio su WhatsApp y Paco lo pasó a ventas.
   */
  const TOOLS_CONTACTO = new Set([
    'capture_visitor_info',
    'request_human_handoff',
  ]);

  const TOOLS_INTENCION_COMERCIAL = new Set([
    'search_products',
    'get_product_details',
    'get_volume_tiers',
    'format_quote',
    'request_human_handoff',
    'note_for_vendor',
  ]);

  // Sprint 2L: polling vendedor → cliente post-handoff
  const AGENT_POLL_INTERVAL_MS = 5000;
  const AGENT_POLL_IDLE_TIMEOUT_MS = 30 * 60 * 1000;
  const MAX_MESSAGE_LEN = 2000;
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const DESKTOP_BREAKPOINT = 1000; // px — debajo de esto: layout stacked

  /**
   * Lado mayor al que se achica una foto antes de subirla.
   *
   * No es optimización: los tokens de una imagen escalan con sus pixeles, y
   * una foto de iPhone son ~4 MB que además chocan con el límite de cuerpo de
   * las funciones. 1568 px es donde el modelo deja de ganar detalle.
   *
   * Y hay un efecto secundario que sí importa: Safari entrega HEIC, que el
   * modelo no acepta. Al pasar por canvas sale JPEG. Si alguien quita este
   * paso "porque la foto ya venía chica", vuelve el HEIC.
   */
  const FOTO_LADO_MAYOR = 1568;
  const FOTO_CALIDAD = 0.82;
  const MAX_FOTOS_POR_MENSAJE = 1;

  // Una foto sin una línea que la acompañe obliga a Paco a adivinar para qué
  // se la mandaron: ¿cuánto cuesta una caja así?, ¿me cabe esto?, ¿tienen
  // este color? Por eso el texto es obligatorio aunque haya foto (decisión
  // del dueño, 28-ago-2026, que revierte a propósito el hallazgo C4). Para
  // que el botón gris no parezca descompuesto, el placeholder cambia en
  // cuanto se adjunta y dice qué falta.
  const PLACEHOLDER_NORMAL = 'Escribe tu pregunta…';
  const PLACEHOLDER_CON_FOTO = 'Cuéntame qué necesitas de esta foto…';

  function achicarFoto(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const escala = Math.min(1, FOTO_LADO_MAYOR / Math.max(img.width, img.height));
        const c = document.createElement('canvas');
        c.width = Math.round(img.width * escala);
        c.height = Math.round(img.height * escala);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        c.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('sin blob'))),
          'image/jpeg',
          FOTO_CALIDAD
        );
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('no se pudo leer la imagen'));
      };
      img.src = url;
    });
  }

  /**
   * Format MXN money — formato mexicano nativo.
   * Sufijo "MXN" siempre. Sin decimales si es entero (.00 se quita).
   *
   *   149     → "$149 MXN"
   *   10.76   → "$10.76 MXN"
   *   1500.50 → "$1,500.50 MXN"
   */
  function fmtMoney(n) {
    if (n == null || isNaN(Number(n))) return '';
    const num = Number(n);
    const isInt = num === Math.floor(num);
    return (
      '$' +
      num.toLocaleString('es-MX', {
        minimumFractionDigits: isInt ? 0 : 2,
        maximumFractionDigits: 2,
      }) +
      ' MXN'
    );
  }


  /** Shopify image URL transform: agrega _<width>x antes de la extensión. */
  function imageUrl(url, width) {
    if (!url) return '';
    // Si ya tiene _NNNx, déjalo
    if (/_\d+x(?=\.\w+)/.test(url)) return url;
    return url.replace(/(\.\w+)(\?|$)/, '_' + width + 'x$1$2');
  }

  /** Trunca string al char límite con elipsis. */
  function truncate(s, n) {
    s = String(s || '');
    return s.length > n ? s.slice(0, n - 1) + '…' : s;
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Extrae el ID numérico de un GID de Shopify.
   *   gid://shopify/ProductVariant/12345  →  12345
   * El endpoint /cart/add.js exige ID numérico — pasarle el GID tira
   * "Required parameter: items".
   */
  function extractNumericId(value) {
    if (value == null) return null;
    const s = String(value);
    // Si ya es numérico puro, OK
    if (/^\d+$/.test(s)) return Number(s);
    const match = s.match(/(\d+)(?:\?|$)/);
    return match ? Number(match[1]) : null;
  }

  /**
   * Parsea pack info de un texto tipo "25 Cajas", "100 bolsas", "1 Cinta".
   * Devuelve {count, unit, unitPlural} o null.
   *
   * Usado para que cards y cart items muestren info estilo search bar
   * del theme V3: "25 CAJAS · $269 MXN · $10.76 MXN por caja".
   */
  function parsePack(text) {
    if (!text) return null;
    const m = String(text).match(/(\d+)\s*([A-Za-zÁÉÍÓÚáéíóúñÑ]+)/);
    if (!m) return null;
    const count = Number(m[1]);
    if (!Number.isFinite(count) || count <= 0) return null;
    const rawUnit = m[2].toLowerCase();
    // Singularización básica: "cajas" → "caja", "bolsas" → "bolsa",
    // "cintas" → "cinta". No tocamos "kg", "m", etc.
    let singular = rawUnit;
    if (/[aeiouáéíóú]s$/.test(rawUnit)) {
      singular = rawUnit.replace(/s$/, '');
    } else if (/es$/.test(rawUnit) && rawUnit.length > 3) {
      singular = rawUnit.replace(/es$/, '');
    }
    return {
      count,
      unit: singular,
      unitPlural: count === 1 ? singular : rawUnit,
    };
  }

  // ───── Ligas en el texto de Paco (8-oct-2026) ─────
  // Paco manda `[cotizador de cajas](https://…?utm_…)` y URLs sueltas; antes
  // salían tal cual (corchetes, paréntesis y todos los utm). Se vuelven <a>
  // creados con createElement (el texto va por textContent: un `<script>` en
  // el mensaje sigue siendo texto). Sólo http/https; nada de javascript: ni
  // data:. Mismo sitio (parapaquetes.com y sus subdominios) en la misma
  // pestaña; externas en pestaña nueva. EXCEPTO las dos apps (8-oct-2026, el
  // dueño): el cotizador de cajas (cajas.) y el estudio (print.) abren en
  // pestaña nueva para que el cliente no pierda su plática con Paco — con
  // `noopener` pero sin `noreferrer` (son nuestras: que vean de dónde viene).
  const RE_LIGA_MD = /\[([^\]\n]+)\]\(([^)\s]+)\)/;
  const RE_URL_SUELTA = /https?:\/\/[^\s<>"'`\[\]{}|\\^]+/;
  const RE_LIGAS = new RegExp(RE_LIGA_MD.source + '|' + RE_URL_SUELTA.source, 'g');
  // Streaming: `[texto](https://caj` todavía sin `)` — se pinta sólo el texto
  // (sin corchetes ni la URL a medias); el siguiente delta la cierra.
  const RE_LIGA_MD_A_MEDIAS = /\[([^\]\n]+)\]\([^)\s]*$/;

  function esMismoSitio(host) {
    host = String(host || '').toLowerCase();
    return host === 'parapaquetes.com' || /\.parapaquetes\.com$/.test(host);
  }

  /** El cotizador (cotizador., y cajas. que le redirige con 301 desde el
   * 14-oct-2026) o el estudio (Print Studio): apps aparte. */
  function esAppAparte(host) {
    return /^(cotizador|cajas|print)\.parapaquetes\.com$/.test(String(host || '').toLowerCase());
  }

  /** URL segura para un href (sólo http/https; una ruta `/…` es del sitio)
   * o null. */
  function urlDeLiga(href) {
    const s = String(href || '').trim();
    if (!/^https?:\/\//i.test(s) && !/^\/(?!\/)/.test(s)) return null;
    try {
      const base = (typeof location !== 'undefined' && location.href) || 'https://parapaquetes.com/';
      const u = new URL(s, base);
      return u.protocol === 'http:' || u.protocol === 'https:' ? u : null;
    } catch (_) {
      return null;
    }
  }

  /** Lo que sobra al final de una URL suelta y es del TEXTO: un punto, una
   * coma, un `)` que no abrió adentro de la URL. */
  function recortarUrlSuelta(url) {
    let u = url;
    for (;;) {
      const ult = u.charAt(u.length - 1);
      if (/[.,;:!?¡¿…»"'*]/.test(ult)) { u = u.slice(0, -1); continue; }
      if (ult === ')') {
        const abre = (u.match(/\(/g) || []).length;
        const cierra = (u.match(/\)/g) || []).length;
        if (cierra > abre) { u = u.slice(0, -1); continue; }
      }
      return u;
    }
  }

  /** El texto corto de una URL suelta: «Abrir cotizador» para el cotizador
   * de cajas; si no, dominio + ruta sin `www.`, sin query ni utm. */
  function textoDeUrl(u) {
    const host = u.hostname.toLowerCase().replace(/^www\./, '');
    if (esMismoSitio(host) && (host.indexOf('cotizador.') === 0 || host.indexOf('cajas.') === 0)) return 'Abrir cotizador';
    const ruta = u.pathname.replace(/\/+$/, '');
    const t = host + ruta;
    return t.length > 48 ? t.slice(0, 47) + '…' : t;
  }

  function crearLiga(u, texto) {
    const a = document.createElement('a');
    a.className = 'pp-assistant__liga';
    a.href = u.href;
    a.textContent = texto;
    if (esAppAparte(u.hostname)) {
      // Una sola línea: «Abrir cotizador» se partía en dos a 1440 px.
      a.className += ' pp-assistant__liga--app';
      a.target = '_blank';
      a.rel = 'noopener';
    } else if (esMismoSitio(u.hostname)) {
      a.rel = 'noopener';
    } else {
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
    }
    return a;
  }

  /** Texto plano con ligas → nodos en `padre`. */
  function pintarConLigas(padre, texto) {
    let s = String(texto);
    let cola = '';
    const aMedias = s.match(RE_LIGA_MD_A_MEDIAS);
    if (aMedias) {
      s = s.slice(0, aMedias.index);
      cola = aMedias[1];
    }
    let i = 0;
    let m;
    RE_LIGAS.lastIndex = 0;
    while ((m = RE_LIGAS.exec(s)) !== null) {
      let nodo = null;
      let fin = m.index + m[0].length;
      if (m[1] !== undefined) {
        const u = urlDeLiga(m[2]);
        // Un esquema no permitido: se queda el texto, sin liga.
        nodo = u ? crearLiga(u, m[1]) : document.createTextNode(m[1]);
      } else {
        const limpia = recortarUrlSuelta(m[0]);
        fin = m.index + limpia.length;
        const u = urlDeLiga(limpia);
        if (u) nodo = crearLiga(u, textoDeUrl(u));
        RE_LIGAS.lastIndex = fin;
      }
      if (!nodo) continue;
      if (m.index > i) padre.appendChild(document.createTextNode(s.slice(i, m.index)));
      padre.appendChild(nodo);
      i = fin;
    }
    if (i < s.length) padre.appendChild(document.createTextNode(s.slice(i)));
    if (cola) padre.appendChild(document.createTextNode(cola));
  }

  /**
   * Renderiza markdown LIGERO seguro en un DocumentFragment:
   *   - **negritas**         → <strong>
   *   - [texto](url) y URLs sueltas → <a> (pintarConLigas)
   *   - saltos de línea y listas: texto con white-space: pre-wrap
   *
   * NO usa innerHTML — todo via createElement + textContent. Inyectar
   * markup arbitrario está fuera de scope (no es Discord, es un chat
   * de ventas).
   */
  function renderMarkdown(text) {
    const frag = document.createDocumentFragment();
    if (!text) return frag;
    // Split por **bold** o ** sin cerrar (lo dejamos como literal si está
    // en medio del stream — se corregirá en el próximo delta)
    const parts = String(text).split(/(\*\*[^*\n]+\*\*)/g);
    parts.forEach((part) => {
      if (part.length >= 4 && part.startsWith('**') && part.endsWith('**')) {
        const strong = document.createElement('strong');
        pintarConLigas(strong, part.slice(2, -2));
        frag.appendChild(strong);
      } else if (part.length > 0) {
        pintarConLigas(frag, part);
      }
    });
    return frag;
  }

  function uuid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function getSessionId() {
    let id = localStorage.getItem(STORAGE_KEY_SESSION);
    if (!id) {
      id = uuid();
      localStorage.setItem(STORAGE_KEY_SESSION, id);
    }
    return id;
  }

  function getConversationId() {
    return localStorage.getItem(STORAGE_KEY_CONV) || null;
  }

  function setConversationId(id) {
    if (id) localStorage.setItem(STORAGE_KEY_CONV, id);
  }

  function clearConversation() {
    localStorage.removeItem(STORAGE_KEY_CONV);
  }

  // ───── Identidad anónima del visitante (Fase 2b · DT-003) ─────
  // Paco publica su PROPIA config de Supabase (data-supabase-url/key en el
  // <pp-assistant>) y crea su propia sesión anónima — el chat-widget humano
  // legacy fue deprecado en el cutover a Paco (chat_enabled=false). La config
  // apunta al mismo proyecto del CRM, así la sesión se persiste bajo la key de
  // localStorage ("sb-<projectRef>-auth-token"); si el widget reviviera con la
  // misma URL, compartirían un solo auth.uid().
  //
  // El auth_user_id se manda a /api/chat; el backend lo persiste en
  // chat_conversations.auth_user_id. Todo best-effort: si el SDK no carga,
  // el theme no expone config de chat, o signInAnonymously falla, devolvemos
  // null y Paco sigue como conversación anónima (comportamiento previo a 2b).
  const SUPABASE_SDK_URL =
    'https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2/dist/umd/supabase.min.js';

  // Carga el SDK UMD una sola vez, coordinando con pp-chat-widget: reutiliza
  // window.supabase si ya está, o engancha al <script> del SDK si ya se está
  // cargando, en vez de inyectar un duplicado.
  function loadSupabaseSdk() {
    if (window.supabase && window.supabase.createClient) {
      return Promise.resolve(window.supabase);
    }
    if (window._ppSbSdkPromise) return window._ppSbSdkPromise;

    window._ppSbSdkPromise = new Promise((resolve, reject) => {
      const existing =
        document.querySelector('script[data-pp-supabase-sdk]') ||
        document.querySelector('script[src*="@supabase/supabase-js"]');
      if (existing) {
        if (window.supabase && window.supabase.createClient) {
          resolve(window.supabase);
          return;
        }
        existing.addEventListener('load', () => resolve(window.supabase), { once: true });
        existing.addEventListener('error', () => reject(new Error('SDK Supabase no cargó')), { once: true });
        return;
      }
      const s = document.createElement('script');
      s.src = SUPABASE_SDK_URL;
      s.async = true;
      s.setAttribute('data-pp-supabase-sdk', '');
      s.onload = () => resolve(window.supabase);
      s.onerror = () => reject(new Error('SDK Supabase no cargó'));
      document.head.appendChild(s);
    });
    return window._ppSbSdkPromise;
  }

  // Cliente Supabase del visitante (o null si el theme no expone config).
  // Lee la config PROPIA de Paco (data-supabase-url/key del <pp-assistant>);
  // si no está, cae al div del chat-widget legacy (#pp-chat-widget) por si
  // algún día se reactiva chat_enabled. Lo expone en window._ppSbClient para
  // que, si ambos coexisten, compartan una sola identidad.
  function getSharedSupabaseClient() {
    if (window._ppSbClient) return Promise.resolve(window._ppSbClient);

    const paco = document.querySelector('pp-assistant');
    const widgetEl = document.getElementById('pp-chat-widget');
    const url =
      (paco && paco.dataset && paco.dataset.supabaseUrl) ||
      (widgetEl && widgetEl.dataset && widgetEl.dataset.ppSupabaseUrl) || null;
    const key =
      (paco && paco.dataset && paco.dataset.supabaseKey) ||
      (widgetEl && widgetEl.dataset && widgetEl.dataset.ppSupabaseKey) || null;
    if (!url || !key) return Promise.resolve(null);

    return loadSupabaseSdk().then((sb) => {
      if (window._ppSbClient) return window._ppSbClient; // otro actor ganó la carrera
      const client = sb.createClient(url, key);
      window._ppSbClient = client;
      window._ppSbLoaded = true;
      return client;
    });
  }

  // getSession primero (reusa sesión persistida sin tocar red ni crear
  // usuario nuevo); si no hay, signInAnonymously. Devuelve el uid o null.
  function resolveVisitorAuthUserId() {
    return getSharedSupabaseClient()
      .then((client) => {
        if (!client) return null;
        return client.auth.getSession().then((res) => {
          const session = res && res.data ? res.data.session : null;
          if (session && session.user) return session.user.id;
          return client.auth.signInAnonymously().then((result) => {
            if (result.error || !result.data || !result.data.user) {
              console.warn('[pp-assistant] signInAnonymously falló:', result.error);
              return null;
            }
            return result.data.user.id;
          });
        });
      })
      .catch((err) => {
        console.warn('[pp-assistant] no se pudo resolver identidad del visitante:', err);
        return null;
      });
  }

  // Cache de una sola promesa por carga de página (hay una sola instancia de
  // <pp-assistant>). Solo cacheamos resoluciones EXITOSAS: si da null (fallo
  // transitorio, SDK no cargó, etc.) limpiamos para reintentar en el próximo
  // turno del chat.
  let _visitorAuthUserIdPromise = null;
  function ensureVisitorAuthUserId() {
    if (_visitorAuthUserIdPromise) return _visitorAuthUserIdPromise;
    _visitorAuthUserIdPromise = resolveVisitorAuthUserId().then((id) => {
      if (!id) _visitorAuthUserIdPromise = null;
      return id;
    });
    return _visitorAuthUserIdPromise;
  }

  /**
   * Helper: construye un SVG path-based icon.
   * Todos los SVGs del custom element van por aquí — paths estáticos,
   * sin contenido del usuario.
   */
  // ── La Cajita ────────────────────────────────────────────────────────
  //
  // La mascota de Para Paquetes: una caja de cartón con cara que ES el botón
  // del chat en escritorio. Diseño aprobado por Padrino el 2026-08-07 tras
  // once vueltas de prototipo.
  //
  // Solo escritorio, a propósito. En celular se queda la píldora de siempre:
  // la mascota a 64px con manitas no se distingue en una pantalla chica, y el
  // espacio ahí es carísimo.
  //
  // Va como markup en vez de construirse nodo por nodo con createElementNS
  // porque son ~40 formas: escribirlas a mano haría este archivo ilegible.

  const CAJITA_DEFS = `
    <linearGradient id="ppCajitaKraft" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#DBB57F"/><stop offset="1" stop-color="#BD9153"/>
    </linearGradient>
    <linearGradient id="ppCajitaTape" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#F0E2C6"/><stop offset="1" stop-color="#E0CCA2"/>
    </linearGradient>
    <linearGradient id="ppCajitaHand" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#CBA067"/><stop offset="1" stop-color="#AF8148"/>
    </linearGradient>
    <radialGradient id="ppCajitaLed" cx="50%" cy="50%" r="50%">
      <stop offset="0" stop-color="#5CC6F2"/><stop offset="1" stop-color="#5CC6F2" stop-opacity="0"/>
    </radialGradient>`;

  // 5-oct-2026 (captura del dueño 11.38.10): el avatar del encabezado salía
  // sin la caja — sólo ojos, boca y chapitas. Cada dibujo traía los MISMOS
  // ids (ppCajitaKraft, ppCajitaTape…) y el navegador resuelve url(#id) al
  // PRIMERO del documento: la cajita del botón flotante. Con Paco abierto
  // en una pantalla angosta el botón está en display:none (se arma con la
  // cajita a ≥750px: tablet, o la ventana que se angosta) y un gradiente
  // dentro de algo no pintado no pinta: la caja quedaba vacía. Cada dibujo
  // lleva ahora sus propios ids.
  let svgSerie = 0;
  function idsUnicos(markup) {
    const sufijo = '-' + (++svgSerie);
    return markup
      .replace(/id="(pp[A-Za-z]+)"/g, (_, id) => `id="${id}${sufijo}"`)
      .replace(/url\(#(pp[A-Za-z]+)\)/g, (_, id) => `url(#${id}${sufijo})`);
  }

  /** La cajita completa, para el botón del chat. */
  function cajitaSvg() {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 120 132');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('class', 'pp-cajita');
    svg.innerHTML = idsUnicos(`
      <defs>${CAJITA_DEFS}</defs>
      <ellipse class="pp-cajita__shadow" cx="60" cy="129" rx="30" ry="4.5" fill="#0B1F33" opacity=".16"/>
      <g class="pp-cajita__body">
       <g class="pp-cajita__estira">
        <g fill="#8A6534"><circle cx="13.5" cy="103" r="9.3"/><circle cx="19.6" cy="97.4" r="4.6"/></g>
        <g fill="url(#ppCajitaHand)"><circle cx="13.5" cy="103" r="8.4"/><circle cx="19.6" cy="97.4" r="3.8"/></g>
        <g class="pp-cajita__arm">
          <g fill="#8A6534"><circle cx="108" cy="70" r="9.7"/><circle cx="101.2" cy="74.8" r="4.7"/></g>
          <g fill="url(#ppCajitaHand)"><circle cx="108" cy="70" r="8.8"/><circle cx="101.2" cy="74.8" r="3.9"/></g>
        </g>
        <rect x="22" y="44" width="76" height="78" rx="7" fill="url(#ppCajitaKraft)"/>
        <path d="M28 48 Q45 46 52 46" stroke="#fff" stroke-width="1.8" fill="none" stroke-linecap="round" opacity=".3"/>
        <path d="M68 46 Q83 46 92 48" stroke="#fff" stroke-width="1.8" fill="none" stroke-linecap="round" opacity=".3"/>
        <rect x="51" y="39.5" width="18" height="6.5" rx="1" fill="url(#ppCajitaTape)"/>
        <path d="M53 44 L67 44 L67 61 L65 65.5 L62 62.5 L59 65.5 L56 62.5 L53 64.5 Z" fill="url(#ppCajitaTape)" opacity=".92"/>
        <g class="pp-cajita__eyes">
          <circle cx="43" cy="72" r="8.6" fill="#fff"/><circle cx="77" cy="72" r="8.6" fill="#fff"/>
          <g class="pp-cajita__pupil">
            <circle cx="43.5" cy="73" r="4.7" fill="#0B1F33"/>
            <circle cx="41.2" cy="70.4" r="1.7" fill="#fff"/>
          </g>
          <g class="pp-cajita__pupil">
            <circle cx="77.5" cy="73" r="4.7" fill="#0B1F33"/>
            <circle cx="75.2" cy="70.4" r="1.7" fill="#fff"/>
          </g>
        </g>
        <rect class="pp-cajita__lid" x="34.4" y="63.4" width="17.2" height="9" rx="4.5" fill="url(#ppCajitaKraft)"/>
        <rect class="pp-cajita__lid" x="68.4" y="63.4" width="17.2" height="9" rx="4.5" fill="url(#ppCajitaKraft)"/>
        <path d="M51 94 Q60 101 69 94" stroke="#0B1F33" stroke-width="2.6" fill="none" stroke-linecap="round"/>
        <circle cx="35" cy="90" r="3.2" fill="#E39B7B" opacity=".45"/>
        <circle cx="85" cy="90" r="3.2" fill="#E39B7B" opacity=".45"/>
        <g transform="translate(58,103) rotate(-6)">
          <rect x="0" y="0" width="34" height="14.5" rx="2" fill="#C4302B"/>
          <rect x="1.6" y="1.6" width="30.8" height="11.3" rx="1.2" fill="none" stroke="#fff" stroke-width="1" opacity=".9"/>
          <text x="17" y="10.3" text-anchor="middle" font-size="6.6" font-weight="800" fill="#fff"
                font-family="ui-sans-serif,system-ui,sans-serif" letter-spacing=".6">FRÁGIL</text>
        </g>
        <circle class="pp-cajita__glow" cx="31" cy="55" r="6" fill="url(#ppCajitaLed)"/>
        <circle class="pp-cajita__led" cx="31" cy="55" r="2.3" fill="#5CC6F2"/>
       </g>
      </g>
      <g class="pp-cajita__zzz" font-family="ui-sans-serif,system-ui,sans-serif"
         font-weight="800" fill="#0B1F33">
        <text class="pp-cajita__z" x="88" y="36" font-size="12">z</text>
        <text class="pp-cajita__z" x="97" y="24" font-size="15">z</text>
        <text class="pp-cajita__z" x="107" y="11" font-size="18">z</text>
      </g>
      <g class="pp-cajita__sueno">
        <g fill="#fff" stroke="#0B1F33" stroke-opacity=".14" stroke-width="1.4">
          <circle class="pp-cajita__sueno-pip" cx="79" cy="37" r="2.6"/>
          <circle class="pp-cajita__sueno-pip" cx="86" cy="30" r="4"/>
          <circle cx="94" cy="17" r="13"/>
          <circle cx="105" cy="21" r="7.4"/>
          <circle cx="85" cy="23" r="7"/>
        </g>
        <g class="pp-cajita__sueno-cosa">
          <text x="94" y="23" font-size="17" font-weight="800" text-anchor="middle"
                fill="#0E3A5C" font-family="ui-sans-serif,system-ui,sans-serif">$</text>
        </g>
        <g class="pp-cajita__sueno-cosa">
          <rect x="87" y="11" width="14" height="12" rx="1.6" fill="none"
                stroke="#0E3A5C" stroke-width="1.9"/>
          <path d="M94 11 V23" stroke="#0E3A5C" stroke-width="1.5"/>
        </g>
        <g class="pp-cajita__sueno-cosa">
          <path d="M94 23.5 C 87 17.5, 87.5 11.5, 91.2 11.5 C 93 11.5, 94 13.4, 94 14.4
                   C 94 13.4, 95 11.5, 96.8 11.5 C 100.5 11.5, 101 17.5, 94 23.5 Z"
                fill="#C4302B"/>
        </g>
      </g>`);
    // Margen real: el ojo mide 8.6 de radio y la pupila 4.7 → 3.9 de juego.
    // Se usa 3 para que nunca toque el borde del blanco.
    seguirConLaMirada(svg, '.pp-cajita__pupil', 3);
    return svg;
  }

  /**
   * Solo la carita, recortada en círculo, para el avatar del chat.
   *
   * Es un dibujo APARTE y no la cajita encogida: a 44px la caja completa con
   * manitas no se distingue — se vuelve una mancha café.
   */
  function caritaSvg() {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 64 64');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('class', 'pp-carita');
    svg.innerHTML = idsUnicos(`
      <defs>${CAJITA_DEFS}<clipPath id="ppCaritaClip"><circle cx="32" cy="32" r="32"/></clipPath></defs>
      <g clip-path="url(#ppCaritaClip)">
        <rect width="64" height="64" fill="url(#ppCajitaKraft)"/>
        <rect x="25" y="0" width="14" height="5" fill="url(#ppCajitaTape)"/>
        <path d="M26.4 4 L37.6 4 L37.6 16 L36 19.4 L33.6 17.2 L31.2 19.4 L28.8 17.2 L26.4 18.6 Z" fill="url(#ppCajitaTape)" opacity=".92"/>
        <circle cx="20.5" cy="30" r="7.6" fill="#fff"/><circle cx="43.5" cy="30" r="7.6" fill="#fff"/>
        <g class="pp-carita__pupil">
          <circle cx="21" cy="31" r="4.1" fill="#0B1F33"/>
          <circle cx="18.9" cy="28.6" r="1.5" fill="#fff"/>
        </g>
        <g class="pp-carita__pupil">
          <circle cx="44" cy="31" r="4.1" fill="#0B1F33"/>
          <circle cx="41.9" cy="28.6" r="1.5" fill="#fff"/>
        </g>
        <path d="M25 44 Q32 50.5 39 44" stroke="#0B1F33" stroke-width="2.6" fill="none" stroke-linecap="round"/>
        <circle cx="10" cy="41" r="3.2" fill="#E39B7B" opacity=".45"/>
        <circle cx="54" cy="41" r="3.2" fill="#E39B7B" opacity=".45"/>
        <circle cx="8.5" cy="12" r="2" fill="#5CC6F2"/>
      </g>`);
    // Ojo 7.6 · pupila 4.1 → 3.5 de juego; 2.5 deja aire.
    seguirConLaMirada(svg, '.pp-carita__pupil', 2.5);
    return svg;
  }

  // ── La mirada sigue el cursor cuando se acerca ───────────────────────
  //
  // Idea de Padrino (2026-08-07): si el mouse anda lejos, la cajita mira a
  // donde quiera; cuando se acerca, te sigue con los ojos. Eso es lo que la
  // vuelve un personaje y no un dibujo pegado en la esquina.
  //
  // UN SOLO listener para todas las caritas (el botón y el avatar del chat).
  // `pointermove` dispara decenas de veces por segundo: se guarda la posición
  // y se pinta en el siguiente frame, nunca en el evento.
  //
  // No corre con puntero grueso (táctil: no hay cursor al que seguir) ni con
  // prefers-reduced-motion.

  const MIRADA_RADIO = 260;  // px desde el centro de la cara donde empieza a seguir
  const miradaSujetos = [];
  let miradaX = 0, miradaY = 0, miradaRaf = null, miradaLista = false;

  function pintarMirada() {
    miradaRaf = null;
    for (const s of miradaSujetos) {
      const caja = s.el.getBoundingClientRect();
      if (!caja.width) continue; // oculto: ni lo calcules

      const cx = caja.left + caja.width / 2;
      const cy = caja.top + caja.height / 2;
      const dx = miradaX - cx;
      const dy = miradaY - cy;
      const dist = Math.hypot(dx, dy);

      let px = 0, py = 0;
      const cerca = dist < MIRADA_RADIO;
      if (cerca && dist > 1) {
        // Cuanto más cerca, más marcada la mirada — hasta el tope del ojo.
        const fuerza = 1 - dist / MIRADA_RADIO;
        const alcance = s.max * Math.min(1, fuerza * 1.6);
        px = (dx / dist) * alcance;
        py = (dy / dist) * alcance;
      }

      if (cerca !== s.cerca) {
        s.cerca = cerca;
        // Mientras sigue el cursor se apaga la mirada al azar: si no, se
        // pelean las dos y los ojos tiemblan.
        s.el.classList.toggle('pp-mirada-activa', cerca);
      }
      for (const pupila of s.pupilas) {
        pupila.style.transform = `translate(${px.toFixed(2)}px, ${py.toFixed(2)}px)`;
      }
    }
  }

  function alMoverElPuntero(ev) {
    miradaX = ev.clientX;
    miradaY = ev.clientY;
    if (!miradaRaf) miradaRaf = requestAnimationFrame(pintarMirada);
  }

  /**
   * Hace que una carita siga el cursor.
   * @param {SVGElement} svg  el dibujo (cajita o carita)
   * @param {string} sel      selector de los grupos de pupila
   * @param {number} max      cuánto se puede mover la pupila, en unidades del
   *                          viewBox (el margen entre el blanco y la pupila)
   */
  function seguirConLaMirada(svg, sel, max) {
    if (!window.matchMedia) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const pupilas = Array.from(svg.querySelectorAll(sel));
    if (!pupilas.length) return;

    miradaSujetos.push({ el: svg, pupilas, max, cerca: false });
    if (!miradaLista) {
      miradaLista = true;
      window.addEventListener('pointermove', alMoverElPuntero, { passive: true });
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════
     LA CAJITA SE DUERME — Y SE ANIMA A QUE LE PREGUNTES
     ═══════════════════════════════════════════════════════════════════════

     Dos ideas de Padrino, las dos sobre lo mismo: que reaccione a lo que la
     persona NO está haciendo.

     1. Si nadie mueve el cursor por un rato, se queda dormida y le salen las
        zetas. En cuanto te mueves, despierta y parpadea.
     2. Si te le quedas viendo encima sin decidirte, te dice que no muerdas —
        que preguntes con confianza.

     Del segundo, Padrino propuso 10 segundos de hover. Se bajó a 2.4: nadie
     deja el mouse quieto encima de un botón 10 segundos, o hace clic o se va,
     así que ese mensaje no lo habría visto nadie. 2.4s es el momento en que se
     nota que la persona está dudando — justo cuando sirve decírselo.
     ═══════════════════════════════════════════════════════════════════════ */

  const DORMIRSE_MS = 15000;   // sin mover el cursor
  const GLOBITO_MS = 2400;     // hover sostenido antes de invitar
  const GLOBITO_VIVE_MS = 9000;  // se iban muy rápido a los 5.6s
  const SONAR_MS = 12000;        // dormida este rato, empieza a soñar
  // Y este rato soñando, se hunde y se queda quieta. No es solo estética: unas
  // animaciones infinitas mantienen ocupado al compositor toda la noche si
  // alguien deja la pestaña abierta. Dormida profunda no mueve ni un pixel.
  const PROFUNDO_MS = 90000;
  const GLOBITO_MAX = 3;       // por carga de página: a la cuarta ya es insistir
  // La caja dibujada no llena el lienzo: arriba hay hueco (ahí salen las
  // zetas). El globito se ancla a la CABEZA, no al borde del botón, o queda
  // flotando lejos y no se lee como que está hablando.
  const CAJITA_CABEZA = 0.3;   // 39 de 132 del viewBox

  // Todas rondan el mismo largo a propósito: el globito no debe cambiar de
  // tamaño entre una y otra, o se siente que salta.
  const INVITACIONES = [
    'Ándale, pregúntame. Para eso estoy aquí.',
    'Sin pena: hazme la pregunta que sea.',
    '¿Te ayudo a escoger? Nomás pregunta.',
    'Aquí ando, por si te surge una duda.',
    '¿Buscas algo en especial? Pregúntame.',
    'Si algo no te queda claro, dime.',
  ];

  function vidaDeLaCajita(fab, svg) {
    if (!window.matchMedia) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let dormida = false;
    let soñando = false;
    let profunda = false;
    let dormidaDesde = 0;
    let encima = false;   // el cursor está sobre la cajita
    let ultimaSeñalDeVida = Date.now();
    let hoverTimer = null;
    let globito = null;
    let globitoTimer = null;
    let globitosMostrados = 0;
    // Arranca en una frase al azar y de ahí avanza en orden: dentro de la
    // misma visita nunca repite, y entre visitas no siempre empieza igual.
    let cualInvitacion = Math.floor(Math.random() * INVITACIONES.length);

    const chatAbierto = () => document.body.classList.contains('pp-assistant-open');

    function dormir() {
      if (dormida || chatAbierto()) return;
      dormida = true;
      dormidaDesde = Date.now();
      svg.classList.remove('pp-cajita--despierta');
      svg.classList.add('pp-cajita--dormida');
    }

    // Al rato de estar dormida se pone a soñar, y sueña con trabajo: cotiza,
    // empaca, deja al cliente contento. Es una segunda etapa a propósito —
    // primero ronca, y solo si de veras la dejaron sola se pone a soñar.
    function soñar() {
      if (soñando) return;
      soñando = true;
      svg.classList.add('pp-cajita--sonando');
    }

    /** Se hunde: se acaba el sueño y se queda completamente quieta. */
    function hundirse() {
      if (profunda) return;
      profunda = true;
      svg.classList.remove('pp-cajita--sonando');
      svg.classList.add('pp-cajita--profundo');
    }

    function despertar() {
      if (!dormida) return;
      dormida = false;
      soñando = false;
      profunda = false;
      svg.classList.remove('pp-cajita--dormida', 'pp-cajita--sonando', 'pp-cajita--profundo');
      // Un parpadeo al abrir los ojos. Solo toca los párpados, así que no se
      // pelea con la flotación ni con el LED.
      svg.classList.add('pp-cajita--despierta');
      // 900 y no 700: el estirón del CSS dura 780ms y se cortaba a media pose.
      setTimeout(() => svg.classList.remove('pp-cajita--despierta'), 900);
    }

    // El movimiento del cursor dispara decenas de veces por segundo, así que
    // aquí solo se apunta la hora — nada de reprogramar un temporizador cada
    // vez. Quien decide es un latido de 1/s, que no se siente.
    function hayVida() {
      ultimaSeñalDeVida = Date.now();
      if (dormida) despertar();
    }

    setInterval(() => {
      if (chatAbierto()) return;
      if (dormida) {
        const rato = Date.now() - dormidaDesde;
        if (rato >= PROFUNDO_MS) hundirse();
        else if (rato >= SONAR_MS) soñar();
        return;
      }
      // Con el cursor encima NO se duerme, aunque nadie lo mueva. Dejar el
      // mouse quieto sobre ella es justo lo contrario a ignorarla —
      // pointermove no dispara si no te mueves, así que sin esto se dormía
      // en la cara de quien la estaba mirando (lo cachó Padrino).
      if (encima) return;
      if (Date.now() - ultimaSeñalDeVida >= DORMIRSE_MS) dormir();
    }, 1000);

    for (const evento of ['pointermove', 'pointerdown', 'keydown', 'scroll', 'wheel']) {
      window.addEventListener(evento, hayVida, { passive: true });
    }

    /* ── El globito de "no muerdo" ─────────────────────────────────────── */

    function colocarGlobito() {
      if (!globito) return;
      const caja = fab.getBoundingClientRect();
      const g = globito.getBoundingClientRect();
      // El piquito vive en el borde derecho del globito, así que alineando ese
      // borde con el de la cajita queda apuntándole a la cabeza.
      let izq = caja.right - g.width;
      let arriba = caja.top + caja.height * CAJITA_CABEZA - g.height - 10;
      if (izq < 8) izq = 8;
      // Si no cabe arriba (ventana muy baja) se va abajo, y el piquito se
      // voltea: apuntando al revés se leería como que habla otra cosa.
      const abajo = arriba < 8;
      if (abajo) arriba = caja.bottom + 10;
      globito.classList.toggle('pp-cajita-globito--abajo', abajo);
      globito.style.left = `${Math.round(izq)}px`;
      globito.style.top = `${Math.round(arriba)}px`;
    }

    function esconderGlobito() {
      if (hoverTimer) { clearTimeout(hoverTimer); hoverTimer = null; }
      if (globitoTimer) { clearTimeout(globitoTimer); globitoTimer = null; }
      if (!globito) return;
      const viejo = globito;
      globito = null;
      viejo.classList.remove('is-visible');
      setTimeout(() => viejo.remove(), 240);
    }

    /** ¿La burbuja de consejos está en pantalla ahorita? */
    function coachVisible() {
      const c = document.querySelector('[data-pp-coach]');
      return Boolean(c && !c.hidden && c.classList.contains('is-visible'));
    }

    function invitar() {
      hoverTimer = null;
      if (globito || chatAbierto() || globitosMostrados >= GLOBITO_MAX) return;
      // Una voz a la vez. La burbuja de consejos y el globito nacen del mismo
      // rincón y se encimaban; el consejo dice algo útil de la página y el
      // globito solo anima a preguntar, así que el que se calla es el globito.
      if (coachVisible()) return;
      globitosMostrados++;

      globito = el('div', {
        cls: 'pp-cajita-globito',
        text: INVITACIONES[cualInvitacion % INVITACIONES.length],
        // Es puro ánimo visual; el botón ya se anuncia solo con su aria-label.
        attrs: { 'aria-hidden': 'true' },
      });
      cualInvitacion++;
      document.body.appendChild(globito);
      colocarGlobito();
      requestAnimationFrame(() => globito && globito.classList.add('is-visible'));
      globitoTimer = setTimeout(esconderGlobito, GLOBITO_VIVE_MS);
    }

    fab.addEventListener('pointerenter', () => {
      encima = true;
      hayVida();  // tenerla bajo el cursor cuenta como atención
      if (hoverTimer || globito) return;
      hoverTimer = setTimeout(invitar, GLOBITO_MS);
    });
    fab.addEventListener('pointerleave', () => {
      encima = false;
      esconderGlobito();
    });
    fab.addEventListener('click', () => {
      // Si ya se animó a preguntar, el "no muerdo" sobra el resto de la visita.
      globitosMostrados = GLOBITO_MAX;
      esconderGlobito();
      despertar();
    });
  }

  /** La cajita solo en escritorio; en celular se queda la píldora de siempre. */
  /**
   * /pages/paco (3-oct-2026): el mensaje con el que arranca la plática en la
   * PÁGINA de Paco. `?q=<mensaje>` lo manda tal cual (recortado); `?vista=pedidos`
   * pregunta por sus pedidos (el servidor los busca con la firma del cliente y
   * pinta la vista `pedido`). Las demás vistas las abre el panel
   * (pp-paco3-logica.js enlacePaco), no el modelo. Pura: se prueba ejecutándola.
   */
  function mensajeDeEnlace(search) {
    let params;
    try { params = new URLSearchParams(String(search || '')); } catch (_) { return ''; }
    const q = String(params.get('q') || '').replace(/\s+/g, ' ').trim().slice(0, 500);
    if (q) return q;
    if (String(params.get('vista') || '').toLowerCase() === 'pedidos') return '¿Dónde está mi pedido?';
    return '';
  }

  /**
   * Login que REGRESA a Paco (3-oct-2026): la marca que deja el panel al
   * picar «Iniciar sesión» (pp-paco3-logica.js marcaReabrir; llave
   * pp_paco_reabrir en sessionStorage). Vigente 30 min. Pura: devuelve
   * { vista, datos, conv } o null. Se prueba ejecutándola.
   */
  const TTL_REABRIR_MS = 30 * 60 * 1000;
  function leerMarcaReabrir(raw, ahora) {
    if (!raw) return null;
    let m;
    try { m = JSON.parse(raw); } catch (_) { return null; }
    if (!m || m.reabrir !== true || typeof m.t !== 'number') return null;
    const edad = Number(ahora) - m.t;
    if (!(edad >= 0) || edad > TTL_REABRIR_MS) return null;
    const vista = typeof m.vista === 'string' && /^[a-z_]{1,30}$/.test(m.vista) ? m.vista : null;
    const datos = {};
    if (m.datos && /^\d{3,10}$/.test(String(m.datos.numero_orden || ''))) datos.numero_orden = String(m.datos.numero_orden);
    const conv = typeof m.conv === 'string' && /^[\w-]{8,80}$/.test(m.conv) ? m.conv : null;
    return { vista, datos, conv, ruta: rutaDelSitio(m.ruta), saltado: m.saltado === true };
  }

  /** Sólo una ruta relativa del MISMO sitio («/algo»): nada de «//otro»,
   * «/\\otro», esquemas ni caracteres de control (anti open-redirect). Si no
   * cuadra, null. */
  function rutaDelSitio(r) {
    const t = typeof r === 'string' ? r : '';
    if (!/^\/(?![\/\\])/.test(t) || /[\u0000-\u001f\u007f\\]/.test(t) || /^\/[^/?#]*:/.test(t) || t.length > 500) return null;
    return t;
  }

  /** Shopify (cuentas nuevas) regresa del login a «/?shop_sign_in=true&…»
   * aunque se le pida return_to (capturas del Padrino, 4-oct 8.10 AM). La
   * URL sin esos parámetros de Shopify. */
  function sinParametrosDeShopify(search) {
    let params;
    try { params = new URLSearchParams(String(search || '')); } catch (_) { return { search: '', habia: false }; }
    let habia = false;
    ['shop_sign_in', 'shop_consented_scopes'].forEach((k) => { if (params.has(k)) { habia = true; params.delete(k); } });
    const q = params.toString();
    return { search: q ? '?' + q : '', habia };
  }

  /**
   * Deslizar entre pestañas en celular (4-oct-2026, el dueño): Chat ↔
   * Productos ↔ Carrito con el dedo sobre todo el contenido. Sólo cuenta un
   * gesto CLARAMENTE de lado (≥ 60 px, al menos 1.5 veces más de lado que
   * hacia abajo, en menos de 800 ms): un scroll vertical nunca cambia de
   * pestaña. Hacia la izquierda avanza, hacia la derecha regresa; en las
   * orillas no hace nada. Pura: devuelve la pestaña nueva o null.
   */
  const TABS_DESLIZAR = ['chat', 'products', 'cart'];
  function tabTrasDeslizar(actual, dx, dy, ms) {
    const ax = Math.abs(Number(dx) || 0);
    const ay = Math.abs(Number(dy) || 0);
    if (ax < 60 || ax < ay * 1.5 || !(Number(ms) >= 0) || Number(ms) > 800) return null;
    const i = TABS_DESLIZAR.indexOf(actual);
    if (i < 0) return null;
    return TABS_DESLIZAR[dx < 0 ? i + 1 : i - 1] || null;
  }

  /**
   * ¿El dedo empezó en algo que ya se desliza de lado (un carrusel, la fila
   * de búsquedas, las miniaturas) o donde se escribe? Ahí el gesto es suyo,
   * no de las pestañas. `estilo(n)` = getComputedStyle (inyectable). Pura.
   */
  function tocaAlgoQueSeDesliza(nodo, raiz, estilo) {
    for (let n = nodo; n && n !== raiz && n.nodeType === 1; n = n.parentNode) {
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(n.tagName)) return true;
      if (n.scrollWidth > n.clientWidth + 1) {
        const ox = (estilo(n) || {}).overflowX;
        if (ox === 'auto' || ox === 'scroll') return true;
      }
    }
    return false;
  }

  /**
   * «Atrás» del navegador con Paco abierto en CELULAR (QA móvil, 4-oct-2026).
   * Antes el botón (o el gesto de orilla) sacaba de la página o cerraba Paco
   * de golpe con todo encima. Ahora cada «atrás» deshace UN paso, como en
   * una app: lo que la v3 tenga encima (reseñas, hoja, Conversaciones,
   * carrito, ficha — `capa`, ya cerrado por PPPaco3.atrasCelular), la ficha
   * vieja del motor (`modalProducto`), una pestaña que no es Chat (vuelve a
   * Chat) y, al final, cerrar Paco (sin salir de la página). Pura.
   */
  function pasoAtrasMovil(s) {
    if (!s) return 'cerrar';
    if (s.capa) return 'capa';
    if (s.modalProducto) return 'modal';
    if (s.tab && s.tab !== 'chat') return 'chat';
    return 'cerrar';
  }

  /**
   * Celular con Paco abierto (iPhone real, 5-oct-2026, captura del dueño):
   * al tocar «Escribe tu pregunta…» iOS desplazaba la página para «mostrar»
   * el campo, todo subía y entre el campo y el teclado se asomaba la tienda
   * (la barra Inicio/Menú/Cuenta…). El QA anterior lo probó SIMULANDO el
   * teclado. Ahora el overlay entero ES la caja que se ve (visualViewport):
   * arriba = offsetTop, alto = height (e izquierda/ancho con zoom), así su
   * orilla de abajo queda pegada al teclado en iOS (que desplaza lo visible)
   * y en Android (que lo encoge). `teclado`: lo visible es < 80% de la
   * ventana sin zoom. Pura; null si no hay visualViewport.
   */
  function cajaVisible(vv, altoVentana) {
    if (!vv) return null;
    const alto = Math.round(Number(vv.height));
    const ancho = Math.round(Number(vv.width));
    if (!(alto > 0) || !(ancho > 0)) return null;
    const h = Number(altoVentana);
    const escala = Number(vv.scale) || 1;
    return {
      arriba: Math.max(0, Math.round(Number(vv.offsetTop) || 0)),
      izquierda: Math.max(0, Math.round(Number(vv.offsetLeft) || 0)),
      alto,
      ancho,
      teclado: h > 0 && escala < 1.05 && alto < h * 0.8,
    };
  }

  /**
   * MODO ESCRIBIENDO (celular): con el campo enfocado sólo se ven la
   * conversación y el campo (sin pestañas, aviso ni «¿Necesitas ayuda
   * humana?»; encabezado de una línea). Entra al enfocar; sale cuando el
   * campo pierde el foco y el teclado ya bajó — o cuando el teclado que se
   * vio subir baja con el campo aún enfocado (el «atrás» de Android esconde
   * el teclado sin quitar el foco). Pura.
   */
  function modoEscribiendo(s) {
    if (!s) return false;
    if (s.foco) return !(s.tecladoVisto && !s.teclado);
    return Boolean(s.prev && s.teclado);
  }

  /**
   * Celular: la página de atrás NO se mueve mientras Paco está abierto. El
   * `overflow: hidden` no basta en iOS (al enfocar un campo desplaza la
   * ventana igual): el body queda `position: fixed` con top = -scrollY y al
   * cerrar se regresa a donde estaba. Devuelve la función que lo suelta.
   */
  function bloquearFondoCelular(win) {
    const b = win && win.document && win.document.body;
    if (!b || !b.style) return function () {};
    const y = Math.round(Number(win.scrollY || win.pageYOffset) || 0);
    const props = ['position', 'top', 'left', 'right', 'width'];
    const antes = {};
    props.forEach((k) => { antes[k] = b.style[k] || ''; });
    b.style.position = 'fixed';
    b.style.top = (-y) + 'px';
    b.style.left = '0';
    b.style.right = '0';
    b.style.width = '100%';
    return function soltar() {
      props.forEach((k) => { b.style[k] = antes[k]; });
      try { win.scrollTo(0, y); } catch (e) { /* nada */ }
    };
  }

  /**
   * Celular: el overlay sigue a lo que se ve (cajaVisible) en cada `resize`
   * y `scroll` del visualViewport (un cuadro por requestAnimationFrame) y
   * pone/quita `data-pp-escribiendo` (modoEscribiendo). Al enfocar el campo
   * regresa la ventana a 0 si iOS la movió. `o.campo()` = el campo del chat,
   * `o.esCelular()`, `o.alEscribir(bool)`. Devuelve { aplicar, apagar }.
   */
  function seguirVistaCelular(win, ov, o) {
    const vv = win.visualViewport;
    const st = ov.style;
    const lados = ['top', 'left', 'height', 'width', 'bottom', 'right'];
    let escribiendo = false;
    let tecladoVisto = false;
    let cuadro = 0;
    let ultimoAlto = 0;
    const timers = [];
    const campo = () => (o && typeof o.campo === 'function' ? o.campo() : null);
    const conFoco = () => { const c = campo(); return Boolean(c && win.document && win.document.activeElement === c); };
    const marcar = (k, si) => { if (si) ov.setAttribute(k, ''); else ov.removeAttribute(k); };
    const cambiar = (nuevo) => {
      marcar('data-pp-escribiendo', nuevo);
      if (nuevo === escribiendo) return;
      escribiendo = nuevo;
      if (o && typeof o.alEscribir === 'function') o.alEscribir(nuevo);
    };
    const limpiar = () => {
      lados.forEach((k) => { st[k] = ''; });
      marcar('data-pp-vista-celular', false);
      marcar('data-pp-teclado', false);
      tecladoVisto = false;
      cambiar(false);
    };
    function aplicar() {
      cuadro = 0;
      const c = vv && (!o || typeof o.esCelular !== 'function' || o.esCelular()) ? cajaVisible(vv, win.innerHeight) : null;
      if (!c) { limpiar(); return; }
      st.top = c.arriba + 'px';
      st.left = c.izquierda + 'px';
      st.height = c.alto + 'px';
      st.width = c.ancho + 'px';
      st.bottom = 'auto';
      st.right = 'auto';
      marcar('data-pp-vista-celular', true);
      marcar('data-pp-teclado', c.teclado);
      const foco = conFoco();
      if (foco && c.teclado) tecladoVisto = true;
      const nuevo = modoEscribiendo({ prev: escribiendo, foco, teclado: c.teclado, tecladoVisto });
      if (!foco && !c.teclado) tecladoVisto = false;
      const otroAlto = c.alto !== ultimoAlto;
      ultimoAlto = c.alto;
      cambiar(nuevo);
      // El teclado sube DESPUÉS del foco: al encogerse la caja, el último
      // mensaje se vuelve a poner a la vista.
      if (nuevo && otroAlto && o && typeof o.alEscribir === 'function') o.alEscribir(true);
    }
    const pedir = () => {
      if (cuadro) return;
      cuadro = (win.requestAnimationFrame ? win.requestAnimationFrame(aplicar) : win.setTimeout(aplicar, 16)) || 1;
    };
    const aCero = () => {
      if (!conFoco()) return;
      if ((Number(win.scrollY) || 0) !== 0) { try { win.scrollTo(0, 0); } catch (e) { /* nada */ } }
      pedir();
    };
    const alFoco = (e) => {
      if (!e || e.target !== campo()) return;
      tecladoVisto = false;
      aplicar();
      // iOS desplaza la ventana DESPUÉS de subir el teclado (~300 ms).
      [0, 120, 350, 700].forEach((ms) => timers.push(win.setTimeout(aCero, ms)));
    };
    // Al perder el foco se espera un poco: un toque en «Enviar» no debe
    // mover la pantalla bajo el dedo antes de que llegue el clic.
    const alSalir = () => { timers.push(win.setTimeout(pedir, 300)); };
    if (vv) {
      vv.addEventListener('resize', pedir);
      vv.addEventListener('scroll', pedir);
    }
    win.addEventListener('resize', pedir);
    win.addEventListener('scroll', pedir, { passive: true });
    ov.addEventListener('focusin', alFoco);
    ov.addEventListener('focusout', alSalir);
    aplicar();
    return {
      aplicar,
      apagar() {
        if (vv) {
          vv.removeEventListener('resize', pedir);
          vv.removeEventListener('scroll', pedir);
        }
        win.removeEventListener('resize', pedir);
        win.removeEventListener('scroll', pedir);
        ov.removeEventListener('focusin', alFoco);
        ov.removeEventListener('focusout', alSalir);
        timers.forEach((t) => win.clearTimeout(t));
        timers.length = 0;
        if (cuadro && win.cancelAnimationFrame) { try { win.cancelAnimationFrame(cuadro); } catch (e) { /* nada */ } }
        cuadro = 0;
        limpiar();
      },
    };
  }

  /** ¿Celular? (el mismo corte que las pestañas: <1000px). */
  function esCelularPaco() {
    try { return Boolean(window.matchMedia && window.matchMedia('(max-width: 999px)').matches); } catch (e) { return false; }
  }

  function usarCajita() {
    return Boolean(window.matchMedia && window.matchMedia('(min-width: 750px)').matches);
  }

  function svgIcon(viewBox, pathD, extraAttrs) {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', viewBox);
    svg.setAttribute('fill', 'none');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', pathD);
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-width', '1.8');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(path);
    if (extraAttrs) {
      Object.entries(extraAttrs).forEach(([k, v]) => svg.setAttribute(k, v));
    }
    return svg;
  }

  const ICONS = {
    chat: 'M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z',
    refresh: 'M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5',
    close: 'M6 6l12 12M18 6L6 18',
    send: 'M5 12l14-7-7 14-2-5-5-2z',
    cart: 'M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4zM3 6h18M16 10a4 4 0 0 1-8 0',
    plus: 'M12 5v14M5 12h14',
    check: 'M5 13l4 4L19 7',
    package: 'M16 16v-1.5a4.5 4.5 0 0 0-9 0V16M21 8l-9-5-9 5v8l9 5 9-5z',
    trash: 'M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6',
    clip: 'M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48',
  };

  /**
   * Crea un elemento con clases, atributos, text, y children — versión
   * compacta de createElement que NO hace innerHTML.
   */
  function el(tag, opts) {
    const node = document.createElement(tag);
    if (!opts) return node;
    if (opts.cls) node.className = opts.cls;
    if (opts.id) node.id = opts.id;
    if (opts.text != null) node.textContent = String(opts.text);
    if (opts.attrs) {
      Object.entries(opts.attrs).forEach(([k, v]) => {
        if (v === false || v == null) return;
        if (v === true) node.setAttribute(k, '');
        else node.setAttribute(k, String(v));
      });
    }
    if (opts.children) {
      opts.children.forEach((c) => c && node.appendChild(c));
    }
    return node;
  }

  // ───── Adjunto que manda el asesor desde el CRM (14-oct-2026) ─────
  // El sync del backend trae `attachment: { url, kind, mime, name }` en cada
  // mensaje del vendedor. Antes el widget sólo pintaba el texto: la imagen
  // «esta sería la caja que se acerca a tus medidas» nunca llegó y el
  // cliente preguntó «¿Cuál, perdón?» 15 veces; las que venían sin texto se
  // veían como burbuja VACÍA. Segunda reja (la primera es el backend): sólo
  // se pinta una URL https del storage del CRM (mismo host que
  // data-supabase-url), nunca una URL arbitraria.
  const CRM_HOST_POR_OMISION = 'dhygbnysealeblqorjts.supabase.co';
  const RUTA_STORAGE_CRM = /^\/storage\/v1\/object\/(public|sign)\/[A-Za-z0-9_.-]+\/./;

  function adjuntoDelAsesor(att, supabaseUrl) {
    if (!att || typeof att !== 'object' || typeof att.url !== 'string') return null;
    let u;
    let host = CRM_HOST_POR_OMISION;
    try {
      if (supabaseUrl) host = new URL(supabaseUrl).host;
      u = new URL(att.url);
    } catch (e) {
      return null;
    }
    if (u.protocol !== 'https:' || u.username || u.password || u.host !== host) return null;
    if (!RUTA_STORAGE_CRM.test(u.pathname)) return null;
    const nombre = typeof att.name === 'string' ? att.name.trim().slice(0, 120) : '';
    return { url: u.href, kind: att.kind === 'image' ? 'image' : 'file', name: nombre };
  }

  class PpAssistant extends HTMLElement {
    connectedCallback() {
      this.backendUrl = this.getAttribute('data-backend-url') || '/apps/assistant';
      this.testSecret = this.getAttribute('data-test-secret') || '';
      // Página del área de cliente donde está parado el cliente. Vacía fuera
      // del área — en ese caso no se manda nada y Paco se comporta igual que
      // siempre. El backend traduce la llave a texto; aquí solo reportamos.
      this.pageKey = this.getAttribute('data-pp-page-key') || '';
      this.pageHandle = this.getAttribute('data-pp-page-handle') || '';
      this.pagePath = this.getAttribute('data-pp-page-path') || '';
      this.titleText = this.getAttribute('data-title') || 'Asistente Para Paquetes';
      // Customer context — populated por Liquid del theme si el cliente
      // está logueado. Null si anónimo (Paco solo ve session_id).
      this.customerId = this.getAttribute('data-customer-id') || null;
      this.customerFirstName = this.getAttribute('data-customer-first-name') || null;
      this.customerEmail = this.getAttribute('data-customer-email') || null;
      this.subtitleText =
        this.getAttribute('data-subtitle') ||
        'Te ayudo a encontrar el empaque correcto.';
      // Greeting personalizado.
      // El setting del Liquid puede traer "¡Hola! ¿En qué te ayudo?..."
      // (default histórico). Para evitar "¡Hola! ¡Hola! ¿En qué...", quito
      // cualquier "¡Hola..." / "Hola..." inicial del baseGreeting antes
      // de prefixar mi saludo nuevo con el nombre del cliente.
      const rawGreeting =
        this.getAttribute('data-greeting') ||
        'Soy Paco, tu asistente de Para Paquetes. ¿En qué te ayudo? Cuéntame qué vas a enviar o qué empaque buscas.';
      const baseGreeting = rawGreeting.replace(/^¡?Hola[^!.,]*[!.,]?\s*/i, '').trim();
      this.greetingText = this.customerFirstName
        ? `¡Hola ${this.customerFirstName}! ${baseGreeting}`
        : `¡Hola! ${baseGreeting}`;

      // Paco 3.0 — interruptor. ?paco=v3 lo prende para esta pestaña
      // (sessionStorage) y ?paco=v2 lo apaga: así el Padrino lo prueba en
      // producción sin prenderlo para nadie más.
      this.v3 = this._modoV3();
      // perf 5-oct-2026: los ~90 KB de la v3 (pp-paco3.js, su lógica y sus
      // dos hojas) ya no bajan junto con el botón — competían con la imagen
      // principal y nadie los usa con Paco cerrado. Bajan al primer indicio
      // (cursor/dedo/foco en el botón), al abrir (open → _conEstilos) o, si
      // nadie se acercó, cuando la página ya cargó y el navegador está libre.
      if (this.v3) { this.classList.add('pp-assistant--v3'); this._programarV3(); }

      this.isOpen = false;
      this.isStreaming = false;
      this.currentAbortController = null;

      // Mega assistant state
      this.recommendedProducts = new Map(); // product_id → ProductData
      this.cartData = null;                  // último snapshot de /cart.js
      // Se declara aquí junto a cartData porque las dos juntas son las que
      // distinguen "todavía no leí" (cartData null, sin error) de "leí y
      // tronó". Colapsarlas fue el bug del carrito fantasma.
      this.cartLoadError = false;            // el último /cart.js falló
      this.cartLoadingPromise = null;        // dedupe concurrent loads
      this.lastFailedMessage = null;         // para botón "Reintentar"
      this.activeMobileTab = 'chat';         // 'chat' | 'products' | 'cart'
      this.pendingCartOps = new Set();       // line keys con op en vuelo

      // Lead capture state (anónimos)
      this.userTurnCount = 0;                // turnos de usuario en esta page-load
      this.leadConsentLoaded = false;        // texto del consent ya jalado del backend
      this.leadIsSubmitting = false;
      this.senalComercial = false;           // ver TOOLS_INTENCION_COMERCIAL
      this.contactoEnElChat = false;         // ver TOOLS_CONTACTO
      // QA móvil 4-oct-2026, hallazgo pendiente: true tras el primer SSE
      // 'panel' de esta page-load — el aviso de cotización no debe competir
      // con el canvas (lo tapaba en celular) ni repetirse si ya se usó.
      this.canvasAbiertoEnConversacion = false;

      // Sprint 2L: polling vendedor → cliente post-handoff
      this.lastAgentSyncAt = null;           // ISO timestamp del último msg del CRM visto
      this.agentPollTimer = null;            // setInterval handle
      this.botActive = true;                 // true = sigue Paco; false = vendedor a cargo
      this.assignedVendorName = null;        // 'María' | null
      this.renderedAgentMsgIds = new Set();  // dedupe mensajes del CRM ya renderizados
      this.agentPollLastMsgAt = null;        // last activity for auto-stop after idle
      this.postHandoffInfoShown = false;     // Sprint 2AD: notify cliente UNA vez
      // Avisos de progreso: true si el backend mandó un 'estado' en ESTE
      // turno — mientras dure, 'tool_use' deja de pisarlo con _toolStatus
      // (si no, el texto parpadea entre los dos). Se reinicia por turno en
      // _sendMessage.
      this.estadoRecibidoEsteTurno = false;
      this._onVisibility = this._handleVisibilityChange.bind(this);
      document.addEventListener('visibilitychange', this._onVisibility);

      // Bridge externo: el cart drawer (o cualquier otro UI) dispatcha
      // `pp:open-product-modal` con `{handle}` para reutilizar el modal
      // de producto del Paco SIN abrir el chat panel detrás. Hacemos
      // fetch a /products/<handle>.js, mostramos el overlay con el
      // panel hidden via clase `pp-assistant__overlay--modal-only`, y
      // abrimos el modal. Al cerrarlo restauramos.
      this._modalStandalone = false;
      this._onOpenProductModalFromOutside = (e) => {
        const handle = e && e.detail && e.detail.handle;
        if (!handle) return;
        fetch('/products/' + encodeURIComponent(handle) + '.js', {
          headers: { Accept: 'application/json' },
        })
          .then((r) => (r.ok ? r.json() : null))
          .then((data) => {
            if (!data) return;
            const product = this._shopifyJsonToPacoShape(data);
            if (!this.isOpen) {
              this._modalStandalone = true;
              this.overlay.removeAttribute('hidden');
              this.overlay.classList.add('pp-assistant__overlay--modal-only');
            }
            this._openProductModal(product);
          })
          .catch(() => {});
      };
      document.addEventListener(
        'pp:open-product-modal',
        this._onOpenProductModalFromOutside
      );

      // Volvió del login de Paco: la marca se consume ANTES de rehidratar la
      // conversación (si la llave se hubiera perdido, se recupera de la marca).
      this._reabrir = this._consumirMarcaReabrir();
      this._render();
      this._attachEvents();
      // «Atrás» en celular (ver _armarAtrasMovil). Si la página se cargó
      // sobre una entrada que dejó Paco (salió a otra página con Paco
      // abierto y volvió sin caché), esa entrada se reusa al abrir, o se
      // salta al ir atrás: así ningún «atrás» se queda sin hacer nada.
      try {
        this._huerfanaAtras = esCelularPaco() && Boolean(window.history.state && window.history.state.ppPaco);
      } catch (e) { this._huerfanaAtras = false; }
      this._onPopAtras = () => this._alAtrasMovil();
      window.addEventListener('popstate', this._onPopAtras);
      // Pre-cargar cart en background — cuando el user abra el panel ya
      // está fresco. Falla silenciosa si /cart.js no responde.
      this._loadCart().catch(() => {});
      // Rehidratar historial si hay conv previa en localStorage.
      // Sin esto el chat se "borra" al navegar entre páginas.
      // Falla silenciosa (404 = conv expirada → clean slate).
      if (getConversationId()) {
        // Restaurar productos recomendados ANTES de _restoreHistory para
        // que cuando el chat termine de rehidratar el panel derecho ya
        // muestre las cards (sino se ve el empty state un frame).
        this._restoreRecommendedProducts();
        this._restoreHistory()
          .then(() => this._syncAgentMessages(true))
          .catch(() => {});
      }
      if (this._reabrir && this._reabrir.redirigiendo) { this._reabrir = null; return; }
      if (this.dataset.ppPagina === 'true') this._montarPagina();
      else if (this._reabrir) this.open();
    }

    /** Lee y BORRA la marca de «volver a Paco» (una sola vez). */
    _consumirMarcaReabrir() {
      let raw = null;
      try { raw = sessionStorage.getItem('pp_paco_reabrir'); } catch (_) { return null; }
      const m = leerMarcaReabrir(raw, Date.now());
      const limpio = sinParametrosDeShopify(window.location.search);
      // Shopify ignoró el return_to y nos dejó en otra página (/?shop_sign_in=true):
      // UNA vez se va a la ruta guardada (validada); la marca sigue viva para
      // que allá se reabra Paco. «saltado» evita un ciclo.
      if (m && m.ruta && !m.saltado && limpio.habia && (window.location.pathname + limpio.search) !== m.ruta) {
        try {
          const o = JSON.parse(raw);
          o.saltado = true;
          sessionStorage.setItem('pp_paco_reabrir', JSON.stringify(o));
          window.location.replace(m.ruta);
          return { redirigiendo: true };
        } catch (_) { /* si no se puede, se reabre aquí mismo */ }
      }
      try { if (raw) sessionStorage.removeItem('pp_paco_reabrir'); } catch (_) {}
      if (limpio.habia) {
        try { window.history.replaceState(window.history.state, '', window.location.pathname + limpio.search + window.location.hash); } catch (_) {}
      }
      if (m && m.conv && !getConversationId()) setConversationId(m.conv);
      return m;
    }

    /**
     * NUNCA pintar el panel sin su CSS (capturas del Padrino, 4-oct 8.10 AM:
     * la reapertura llamaba open() sin que nadie pidiera pp-assistant-panel.css
     * — ese CSS sólo baja al «acercarse» al botón — y el panel quedó crudo al
     * fondo de la home, con la mascota a todo lo ancho). Las hojas que necesita
     * el panel: la base, la del panel y, en v3, pp-paco3.css. Sin las URLs
     * publicadas (snippet viejo en caché) se comporta como antes.
     */
    _hojasDelPanel() {
      const urls = [this.dataset.ppBaseCss, this.dataset.ppPanelCss];
      if (this.v3) urls.push(this.dataset.ppV3Css);
      if (this.v3) urls.push(this.dataset.ppV3MovilCss); // 4-oct-2026: Paco en celular (sólo <1000px)
      return urls.filter(Boolean).map((u) => String(u).split('?')[0].split('/').pop()).filter(Boolean);
    }

    _hojaLista(nombre) {
      const links = document.querySelectorAll('link[rel="stylesheet"]');
      for (let i = 0; i < links.length; i++) {
        const l = links[i];
        const href = (l.getAttribute('href') || '').split('?')[0];
        if (href.split('/').pop() !== nombre) continue;
        if (l.media === 'print') continue; // la base entra con media=print → all al llegar
        if (l.sheet) return true;
      }
      return false;
    }

    _estilosListos() {
      return this._hojasDelPanel().every((n) => this._hojaLista(n));
    }

    /** Pide lo que falte y espera (máx. 8 s). Resuelve true si todo cargó. */
    _conEstilos() {
      if (this._estilosListos()) return Promise.resolve(true);
      const urls = { [String(this.dataset.ppPanelCss || '').split('?')[0].split('/').pop()]: this.dataset.ppPanelCss };
      if (this.v3) this._cargarV3();
      Object.keys(urls).forEach((nombre) => {
        if (!nombre || !urls[nombre]) return;
        const ya = [...document.querySelectorAll('link[rel="stylesheet"]')].some((l) => (l.getAttribute('href') || '').split('?')[0].split('/').pop() === nombre);
        if (!ya) { const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = urls[nombre]; document.head.appendChild(l); }
      });
      return new Promise((resolve) => {
        const inicio = Date.now();
        const mirar = () => {
          if (this._estilosListos()) return resolve(true);
          if (Date.now() - inicio > 8000) return resolve(false);
          setTimeout(mirar, 50);
        };
        mirar();
      });
    }

    /**
     * /pages/paco: Paco ES la página. Abre al cargar, la X lleva al inicio de
     * la tienda (cerrar dejaría una página vacía) y Escape / clic afuera no
     * lo cierran. ?q= arranca la plática UNA vez: se quita de la URL para que
     * recargar no lo mande otra vez.
     */
    _montarPagina() {
      this._esPagina = true;
      document.body.classList.add('pp-assistant-pagina');
      if (this.closeBtn) this.closeBtn.setAttribute('aria-label', 'Ir al inicio de la tienda');
      this.open();
      const mensaje = mensajeDeEnlace(window.location.search);
      if (!mensaje) return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('q');
        if (url.searchParams.get('vista') === 'pedidos') url.searchParams.delete('vista');
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      } catch (_) {}
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('pp-assistant:open', { detail: { message: mensaje } }));
      }, 400);
    }


    disconnectedCallback() {
      if (this.currentAbortController) {
        this.currentAbortController.abort();
      }
      if (this._keyHandler) {
        document.removeEventListener('keydown', this._keyHandler);
      }
      if (this._onExternalOpen) {
        document.removeEventListener('pp-assistant:open', this._onExternalOpen);
      }
      if (this._onPopAtras) window.removeEventListener('popstate', this._onPopAtras);
      this._seguirTeclado(false);
      if (this._onVisibility) {
        document.removeEventListener('visibilitychange', this._onVisibility);
      }
      if (this._onOpenProductModalFromOutside) {
        document.removeEventListener(
          'pp:open-product-modal',
          this._onOpenProductModalFromOutside
        );
      }
      this._stopAgentPolling();
    }

    /**
     * Decide si esta pestaña corre en modo v3 (pantalla completa). El query
     * param manda sobre todo lo demás porque es la forma en que el Padrino
     * lo prueba en producción sin tocar el setting del customizer: ?paco=v3
     * lo prende y lo recuerda para el resto de la sesión (sessionStorage),
     * ?paco=v2 lo apaga y borra la marca aunque el setting esté prendido.
     */
    _modoV3() {
      let q = null;
      try { q = new URL(window.location.href).searchParams.get('paco'); } catch (_) {}
      try {
        if (q === 'v3') sessionStorage.setItem('pp_paco_v3', '1');
        if (q === 'v2') sessionStorage.removeItem('pp_paco_v3');
      } catch (_) {}
      if (q === 'v2') return false;
      if (q === 'v3') return true;
      if (this.dataset.ppV3 === 'true') return true;
      try { return sessionStorage.getItem('pp_paco_v3') === '1'; } catch (_) { return false; }
    }

    /**
     * Inyecta los assets de la v3 UNA sola vez. Con el modo apagado este
     * método nunca se llama, así que nada nuevo se descarga — `main` sigue
     * siendo producción para todo el mundo.
     */
    /** Agenda _cargarV3: en reposo tras el load, o antes si alguien se acerca. */
    _programarV3() {
      const ya = () => this._cargarV3();
      const enReposo = () => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(ya, { timeout: 3000 });
        else setTimeout(ya, 1500);
      };
      if (document.readyState === 'complete') enReposo();
      else window.addEventListener('load', enReposo, { once: true });
      // Al primer indicio sobre el botón (el botón ya existe: _render corre
      // después en este mismo connectedCallback; la delegación no depende).
      const alAcercarse = (e) => {
        const t = e.target;
        if (!t || !t.closest || !t.closest('[data-pp-assistant-fab],[data-pp-mobile-tab-chat],[data-pp-paco-prompt],[data-pp-open-paco]')) return;
        ['pointerover', 'touchstart', 'focusin'].forEach((n) => document.removeEventListener(n, alAcercarse, true));
        ya();
      };
      ['pointerover', 'touchstart', 'focusin'].forEach((n) => document.addEventListener(n, alAcercarse, { capture: true, passive: true }));
    }

    _cargarV3() {
      if (this._v3Cargada) return;
      this._v3Cargada = true;
      const css = this.dataset.ppV3Css;
      const js = this.dataset.ppV3Js;
      if (css) { const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = css; document.head.appendChild(l); }
      // 4-oct-2026: la v3 en CELULAR (pestañas). Todo su CSS vive en su
      // propio @media (max-width: 999px): en escritorio no pinta nada.
      const movil = this.dataset.ppV3MovilCss;
      if (movil) { const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = movil; document.head.appendChild(l); }
      if (js) { const s = document.createElement('script'); s.src = js; s.defer = true; document.head.appendChild(s); }
    }

    _render() {
      // FAB
      // En escritorio el botón ES la cajita: sin píldora ni etiqueta, la
      // mascota se explica sola. En celular se queda la píldora con texto,
      // que es lo que funciona en una pantalla chica.
      const conCajita = usarCajita();
      const cajita = conCajita ? cajitaSvg() : null;
      this.fab = el('button', {
        cls: conCajita
          ? 'pp-assistant__fab pp-assistant__fab--cajita'
          : 'pp-assistant__fab',
        attrs: {
          type: 'button',
          // La etiqueta accesible se queda aunque desaparezca el texto: para
          // un lector de pantalla la cajita es un dibujo, no una palabra.
          'aria-label': 'Abrir el asistente de compras',
          'data-pp-assistant-fab': true,
        },
        children: cajita
          ? [cajita]
          : [
              svgIcon('0 0 24 24', ICONS.chat),
              el('span', { cls: 'pp-assistant__fab-label', text: 'Pregúntame' }),
            ],
      });
      // Se duerme sola si nadie se mueve, y te anima a preguntar si te le
      // quedas viendo encima sin decidirte.
      if (cajita) vidaDeLaCajita(this.fab, cajita);

      // Header del panel. Guardamos refs a title/subtitle porque
      // post-handoff los actualizamos al nombre del vendedor.
      const titleEl = el('div', {
        cls: 'pp-assistant__title',
        id: 'pp-assistant-title',
        text: this.titleText,
      });
      const subtitleEl = el('div', {
        cls: 'pp-assistant__subtitle',
        text: this.subtitleText,
      });
      this.titleEl = titleEl;
      this.subtitleEl = subtitleEl;
      const brand = el('div', {
        cls: 'pp-assistant__brand',
        children: [
          // Su carita, también en celular: al abrir el chat la cajita se
          // quita de la esquina, y este avatar es lo que dice que sigues
          // hablando con ella y no con otra cosa.
          el('div', {
            cls: 'pp-assistant__avatar pp-assistant__avatar--carita',
            attrs: { 'aria-hidden': 'true' },
            children: [caritaSvg()],
          }),
          el('div', { children: [titleEl, subtitleEl] }),
        ],
      });

      this.resetBtn = el('button', {
        cls: 'pp-assistant__icon-button',
        attrs: {
          type: 'button',
          'aria-label': 'Empezar conversación nueva',
          title: 'Empezar de nuevo',
          'data-pp-assistant-reset': true,
        },
        children: [svgIcon('0 0 24 24', ICONS.refresh)],
      });
      this.closeBtn = el('button', {
        cls: 'pp-assistant__icon-button',
        attrs: {
          type: 'button',
          'aria-label': 'Cerrar',
          'data-pp-assistant-close': true,
        },
        children: [svgIcon('0 0 24 24', ICONS.close)],
      });
      const headerActions = el('div', {
        cls: 'pp-assistant__header-actions',
        children: [this.resetBtn, this.closeBtn],
      });
      const header = el('header', {
        cls: 'pp-assistant__header',
        children: [brand, headerActions],
      });

      // === CHAT COLUMN ===
      // Messages container con el greeting inicial.
      // OJO: aria-live se queda en off — el live announcer dedicado
      // (#liveRegion abajo) acumula deltas con debounce para no spamear
      // al screen reader letra a letra.
      this.messagesEl = el('div', {
        cls: 'pp-assistant__messages',
        attrs: {
          'data-pp-assistant-messages': true,
          role: 'log',
          'aria-label': 'Conversación con el asistente',
        },
        children: [
          el('div', {
            cls: 'pp-assistant__msg pp-assistant__msg--assistant',
            children: [
              el('div', {
                cls: 'pp-assistant__msg-bubble',
                text: this.greetingText,
              }),
            ],
          }),
        ],
      });

      // Live region dedicada — anuncia mensajes completos del asistente
      // cuando termina cada stream (no en cada delta).
      this.liveRegion = el('div', {
        cls: 'pp-assistant__visually-hidden',
        attrs: {
          'aria-live': 'polite',
          'aria-atomic': 'true',
        },
      });

      // Composer
      this.input = el('textarea', {
        cls: 'pp-assistant__input',
        id: 'pp-assistant-input',
        attrs: {
          rows: 1,
          placeholder: PLACEHOLDER_NORMAL,
          maxlength: MAX_MESSAGE_LEN,
          'data-pp-assistant-input': true,
        },
      });
      this.sendBtn = el('button', {
        cls: 'pp-assistant__send',
        attrs: {
          type: 'submit',
          'aria-label': 'Enviar',
          'data-pp-assistant-send': true,
          disabled: true,
        },
        children: [svgIcon('0 0 24 24', ICONS.send)],
      });
      this.fotoInput = el('input', {
        cls: 'pp-assistant__visually-hidden',
        // tabindex="-1": está oculto con clip/position:absolute (no
        // display:none), así que sin esto un usuario de teclado pasa por un
        // stop invisible antes de llegar al botón visible del clip.
        attrs: {
          type: 'file',
          // PDF: la Constancia de Situación Fiscal (13-oct-2026). Un PDF en el
          // chat sólo puede ser eso; ver _recibirConstancia.
          accept: 'image/*,application/pdf',
          id: 'pp-assistant-foto',
          tabindex: '-1',
        },
      });
      this.fotoBtn = el('button', {
        cls: 'pp-assistant__adjuntar',
        attrs: { type: 'button', 'aria-label': 'Adjuntar una foto o tu constancia fiscal' },
        children: [svgIcon('0 0 24 24', ICONS.clip)],
      });
      this.fotoPrevia = el('div', { cls: 'pp-assistant__foto-previa', attrs: { hidden: true } });

      this.form = el('form', {
        attrs: { 'data-pp-assistant-form': true },
        children: [
          el('label', {
            cls: 'pp-assistant__visually-hidden',
            attrs: { for: 'pp-assistant-input' },
            text: 'Mensaje al asistente',
          }),
          this.fotoInput,
          this.fotoBtn,
          this.input,
          this.sendBtn,
        ],
      });
      // Link discreto "Hablar con asesor humano" — siempre visible.
      // Filosofía CEO: que esté ahí para usarlo, pero sin que sea botón
      // gigante (queremos que prueben el AI primero). Después del audit
      // del CRM real se transforma en handoff verdadero; por ahora abre
      // WhatsApp/correo del equipo.
      this.humanHandoffLink = el('button', {
        cls: 'pp-assistant__human-handoff',
        attrs: { type: 'button', 'data-pp-human-handoff': true },
        text: '¿Necesitas ayuda humana? Habla con un asesor →',
      });

      const composer = el('footer', {
        cls: 'pp-assistant__composer',
        children: [
          this.fotoPrevia,
          this.form,
          el('div', {
            cls: 'pp-assistant__disclaimer',
            text: 'Puedo equivocarme. Verifica detalles importantes antes de comprar.',
          }),
          this.humanHandoffLink,
        ],
      });

      // FAQ chips: respuestas precargadas que NO disparan el LLM.
      // Visible solo al inicio de la conversación (auto-oculto al primer
      // user message — ver _appendMessage / _reset).
      this.faqChipsEl = this._renderFaqChips();

      const chatCol = el('div', {
        cls: 'pp-assistant__chat-col',
        children: [this.messagesEl, this.faqChipsEl, composer],
      });

      // === CONTENT COLUMN (desktop sidebar / mobile bottom) ===
      // Section: Productos recomendados
      this.productsEmpty = el('div', {
        cls: 'pp-assistant__products-empty',
        text:
          'Cuéntame qué vas a enviar o qué vendes — te recomiendo el empaque ideal con precio, stock y un botón para agregarlo al carrito.',
      });
      this.productsList = el('div', {
        cls: 'pp-assistant__products-list',
        attrs: { 'data-pp-assistant-products': true },
      });
      const productsSection = el('section', {
        cls: 'pp-assistant__section pp-assistant__section--products',
        children: [
          el('div', {
            cls: 'pp-assistant__section-header',
            children: [
              el('div', {
                cls: 'pp-assistant__section-icon',
                children: [svgIcon('0 0 24 24', ICONS.package)],
              }),
              el('h3', {
                cls: 'pp-assistant__section-title',
                text: 'Productos recomendados',
              }),
            ],
          }),
          el('div', {
            cls: 'pp-assistant__section-body',
            children: [this.productsEmpty, this.productsList],
          }),
        ],
      });

      // Section: Tu carrito — collapsible bottom bar
      // Cuando el carrito está vacío: hidden entero.
      // Con items: header compacto (count + subtotal + chevron) sticky abajo;
      // click expande mostrando items + ahorro + botón checkout.
      this.cartItemsEl = el('div', {
        cls: 'pp-assistant__cart-items',
        attrs: { 'data-pp-assistant-cart-items': true },
      });
      this.cartExpandedBody = el('div', {
        cls: 'pp-assistant__cart-expanded',
        attrs: { hidden: true },
        children: [this.cartItemsEl],
      });
      // Header siempre visible: chevron + label + count + subtotal compacto
      this.cartHeaderSubtotal = el('span', {
        cls: 'pp-assistant__cart-header-subtotal',
        text: '$0.00',
      });
      this.cartCountBadge = el('span', {
        cls: 'pp-assistant__cart-count',
        text: '0',
      });
      this.cartToggleBtn = el('button', {
        cls: 'pp-assistant__cart-toggle',
        attrs: {
          type: 'button',
          'aria-expanded': 'false',
          'aria-controls': 'pp-cart-expanded',
          'aria-label': 'Mostrar items del carrito',
          'data-pp-cart-toggle': true,
        },
        children: [
          el('div', {
            cls: 'pp-assistant__cart-header-icon',
            children: [svgIcon('0 0 24 24', ICONS.cart)],
          }),
          el('div', {
            cls: 'pp-assistant__cart-header-text',
            children: [
              el('span', {
                cls: 'pp-assistant__cart-header-label',
                text: 'Tu carrito',
              }),
            ],
          }),
          // count a la derecha (donde antes iba el subtotal); subtotal
          // ahora solo vive en el row inferior.
          this.cartCountBadge,
          el('div', {
            cls: 'pp-assistant__cart-chevron',
            attrs: { 'aria-hidden': 'true' },
          }),
        ],
      });
      this.cartExpandedBody.id = 'pp-cart-expanded';

      this.cartSection = el('section', {
        cls: 'pp-assistant__cart-dock',
        attrs: { hidden: true }, // hidden hasta que haya items
        children: [this.cartToggleBtn, this.cartExpandedBody],
      });

      // Empty state cuando user pone tab=Carrito pero el carrito está vacío
      // (en mobile). Visible solo via CSS data-pp-active-tab=cart + sin items.
      this.cartEmpty = el('div', {
        cls: 'pp-assistant__cart-empty-state',
        children: [
          el('div', {
            cls: 'pp-assistant__cart-empty-icon',
            children: [svgIcon('0 0 24 24', ICONS.cart)],
          }),
          el('p', {
            cls: 'pp-assistant__cart-empty-text',
            text: 'Tu carrito está vacío. Pídele a Paco que te recomiende productos.',
          }),
        ],
      });

      this.contentCol = el('aside', {
        cls: 'pp-assistant__content-col',
        attrs: { 'data-pp-assistant-content': true },
        children: [productsSection, this.cartSection, this.cartEmpty],
      });

      // === MOBILE TABS (solo visibles < 1000px via CSS) ===
      // Permiten al user alternar Chat / Productos / Carrito en mobile
      // donde el split desktop no cabe.
      this.tabBtns = {};
      const tabSpec = [
        { id: 'chat', label: 'Chat' },
        { id: 'products', label: 'Productos', badgeKey: 'products' },
        { id: 'cart', label: 'Carrito', badgeKey: 'cart' },
      ];
      const tabsRow = el('div', {
        cls: 'pp-assistant__tabs',
        attrs: { role: 'tablist', 'aria-label': 'Vistas del asistente' },
        children: tabSpec.map((t) => {
          const badge = el('span', {
            cls: 'pp-assistant__tab-badge',
            attrs: { hidden: true, 'aria-hidden': 'true' },
            text: '',
          });
          const btn = el('button', {
            cls: 'pp-assistant__tab',
            attrs: {
              type: 'button',
              role: 'tab',
              'aria-selected': t.id === 'chat' ? 'true' : 'false',
              'aria-controls': 'pp-tabpanel-' + t.id,
              'data-pp-tab': t.id,
            },
            children: [
              el('span', { cls: 'pp-assistant__tab-label', text: t.label }),
              badge,
            ],
          });
          this.tabBtns[t.id] = { btn, badge };
          return btn;
        }),
      });

      // === BODY ROW ===
      // chatCol y contentCol obtienen role=tabpanel + ids para tabs en mobile.
      chatCol.setAttribute('role', 'tabpanel');
      chatCol.setAttribute('id', 'pp-tabpanel-chat');
      chatCol.setAttribute('data-pp-panel', 'chat');
      this.contentCol.setAttribute('id', 'pp-tabpanel-products');
      this.contentCol.setAttribute('data-pp-panel', 'products');

      const body = el('div', {
        cls: 'pp-assistant__body',
        children: [chatCol, this.contentCol],
      });

      const panel = el('div', {
        cls: 'pp-assistant__panel',
        attrs: {
          role: 'dialog',
          'aria-modal': 'true',
          'aria-labelledby': 'pp-assistant-title',
        },
        children: [header, tabsRow, body, this.liveRegion],
      });
      // Se guarda porque el arrastrar-y-soltar de fotos necesita un
      // contenedor al que colgarle los eventos y la clase visual.
      this.panel = panel;

      // Toast (notificaciones de éxito al agregar al carrito)
      this.toastEl = el('div', {
        cls: 'pp-assistant__toast',
        attrs: { 'data-pp-assistant-toast': true, hidden: true, role: 'status' },
      });

      // Lead capture modal — overlay-within-overlay, escondido por default
      this.leadModal = this._buildLeadModal();

      // Product detail modal (bottom-sheet style mobile) — armado lazy en
      // _buildProductModal, hidden por default.
      this.productModal = this._buildProductModal();

      this.overlay = el('div', {
        cls: 'pp-assistant__overlay',
        attrs: { 'data-pp-assistant-overlay': true, hidden: true },
        children: [panel, this.toastEl, this.leadModal, this.productModal],
      });

      // Limpiar el host y agregar
      while (this.firstChild) this.removeChild(this.firstChild);
      this.appendChild(this.fab);
      this.appendChild(this.overlay);

      // Render inicial del cart si ya tenemos data (pre-cargado)
      this._renderCart();
    }

    /**
     * Modal de lead capture para clientes anónimos.
     * Aparece después de 3+ turnos cuando hay señales de intent comercial
     * (carrito con items O conversación de cotización). El cliente decide
     * dar contacto + consentir marketing o cerrar y seguir conversando.
     */
    _buildLeadModal() {
      const titleEl = el('h3', {
        cls: 'pp-assistant__lead-modal-title',
        text: '¿Te guardamos esta cotización?',
      });
      const subtitleEl = el('p', {
        cls: 'pp-assistant__lead-modal-subtitle',
        text:
          'Déjanos tu contacto y un asesor te da seguimiento. Lo agarras cuando puedas — no perdemos lo que ya armamos.',
      });

      // Los tres inputs llevan aria-label además del placeholder: el
      // placeholder DESAPARECE al escribir, así que por sí solo no es un
      // nombre accesible. Hasta el 5-sep-2026 el lector de pantalla leía
      // "editar texto" tres veces seguidas — justo en el formulario que
      // captura al lead que llegó por el chat.
      this.leadNameInput = el('input', {
        cls: 'pp-assistant__lead-input',
        attrs: {
          type: 'text',
          name: 'name',
          placeholder: 'Tu nombre (opcional)',
          'aria-label': 'Tu nombre',
          autocomplete: 'name',
          maxlength: '60',
        },
      });
      this.leadWhatsappInput = el('input', {
        cls: 'pp-assistant__lead-input',
        attrs: {
          type: 'tel',
          name: 'whatsapp',
          placeholder: 'WhatsApp (10 dígitos)',
          'aria-label': 'Tu WhatsApp, 10 dígitos',
          autocomplete: 'tel',
          inputmode: 'tel',
          maxlength: '20',
        },
      });
      this.leadEmailInput = el('input', {
        cls: 'pp-assistant__lead-input',
        attrs: {
          type: 'email',
          name: 'email',
          placeholder: 'Email (opcional)',
          'aria-label': 'Tu correo electrónico',
          autocomplete: 'email',
          maxlength: '120',
        },
      });

      this.leadConsentCheckbox = el('input', {
        attrs: {
          type: 'checkbox',
          id: 'pp-lead-consent',
          name: 'consent_marketing',
        },
      });
      this.leadConsentText = el('span', {
        cls: 'pp-assistant__lead-consent-text',
        text: 'Cargando consentimiento…',
      });
      const consentLabel = el('label', {
        cls: 'pp-assistant__lead-consent-label',
        attrs: { for: 'pp-lead-consent' },
        children: [this.leadConsentCheckbox, this.leadConsentText],
      });

      this.leadStatusEl = el('div', {
        cls: 'pp-assistant__lead-status',
        attrs: { 'aria-live': 'polite' },
      });

      this.leadSubmitBtn = el('button', {
        cls: 'pp-assistant__lead-submit',
        attrs: { type: 'submit', 'data-pp-lead-submit': true },
        text: 'Guardar mi contacto',
      });
      const dismissBtn = el('button', {
        cls: 'pp-assistant__lead-dismiss',
        attrs: { type: 'button', 'data-pp-lead-dismiss': true },
        text: 'Después',
      });

      this.leadForm = el('form', {
        cls: 'pp-assistant__lead-form',
        attrs: { 'data-pp-lead-form': true, novalidate: true },
        children: [
          this.leadNameInput,
          this.leadWhatsappInput,
          this.leadEmailInput,
          consentLabel,
          this.leadStatusEl,
          el('div', {
            cls: 'pp-assistant__lead-actions',
            children: [dismissBtn, this.leadSubmitBtn],
          }),
        ],
      });

      // ✕ sólo se ve en la tarjeta compacta de celular (CSS la esconde por
      // default); en escritorio queda igual que siempre — "Después" en el
      // form sigue siendo su única salida visible (QA 4-oct-2026).
      const closeBtn = el('button', {
        cls: 'pp-assistant__lead-card-close',
        attrs: { type: 'button', 'data-pp-lead-dismiss': true, 'aria-label': 'Cerrar' },
        text: '✕',
      });
      const headRow = el('div', {
        cls: 'pp-assistant__lead-card-head',
        children: [titleEl, closeBtn],
      });

      this.leadCard = el('div', {
        cls: 'pp-assistant__lead-card',
        attrs: { role: 'dialog', 'aria-labelledby': 'pp-lead-title' },
        children: [headRow, subtitleEl, this.leadForm],
      });
      titleEl.id = 'pp-lead-title';

      // Delegado en la tarjeta (no en el overlay): así sigue atendiendo el
      // ✕/"Después" tanto si la tarjeta vive dentro de leadModal (escritorio)
      // como si se montó dentro del chat (tarjeta compacta de celular).
      this.leadCard.addEventListener('click', (e) => {
        const dismiss = e.target.closest('[data-pp-lead-dismiss]');
        if (dismiss) {
          e.preventDefault();
          this._dismissLeadCapture();
        }
      });

      return el('div', {
        cls: 'pp-assistant__lead-modal',
        attrs: { 'data-pp-lead-modal': true, hidden: true },
        children: [this.leadCard],
      });
    }

    /**
     * Construye el bloque de FAQ chips — botones precargados que
     * responden queries genéricas SIN tocar el LLM (cero tokens).
     *
     * Cada chip mapea a un topic de COMPANY_INFO_TOPICS (lib/tools/company-info.ts
     * del backend). Al hacer click, _handleFaqChipClick hace fetch a
     * /apps/assistant/api/faq/<topic> y renderiza la respuesta como bubble
     * del assistant. La pregunta del user (label del chip sin emoji) se
     * muestra como bubble user antes para mantener el flujo conversacional.
     *
     * Comportamiento de visibilidad:
     *  - Visible al inicio (greeting solo, sin user messages)
     *  - Oculto al primer _appendMessage('user', …)
     *  - Vuelve a visible al reset
     */
    _renderFaqChips() {
      // 2 grupos visibles al inicio:
      //  1. Venta: chips que disparan el LLM con prompt vendedor (el user
      //     viene a Paco a COMPRAR, los primeros chips deben ofrecer eso).
      //  2. Operativos: FAQs preset que NO tocan el LLM (cero tokens) —
      //     accesibles abajo para quien tenga una duda específica.
      // Cada chip de venta tiene data-pp-faq-action='send_message' y
      // data-pp-prompt-message con el prompt que se manda al LLM como
      // si el user lo hubiera escrito (ver _handleFaqChipClick).
      const salesChips = [
        {
          action: 'send_message',
          emoji: '📦',
          label: '¿Qué empaque necesito?',
          message: 'Tengo un negocio y no sé qué empaque me conviene. ¿Me ayudas a elegir según lo que vendo?',
        },
        {
          action: 'send_message',
          emoji: '📏',
          label: 'Encuentra tu medida',
          message: 'Necesito una medida específica de caja o bolsa. Te digo lo que voy a enviar y me recomiendas.',
        },
        {
          action: 'send_message',
          emoji: '🏷️',
          label: 'Precios mayoreo',
          message: '¿Cómo funcionan los descuentos por volumen? Quiero pedir mayoreo para mi negocio.',
        },
        {
          action: 'send_message',
          emoji: '⚡',
          label: 'Lo más vendido',
          message: 'Muéstrame los empaques más vendidos para que vea qué usan otros negocios como el mío.',
        },
      ];
      const infoChips = [
        { topic: 'envios', emoji: '🚚', label: 'Envíos' },
        { topic: 'facturacion', emoji: '🧾', label: 'Facturación' },
        {
          topic: 'descuentos_volumen_politica',
          emoji: '📊',
          label: 'Política de descuentos',
        },
        { topic: 'ubicaciones', emoji: '📍', label: 'Ubicaciones' },
        { topic: 'horarios', emoji: '🕒', label: 'Horarios' },
        { topic: 'metodos_de_pago', emoji: '💳', label: 'Métodos de pago' },
        { topic: 'devoluciones', emoji: '↩️', label: 'Devoluciones' },
        { topic: 'contacto', emoji: '📞', label: 'Contacto' },
      ];
      const spec = salesChips.concat(infoChips);

      const row = el('div', { cls: 'pp-assistant__faq-chips-row' });

      spec.forEach((s) => {
        const attrs = {
          type: 'button',
          'data-pp-faq-label': s.label,
          'aria-label': s.label,
        };
        if (s.action) {
          attrs['data-pp-faq-action'] = s.action;
          if (s.message) attrs['data-pp-prompt-message'] = s.message;
        } else if (s.topic) {
          attrs['data-pp-faq-topic'] = s.topic;
        }
        const cls = s.action === 'send_message'
          ? 'pp-assistant__faq-chip pp-assistant__faq-chip--sales'
          : 'pp-assistant__faq-chip';
        const btn = el('button', {
          cls,
          attrs,
          text: `${s.emoji} ${s.label}`,
        });
        row.appendChild(btn);
      });

      // Escalation chip — variant accent. Sprint 1: solo placeholder
      // explicando contacto directo; Sprint 2 enchufa el handoff real.
      const escalateBtn = el('button', {
        cls: 'pp-assistant__faq-chip pp-assistant__faq-chip--escalate',
        attrs: {
          type: 'button',
          'data-pp-faq-action': 'talk_to_human',
          'data-pp-faq-label': 'Hablar con asesor',
          'aria-label': 'Hablar con un asesor humano',
        },
        text: '👤 Hablar con asesor',
      });
      row.appendChild(escalateBtn);

      const wrap = el('div', {
        cls: 'pp-assistant__faq-chips',
        attrs: { 'data-pp-faq-chips': true },
        children: [
          el('div', {
            cls: 'pp-assistant__faq-chips-label',
            text: 'Respuestas rápidas',
          }),
          row,
        ],
      });

      return wrap;
    }

    /**
     * Click en FAQ chip → fetch al endpoint estático y render como bubble.
     * NO toca el backend del chat (cero tokens IA).
     * NO persiste en messages table (queries hardcodeadas no entran al
     * history de Anthropic).
     */
    async _handleFaqChipClick(btn) {
      if (!btn || this.isStreaming) return;

      const action = btn.getAttribute('data-pp-faq-action');
      const topic = btn.getAttribute('data-pp-faq-topic');
      const label = btn.getAttribute('data-pp-faq-label') || '';

      // Escalation: comparte handler con el link permanente del composer
      if (action === 'talk_to_human') {
        this._handleHumanHandoff(label || 'Hablar con asesor');
        return;
      }

      // Sales chip: manda el prompt como mensaje del user al chat normal
      // (entra al LLM como si el user lo hubiera tipeado). Oculta los
      // chips antes para que el chat fluya natural.
      if (action === 'send_message') {
        const message = btn.getAttribute('data-pp-prompt-message') || label;
        if (!message) return;
        if (this.faqChipsEl) this.faqChipsEl.setAttribute('hidden', '');
        this._sendMessage(message);
        return;
      }

      if (!topic) return;

      // Render del user bubble primero (pregunta implícita) para que el
      // flujo se vea natural.
      this._appendMessage('user', label || topic);
      if (this.faqChipsEl) this.faqChipsEl.setAttribute('hidden', '');

      // Loading bubble del assistant con dots animados
      const pendingWrap = this._appendMessage('assistant', '', { typing: true });
      const pendingBubble =
        pendingWrap && pendingWrap.querySelector('.pp-assistant__msg-bubble');

      // Deshabilita el chip clickeado para evitar doble fetch mientras
      // está en vuelo (el resto de chips se ocultan con el wrapper hidden,
      // pero por accesibilidad y safety lo marcamos también).
      btn.setAttribute('disabled', '');

      const url =
        this.backendUrl.replace(/\/$/, '') +
        '/api/faq/' +
        encodeURIComponent(topic);

      const headers = { Accept: 'application/json' };
      if (this.testSecret) headers['x-assistant-test-secret'] = this.testSecret;

      try {
        const res = await fetch(url, {
          method: 'GET',
          headers,
          credentials: 'omit',
        });
        if (!res.ok) {
          throw new Error('HTTP ' + res.status);
        }
        const data = await res.json();
        if (!data || !data.ok || typeof data.content_es !== 'string') {
          throw new Error('respuesta inválida');
        }

        // Render del contenido en el bubble pending (reemplaza dots).
        if (pendingBubble) {
          pendingBubble.classList.remove('pp-assistant__msg-bubble--typing');
          while (pendingBubble.firstChild) {
            pendingBubble.removeChild(pendingBubble.firstChild);
          }
          // Guardar raw text para a11y announce + reaplicar si hace falta.
          pendingBubble._rawText = data.content_es;
          pendingBubble.appendChild(renderMarkdown(data.content_es));

          // Source link al final si existe.
          if (data.source_url) {
            const sourceWrap = el('div', {
              cls: 'pp-assistant__faq-source',
              children: [
                el('a', {
                  cls: 'pp-assistant__faq-source-link',
                  attrs: {
                    href: String(data.source_url),
                    target: '_blank',
                    rel: 'noopener',
                  },
                  text: 'Ver detalles en parapaquetes.com →',
                }),
              ],
            });
            pendingBubble.appendChild(sourceWrap);
          }

          this._announce(data.content_es);
        }
        this._scrollToBottom();
      } catch (err) {
        // Falla → bubble de error con sugerencia de preguntar a Paco.
        if (pendingBubble) {
          pendingBubble.classList.remove('pp-assistant__msg-bubble--typing');
          pendingBubble.classList.add('pp-assistant__msg-bubble--error');
          while (pendingBubble.firstChild) {
            pendingBubble.removeChild(pendingBubble.firstChild);
          }
          pendingBubble.appendChild(
            document.createTextNode(
              'No pude cargar la respuesta. Escríbele tu pregunta a Paco en el chat.'
            )
          );
        }
        console.warn('[pp-assistant] faq fetch failed:', err);
      } finally {
        // El chip queda disabled — los chips se reaparecen solo al reset.
        // No re-habilitamos para evitar spam.
        btn.removeAttribute('disabled');
      }
    }

    // Hallazgo C4 (revisión final 27-ago-2026): el botón de Enviar se
    // habilita con texto O con una foto pendiente — antes sólo se
    // recalculaba en el evento 'input' del textarea mirando sólo el texto,
    // así que adjuntar una foto sin escribir nada dejaba el botón gris sin
    // explicación (el cliente veía la miniatura pero no podía mandarla).
    // Centralizado aquí y llamado desde _pintarFotoPrevia (que ya corre en
    // cada punto donde fotoPendiente cambia: adjuntar, quitar, reset,
    // después de subir) para no tener que acordarse de llamarlo aparte.
    _recalcularBotonEnviar() {
      // El texto es obligatorio SIEMPRE, haya foto o no. Ver
      // PLACEHOLDER_CON_FOTO: la pista de por qué sigue gris la da el
      // placeholder, no un botón mudo.
      this.sendBtn.disabled =
        this.input.value.trim().length === 0 || this.isStreaming;
    }

    _pintarFotoPrevia() {
      this._recalcularBotonEnviar();
      // Corre en TODO punto donde fotoPendiente cambia (adjuntar, quitar,
      // reset, después de subir), así que el placeholder no se queda pegado.
      this.input.placeholder = this.fotoPendiente
        ? PLACEHOLDER_CON_FOTO
        : PLACEHOLDER_NORMAL;
      while (this.fotoPrevia.firstChild) {
        this.fotoPrevia.removeChild(this.fotoPrevia.firstChild);
      }
      if (!this.fotoPendiente) {
        this.fotoPrevia.hidden = true;
        return;
      }
      const img = document.createElement('img');
      img.src = URL.createObjectURL(this.fotoPendiente);
      img.alt = 'Foto por enviar';
      // Se libera al cargar: si no, el objectURL queda vivo toda la sesión.
      img.onload = () => URL.revokeObjectURL(img.src);
      const quitar = document.createElement('button');
      quitar.type = 'button';
      quitar.className = 'pp-assistant__foto-quitar';
      quitar.setAttribute('aria-label', 'Quitar la foto');
      quitar.textContent = '✕';
      quitar.addEventListener('click', () => {
        this.fotoPendiente = null;
        this._pintarFotoPrevia();
      });
      // Una foto puede ser de un producto… o de la constancia. No se adivina:
      // se le ofrece, y si lo es no viaja al modelo como foto.
      const esConstancia = document.createElement('button');
      esConstancia.type = 'button';
      esConstancia.className = 'pp-assistant__foto-constancia';
      esConstancia.textContent = 'Es mi constancia fiscal';
      esConstancia.addEventListener('click', () => {
        const foto = this.fotoPendiente;
        this.fotoPendiente = null;
        this._pintarFotoPrevia();
        if (foto) this._recibirConstancia(foto);
      });
      this.fotoPrevia.appendChild(img);
      this.fotoPrevia.appendChild(quitar);
      this.fotoPrevia.appendChild(esConstancia);
      this.fotoPrevia.hidden = false;
    }

    /**
     * Único camino para adjuntar una foto, venga del clip, del portapapeles
     * o de arrastrarla. Antes esto vivía dentro del handler del `change` del
     * input, y agregar un segundo camino habría duplicado el achicado —que
     * es lo que convierte el HEIC de Safari en JPEG— con lo fácil que es
     * que a la copia se le olvide.
     */
    async _aceptarFoto(file) {
      // Un PDF en el chat es la constancia: no va al modelo como «foto», se lee
      // y se guarda en la cuenta (_recibirConstancia).
      if (file && file.type === 'application/pdf') {
        this._recibirConstancia(file);
        return;
      }
      if (!file || !file.type || file.type.indexOf('image/') !== 0) {
        this._showToast('Eso no lo puedo abrir. Manda una foto, o tu constancia fiscal en PDF.', { error: true });
        return;
      }
      try {
        this.fotoPendiente = await achicarFoto(file);
        this._pintarFotoPrevia();
        this.input.focus();
      } catch {
        this._showToast('No pude leer esa imagen. Intenta con otra.', { error: true });
      }
    }

    /**
     * Abre el selector para mandar la constancia (mosaico «Datos fiscales» de
     * la bienvenida). Público: lo llama la v3 (assets/pp-paco3.js).
     */
    pedirConstancia() {
      this._esperaConstancia = true;
      // Si cancela el selector no hay `change`: la marca no debe quedarse
      // pegada para la siguiente foto que adjunte con el clip.
      window.addEventListener('focus', () => setTimeout(() => { this._esperaConstancia = false; }, 1500), { once: true });
      if (typeof this._switchTab === 'function') { try { this._switchTab('chat'); } catch (_) { /* escritorio: sin pestañas */ } }
      this.fotoInput.click();
    }

    /** La pieza de la constancia (assets/pp-fiscal-constancia.js), bajo demanda. */
    _conConstancia() {
      if (window.PPFiscalConstancia) return Promise.resolve(window.PPFiscalConstancia);
      if (this._constanciaP) return this._constanciaP;
      const url = this.getAttribute('data-pp-fiscal-constancia');
      if (!url) return Promise.resolve(null);
      this._constanciaP = new Promise((resolve) => {
        const s = document.createElement('script');
        let listo = false;
        const fin = () => { if (listo) return; listo = true; resolve(window.PPFiscalConstancia || null); };
        s.src = url;
        s.onload = fin;
        s.onerror = () => { this._constanciaP = null; fin(); };
        setTimeout(fin, 10000);
        document.head.appendChild(s);
      });
      return this._constanciaP;
    }

    /**
     * «Mándale tu constancia a Paco y él la guarda en tu cuenta» (Padrino,
     * 13-oct-2026). La constancia NO va al modelo de la conversación: la lee
     * el mismo lector de la cuenta (/apps/crm/customer-constancia) y, si vino
     * completa, se guarda como perfil fiscal (/apps/crm/customer-fiscal). Es
     * determinista a propósito: un RFC o una razón social no se «interpretan».
     * Lo que dice Paco lo arma PPFiscalConstancia.mensajeDeGuardado.
     */
    async _recibirConstancia(file) {
      if (this._leyendoConstancia) return;
      this._leyendoConstancia = true;
      const esPdf = file.type === 'application/pdf';
      this._appendMessage('user', 'Te mando mi constancia de situación fiscal.', esPdf ? undefined : { fotoFile: file });
      const espera = this._appendMessage('assistant', '', { typing: true });
      const decir = (m) => {
        const burbuja = espera.querySelector('.pp-assistant__msg-bubble');
        if (burbuja) {
          burbuja.classList.remove('pp-assistant__msg-bubble--typing');
          burbuja.textContent = m.texto;
          const href = m.login ? this.getAttribute('data-pp-cuenta-login') : (m.liga ? '/pages/cliente-facturacion' : '');
          if (href) {
            const a = document.createElement('a');
            a.className = 'pp-assistant__msg-liga';
            a.href = href;
            a.textContent = m.login ? 'Entrar a mi cuenta' : 'Ver mis datos fiscales';
            burbuja.appendChild(document.createElement('br'));
            burbuja.appendChild(a);
          }
        }
        this._announce(m.texto);
        this._scrollToBottom();
      };
      try {
        const C = await this._conConstancia();
        if (!C) { decir({ texto: 'No pude abrir el lector en este momento. Guarda tus datos fiscales desde tu cuenta.', liga: true }); return; }
        const cliente = { email: this.customerEmail || '', first_name: this.customerFirstName || '' };
        // Sin sesión no hay cuenta dónde guardar: se dice antes de leer nada.
        if (!this.customerId) { decir(C.mensajeDeGuardado({ tipo: 'sesion' }, null, cliente)); return; }
        const lectura = await C.leer(file);
        if (!lectura.ok) { decir({ texto: lectura.error }); return; }
        const guardado = await C.guardarEnCuenta(lectura.datos, lectura.faltan, cliente);
        decir(C.mensajeDeGuardado(guardado, lectura.datos, cliente));
      } catch (_) {
        decir({ texto: 'No pude leer tu constancia en este momento. Guarda tus datos fiscales desde tu cuenta.', liga: true });
      } finally {
        this._leyendoConstancia = false;
      }
    }

    /**
     * Pegar y arrastrar. El clip ya existía; estos dos son los gestos que la
     * gente intenta primero cuando trae la foto en el portapapeles (captura
     * de pantalla) o en el escritorio.
     */
    _attachFotoGestos() {
      // PEGAR — sobre el textarea, que es donde el cursor ya está.
      this.input.addEventListener('paste', (e) => {
        const items = (e.clipboardData && e.clipboardData.items) || [];
        for (let i = 0; i < items.length; i++) {
          if (items[i].kind === 'file' && items[i].type.indexOf('image/') === 0) {
            // Sólo se intercepta cuando de verdad hay imagen: pegar texto
            // normal tiene que seguir funcionando igual.
            e.preventDefault();
            this._aceptarFoto(items[i].getAsFile());
            return;
          }
        }
      });

      // ARRASTRAR — sobre el panel entero, no sólo el textarea: quien
      // arrastra un archivo apunta a la ventana, no a un campo de 36 px.
      //
      // El contador existe porque `dragleave` también dispara al pasar de un
      // hijo a otro dentro del panel. Sin él, el resaltado parpadea y se
      // apaga a media ventana.
      let dentro = 0;
      const traeArchivo = (e) =>
        !!(e.dataTransfer && Array.prototype.indexOf.call(e.dataTransfer.types || [], 'Files') !== -1);

      this.panel.addEventListener('dragenter', (e) => {
        if (!traeArchivo(e)) return;
        e.preventDefault();
        dentro++;
        this.panel.classList.add('is-arrastrando');
      });
      this.panel.addEventListener('dragover', (e) => {
        // Sin este preventDefault el navegador NO emite `drop`: abre la
        // imagen en la pestaña y se pierde la conversación.
        if (traeArchivo(e)) e.preventDefault();
      });
      this.panel.addEventListener('dragleave', (e) => {
        if (!traeArchivo(e)) return;
        dentro = Math.max(0, dentro - 1);
        if (dentro === 0) this.panel.classList.remove('is-arrastrando');
      });
      this.panel.addEventListener('drop', (e) => {
        if (!traeArchivo(e)) return;
        e.preventDefault();
        dentro = 0;
        this.panel.classList.remove('is-arrastrando');
        const files = e.dataTransfer.files;
        if (!files || !files.length) return;
        // MAX_FOTOS_POR_MENSAJE es 1: si sueltan varias, se toma la primera
        // y se dice, en vez de mandar una al azar en silencio.
        if (files.length > MAX_FOTOS_POR_MENSAJE) {
          this._showToast('Va una foto por mensaje — tomé la primera.');
        }
        this._aceptarFoto(files[0]);
      });
    }

    _attachEvents() {
      this.fab.addEventListener('click', () => this.open());
      this.closeBtn.addEventListener('click', () => this.close());
      this.resetBtn.addEventListener('click', () => this._reset());

      this.fotoBtn.addEventListener('click', () => this.fotoInput.click());
      this.fotoInput.addEventListener('change', () => {
        const file = this.fotoInput.files && this.fotoInput.files[0];
        this.fotoInput.value = '';
        // Si lo pidió «Datos fiscales», lo elegido ES la constancia (PDF o foto).
        const esConstancia = this._esperaConstancia;
        this._esperaConstancia = false;
        if (!file) return;
        if (esConstancia) this._recibirConstancia(file);
        else this._aceptarFoto(file);
      });

      this._attachFotoGestos();

      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          this.form.requestSubmit();
        }
      });

      this.input.addEventListener('input', () => {
        this._recalcularBotonEnviar();
        this._autosize();
      });

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        const msg = this.input.value.trim();
        // El texto es obligatorio aunque haya foto (decisión del dueño,
        // 28-ago-2026): sin una línea que diga para qué es la imagen, Paco
        // adivina. El backend SIGUE aceptando un turno sólo-foto a
        // propósito — un widget viejo cacheado no debe romperse — pero
        // desde aquí ya no se produce.
        if (!msg || this.isStreaming) return;
        this._sendMessage(msg);
      });
      // Celular: tocar «Enviar» no le quita el foco al campo — el teclado se
      // queda arriba y se sigue escribiendo (modo escribiendo), como en
      // cualquier app de mensajes. El clic (submit) llega igual.
      this.sendBtn.addEventListener('mousedown', (e) => {
        if (esCelularPaco() && document.activeElement === this.input) e.preventDefault();
      });

      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay && !this._esPagina) this.close();
      });

      // Cart toggle
      this.cartToggleBtn.addEventListener('click', () => this._toggleCart());

      // Tabs (mobile)
      Object.entries(this.tabBtns).forEach(([id, { btn }]) => {
        btn.addEventListener('click', () => this._switchTab(id));
      });
      this._attachDeslizar();

      // Delegación de eventos en la lista de productos:
      //  - botón "Agregar" → _addToCart con qty del stepper
      //  - botones +/− del qty stepper → ajustan input
      //  - cambio de variant select → re-evalúa price/SKU/disponibilidad
      this.productsList.addEventListener('click', (e) => {
        const stepBtn = e.target.closest('[data-pp-qty-step]');
        if (stepBtn) {
          e.preventDefault();
          this._handleQtyStep(stepBtn);
          return;
        }
        const addBtn = e.target.closest('[data-pp-add-variant]');
        if (addBtn && !addBtn.disabled) {
          e.preventDefault();
          this._handleAddClick(addBtn);
          return;
        }
        // Tap en el badge idle (mobile rappi-style) → reactiva stepper.
        const badge = e.target.closest('[data-pp-qty-badge]');
        if (badge) {
          e.preventDefault();
          const card = badge.closest('.pp-assistant__product-card');
          if (card) this._activateCardQtyControl(card);
          return;
        }
        // Tap en cualquier otra parte del card (foto/título/info) en mobile
        // → abre el modal de detalle.
        if (window.matchMedia('(max-width: 749px)').matches) {
          const card = e.target.closest('.pp-assistant__product-card');
          if (card) {
            e.preventDefault();
            const productId = card.getAttribute('data-pp-product-id');
            const product = this.recommendedProducts.get(productId);
            if (product) this._openProductModal(product);
          }
        }
      });

      this.productsList.addEventListener('change', (e) => {
        const select = e.target.closest('[data-pp-variant-select]');
        if (select) this._handleVariantChange(select);
      });

      this.productsList.addEventListener('input', (e) => {
        const qtyInput = e.target.closest('[data-pp-qty-input]');
        if (qtyInput) {
          this._clampQtyInput(qtyInput);
          const card = qtyInput.closest('.pp-assistant__product-card');
          if (card) this._updateCardPriceDisplay(card);
        }
      });

      // Delegación cart: solo botón remove (X). El stepper inline se
      // removió — era lento (cada cambio = fetch /cart/change.js) y la
      // edición de cantidades vive en /cart vía "Ver carrito completo".
      this.cartExpandedBody.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-pp-cart-remove]');
        if (removeBtn) {
          e.preventDefault();
          this._handleCartRemove(removeBtn);
          return;
        }
        const retryBtn = e.target.closest('[data-pp-cart-retry]');
        if (retryBtn) {
          e.preventDefault();
          this._loadCart();
        }
      });

      // Retry de mensaje fallido (botón en bubble de error)
      this.messagesEl.addEventListener('click', (e) => {
        const retry = e.target.closest('[data-pp-msg-retry]');
        if (retry && this.lastFailedMessage) {
          e.preventDefault();
          const text = this.lastFailedMessage;
          this.lastFailedMessage = null;
          // Borra el mensaje de error visual antes de re-enviar
          const errWrap = retry.closest('.pp-assistant__msg');
          if (errWrap) errWrap.remove();
          this._sendMessage(text);
        }
      });

      // Link "Hablar con asesor" — siempre visible. Por ahora abre el
      // bubble placeholder con WhatsApp/correo. Sprint 2 lo convierte
      // en handoff real al CRM.
      this.humanHandoffLink.addEventListener('click', (e) => {
        e.preventDefault();
        this._handleHumanHandoff();
      });

      // Lead capture: dismiss (✕/"Después") queda delegado en this.leadCard
      // (ver _buildLeadModal) — funciona igual dentro del overlay de
      // escritorio que dentro del chat en la tarjeta compacta de celular.
      this.leadForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this._submitLead();
      });
      this._keyHandler = (e) => {
        if (e.key === 'Escape' && this.isOpen && !this._esPagina) this.close();
      };
      document.addEventListener('keydown', this._keyHandler);

      // FAQ chips — delegación en el wrapper (faqChipsEl). Listener separado
      // del de productos/cart para no enredar handlers.
      if (this.faqChipsEl) {
        this.faqChipsEl.addEventListener('click', (e) => {
          const chip = e.target.closest('[data-pp-faq-topic], [data-pp-faq-action]');
          if (!chip || chip.hasAttribute('disabled')) return;
          e.preventDefault();
          this._handleFaqChipClick(chip);
        });
      }

      this._onExternalOpen = (e) => {
        const detail = e.detail || {};
        this.open();
        if (detail.message) {
          this.input.value = String(detail.message);
          this.sendBtn.disabled = false;
          this._autosize();
          setTimeout(() => {
            if (this.input.value.trim()) {
              this.form.requestSubmit();
            }
          }, 100);
        }
      };
      document.addEventListener('pp-assistant:open', this._onExternalOpen);
    }

    _autosize() {
      this.input.style.height = 'auto';
      this.input.style.height = Math.min(this.input.scrollHeight, 200) + 'px';
    }

    open() {
      if (this.isOpen) return;
      // Sin su CSS el panel NO se pinta: se espera (y se pide lo que falte).
      // Si no llega en 8 s, no se abre — mejor el botón que un panel crudo.
      if (!this._estilosListos()) {
        if (this._esperandoEstilos) return;
        this._esperandoEstilos = true;
        this._conEstilos().then((ok) => { this._esperandoEstilos = false; if (ok) this.open(); });
        return;
      }
      // Se recuerda quién tenía el foco para devolvérselo al cerrar.
      this.lastFocus = document.activeElement;
      this.isOpen = true;
      this.overlay.removeAttribute('hidden');
      document.body.classList.add('pp-assistant-open');
      // Apaga el badge de "tienes mensaje nuevo" cuando el cliente abre
      // el panel (Sprint 2L).
      if (this.fab) this.fab.classList.remove('pp-assistant__fab--has-new');
      marcarChatNuevo(false);
      // En mobile siempre arrancamos en tab Chat (independiente del último
      // estado) — el foco debe ir al input.
      this._switchTab('chat', { silent: true });
      // En celular NO se enfoca el campo al abrir: el teclado salía de
      // inmediato y tapaba la bienvenida y sus ejemplos (QA móvil, 4-oct).
      // El cliente toca el campo cuando quiere escribir. Escritorio igual.
      if (!esCelularPaco()) {
        requestAnimationFrame(() => {
          setTimeout(() => this.input.focus(), 100);
        });
      } else if (this.closeBtn && typeof this.closeBtn.focus === 'function') {
        // El foco entra al panel (lector de pantalla) sin abrir el teclado.
        try { this.closeBtn.focus({ preventScroll: true }); } catch (e) { /* nada */ }
      }
      this._armarAtrasMovil();
      this._seguirTeclado(true);
    }

    /**
     * Celular: una entrada en el historial mientras Paco está abierto, para
     * que «atrás» deshaga un paso dentro de Paco (pasoAtrasMovil) en vez de
     * sacar al cliente de la página. Escritorio y /pages/paco no la usan.
     */
    _armarAtrasMovil() {
      if (this._esPagina || !esCelularPaco() || this._conEntradaAtras) return;
      if (this._huerfanaAtras) { this._huerfanaAtras = false; this._conEntradaAtras = true; return; }
      try {
        const st = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
        window.history.pushState(Object.assign({}, st, { ppPaco: true }), '');
        this._conEntradaAtras = true;
      } catch (e) { /* sin historial: el botón ✕ sigue cerrando */ }
    }

    _alAtrasMovil() {
      // El «atrás» que provoca el propio close() (consume la entrada): nada.
      if (this._ignorarPopAtras) { this._ignorarPopAtras = false; return; }
      // Salió de la entrada huérfana con Paco cerrado: ese «atrás» no movió
      // nada a la vista, así que se da el que el cliente quería.
      if (this._huerfanaAtras && !this.isOpen) {
        this._huerfanaAtras = false;
        try { window.history.back(); } catch (e) { /* nada */ }
        return;
      }
      if (!this._conEntradaAtras) return;
      this._conEntradaAtras = false;
      if (!this.isOpen) return;
      let capa = false;
      try {
        capa = Boolean(window.PPPaco3 && typeof window.PPPaco3.atrasCelular === 'function' && window.PPPaco3.atrasCelular());
      } catch (e) { capa = false; }
      const paso = pasoAtrasMovil({
        capa,
        modalProducto: Boolean(this.productModal && !this.productModal.hidden),
        tab: this.activeMobileTab,
      });
      if (paso === 'cerrar') { this.close({ desdeAtras: true }); return; }
      if (paso === 'modal') this._closeProductModal();
      if (paso === 'chat') this._switchTab('chat', { silent: true });
      this._armarAtrasMovil();
    }

    /**
     * Celular: mientras Paco está abierto el overlay ES lo que se ve
     * (seguirVistaCelular sobre el visualViewport), la página de atrás no
     * se mueve (bloquearFondoCelular) y con el campo enfocado entra el modo
     * escribiendo (sólo conversación + campo; el chat baja al último
     * mensaje). Escritorio y /pages/paco no pasan por aquí.
     */
    _seguirTeclado(prender) {
      if (!prender) {
        if (this._vistaCelular) { this._vistaCelular.apagar(); this._vistaCelular = null; }
        if (this._soltarFondo) { const s = this._soltarFondo; this._soltarFondo = null; s(); }
        return;
      }
      if (this._esPagina || this._vistaCelular || !esCelularPaco() || !this.overlay) return;
      this._soltarFondo = bloquearFondoCelular(window);
      this._vistaCelular = seguirVistaCelular(window, this.overlay, {
        campo: () => this.input,
        esCelular: esCelularPaco,
        alEscribir: (si) => {
          if (si && this.activeMobileTab === 'chat' && typeof this._scrollToBottom === 'function') {
            requestAnimationFrame(() => this._scrollToBottom());
          }
        },
      });
    }

    /**
     * Deslizar con el dedo sobre el contenido cambia de pestaña (celular).
     * Pasivo: nunca frena el scroll vertical ni el de un carrusel; sólo al
     * soltar decide (tabTrasDeslizar). Fuera si hay una hoja o la ficha de
     * antes abierta encima, si son dos dedos o si empezó en algo que se
     * desliza de lado (tocaAlgoQueSeDesliza).
     */
    _attachDeslizar() {
      const body = this.querySelector('.pp-assistant__body');
      if (!body || !window.matchMedia) return;
      let ini = null;
      body.addEventListener('touchstart', (e) => {
        ini = null;
        if (!window.matchMedia('(max-width: 999px)').matches) return;
        if (!e.touches || e.touches.length !== 1) return;
        if (this.querySelector('.pp3-hoja:not([hidden])')) return;
        if (this.productModal && !this.productModal.hidden) return;
        if (tocaAlgoQueSeDesliza(e.target, body, (n) => window.getComputedStyle(n))) return;
        const t = e.touches[0];
        ini = { x: t.clientX, y: t.clientY, t: Date.now() };
      }, { passive: true });
      body.addEventListener('touchmove', (e) => {
        if (ini && e.touches && e.touches.length > 1) ini = null;
      }, { passive: true });
      body.addEventListener('touchend', (e) => {
        if (!ini) return;
        const t = e.changedTouches && e.changedTouches[0];
        const desde = ini;
        ini = null;
        if (!t) return;
        const nueva = tabTrasDeslizar(this.activeMobileTab, t.clientX - desde.x, t.clientY - desde.y, Date.now() - desde.t);
        if (!nueva) return;
        // En la ficha (pestaña Productos), deslizar a la derecha REGRESA a
        // los resultados, como el «atrás» de cualquier app; ya en la rejilla,
        // el mismo gesto lleva a Chat.
        if (t.clientX - desde.x > 0 && this.activeMobileTab === 'products' && window.PPPaco3 &&
            typeof window.PPPaco3.volverEnProductos === 'function' && window.PPPaco3.volverEnProductos()) return;
        // silent: llegar al Chat deslizando no abre el teclado.
        this._switchTab(nueva, { silent: true, deslizar: t.clientX - desde.x < 0 ? 'izquierda' : 'derecha' });
      }, { passive: true });
      body.addEventListener('touchcancel', () => { ini = null; }, { passive: true });
    }

    /**
     * Cambia la tab activa en mobile. En desktop no hace nada visual (las
     * tabs van display:none por CSS) pero igual mantenemos el estado.
     */
    _switchTab(id, opts) {
      if (!this.tabBtns[id]) return;
      this.activeMobileTab = id;
      Object.entries(this.tabBtns).forEach(([tid, { btn }]) => {
        const active = tid === id;
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
        btn.classList.toggle('pp-assistant__tab--active', active);
      });
      // Aplica data attr al panel para que CSS muestre/oculte
      const body = this.querySelector('.pp-assistant__body');
      if (body) body.setAttribute('data-pp-active-tab', id);
      // Al deslizar, la pestaña nueva entra del lado del dedo (CSS; nada
      // con prefers-reduced-motion).
      if (body) {
        body.removeAttribute('data-pp-deslizar');
        if (opts && opts.deslizar) { void body.offsetWidth; body.setAttribute('data-pp-deslizar', opts.deslizar); }
      }
      // Quien siga las pestañas (la v3 relee el carrito al llegar a
      // «Carrito» en celular) se entera aquí.
      try { this.dispatchEvent(new CustomEvent('pp-assistant:tab', { detail: { id } })); } catch (e) { /* sin CustomEvent */ }
      // products + cart comparten el contentCol — basta con que esté visible
      // si la tab es products o cart.
      if (!opts || !opts.silent) {
        // Mover foco al primer elemento focuseable del panel para a11y
        // En celular tocar «Chat» no abre el teclado (tapaba la plática).
        if (id === 'chat' && !esCelularPaco()) {
          setTimeout(() => this.input && this.input.focus(), 50);
        }
      }
    }

    /**
     * Actualiza badges numéricos en las tabs (productos recomendados, items
     * en carrito) para que el user vea cuando hay algo nuevo sin tener que
     * cambiar de tab.
     */
    _updateTabBadges() {
      // 4-oct-2026: con la v3 en celular, la pestaña Productos enseña el kit
      // de la búsqueda a la vista; pp-paco3.js deja aquí cuántos son (null:
      // aún no hay búsqueda y se cuentan los recomendados de siempre).
      const productsCount = typeof this.productsBadgeCount === 'number'
        ? this.productsBadgeCount
        : this.recommendedProducts.size;
      const cartCount =
        this.cartData && this.cartData.item_count ? this.cartData.item_count : 0;
      const setBadge = (key, n) => {
        const ref = this.tabBtns[key];
        if (!ref) return;
        if (n > 0) {
          ref.badge.textContent = String(n);
          ref.badge.removeAttribute('hidden');
        } else {
          ref.badge.setAttribute('hidden', '');
        }
      };
      setBadge('products', productsCount);
      setBadge('cart', cartCount);
    }

    close(opts) {
      if (!this.isOpen) return;
      // En /pages/paco cerrar dejaría la página vacía: la X lleva al inicio.
      if (this._esPagina) { window.location.assign('/'); return; }
      this.isOpen = false;
      this._seguirTeclado(false);
      // Celular: cerrar con ✕ (o fondo/Esc) consume la entrada que puso
      // _armarAtrasMovil, para que el siguiente «atrás» sí sea de la tienda.
      if (this._conEntradaAtras && !(opts && opts.desdeAtras)) {
        this._conEntradaAtras = false;
        this._ignorarPopAtras = true;
        try { window.history.back(); } catch (e) { this._ignorarPopAtras = false; }
      }
      /*
       * DEVOLVER EL FOCO AL CERRAR.
       *
       * Este panel declara `aria-modal`, así que mientras está abierto la ayuda
       * técnica trata todo lo de atrás como oculto. Al cerrar sin devolver el
       * foco, éste cae a <body> y la tabulación reinicia desde el logo: quien
       * navega con teclado pierde su lugar a media navegación de catálogo.
       *
       * Aquí el destino natural es el botón flotante, que es de donde se abrió.
       * `preventScroll` para que devolver el foco no brinque la página.
       */
      var destino = (this.lastFocus && typeof this.lastFocus.focus === 'function')
        ? this.lastFocus
        : this.fab;
      if (destino && typeof destino.focus === 'function') {
        try { destino.focus({ preventScroll: true }); } catch (e) { /* nodo ya no existe */ }
      }
      this.lastFocus = null;

      this.overlay.setAttribute('hidden', '');
      document.body.classList.remove('pp-assistant-open');
      if (this.currentAbortController) {
        this.currentAbortController.abort();
      }
    }

    _reset() {
      if (this.isStreaming) return;
      if (!confirm('¿Empezar una conversación nueva? Lo de ahora se descarta.')) {
        return;
      }
      // Sprint 2AC: si la conv anterior tuvo handoff, restaurar el
      // header de Paco para que el cliente nuevo vea el branding bot.
      this._stopAgentPolling();
      this.botActive = true;
      this.assignedVendorName = null;
      this.lastAgentSyncAt = null;
      this.renderedAgentMsgIds.clear();
      this.postHandoffInfoShown = false;
      this._resetHeader();
      clearConversation();
      while (this.messagesEl.firstChild) {
        this.messagesEl.removeChild(this.messagesEl.firstChild);
      }
      this.messagesEl.appendChild(
        el('div', {
          cls: 'pp-assistant__msg pp-assistant__msg--assistant',
          children: [
            el('div', {
              cls: 'pp-assistant__msg-bubble',
              text: this.greetingText,
            }),
          ],
        })
      );
      // Limpia productos recomendados (el carrito persiste — es del cliente)
      this.recommendedProducts.clear();
      try { localStorage.removeItem(STORAGE_KEY_RECOMMENDED); } catch {}
      while (this.productsList.firstChild) {
        this.productsList.removeChild(this.productsList.firstChild);
      }
      this.productsEmpty.removeAttribute('hidden');
      this.input.value = '';
      this.sendBtn.disabled = true;
      // El confirm de arriba promete "lo de ahora se descarta" — eso incluye
      // la foto adjuntada y sin mandar. Si no se limpia aquí, sobrevive al
      // reset y se sube con el primer mensaje de la conversación nueva.
      this.fotoPendiente = null;
      this._pintarFotoPrevia();
      // FAQ chips reaparecen al reset — conversación nueva, queries
      // genéricas vuelven a ser una shortcut útil. Y des-deshabilitamos
      // cada chip por si quedó disabled de un fetch previo.
      if (this.faqChipsEl) {
        this.faqChipsEl.removeAttribute('hidden');
        this.faqChipsEl
          .querySelectorAll('.pp-assistant__faq-chip[disabled]')
          .forEach((b) => b.removeAttribute('disabled'));
      }
      this.input.focus();
      // Si en localStorage había un conv_id, ya quedó borrado por
      // clearConversation() arriba. El backend mantiene la conversación
      // anterior (histórico para analytics) pero ya no se referencia.

      // Hallazgo final #2: "Nueva conversación" sólo limpiaba este lado
      // (burbujas, productos recomendados). El panel de la v3 (rejilla,
      // chips) seguía mostrando la conversación descartada hasta el
      // siguiente 'ready' — clickeable, con datos de una conv que ya no
      // existe. Al final, cuando este lado ya quedó limpio, avisa 'reset'.
      // Inerte con v3 apagado: nadie lo escucha.
      this.dispatchEvent(new CustomEvent('pp-assistant:sse', { detail: { event: 'reset', data: null, bubble: null } }));
    }

    async _sendMessage(text) {
      // Capturado ANTES de mandar: this.fotoPendiente se limpia más abajo
      // en cuanto arranca la subida (línea con `this.fotoPendiente = null`
      // tras el fetch a /api/upload) — sin esta referencia local, la
      // burbuja del cliente se quedaría sin miniatura (hallazgo I3).
      const fotoDelTurno = this.fotoPendiente;
      // Turno nuevo: ningún 'estado' ha llegado todavía — 'tool_use' puede
      // volver a pintar su propio texto hasta que el backend mande uno.
      this.estadoRecibidoEsteTurno = false;
      this._appendMessage('user', text, { fotoFile: fotoDelTurno });
      this.input.value = '';
      this._autosize();
      // Cuenta turnos del usuario para el trigger de lead capture
      this.userTurnCount++;
      this.sendBtn.disabled = true;
      this.isStreaming = true;
      this.input.setAttribute('disabled', '');

      const assistantBubble = this._appendMessage('assistant', '', { typing: true });
      const bubbleText = assistantBubble.querySelector('.pp-assistant__msg-bubble');

      const sessionId = getSessionId();
      const conversationId = getConversationId();
      // Fase 2b DT-003: identidad anónima del visitante, reutilizada del
      // chat-widget (una sola identidad). Se manda a /api/chat → el backend
      // la persiste en chat_conversations.auth_user_id. Null-safe: si no se
      // pudo resolver, va undefined y la conversación queda anónima (igual
      // que antes de 2b).
      const authUserId = await ensureVisitorAuthUserId();
      this.currentAbortController = new AbortController();
      const url = this.backendUrl.replace(/\/$/, '') + '/api/chat';

      const headers = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      };
      if (this.testSecret) {
        headers['x-assistant-test-secret'] = this.testSecret;
      }

      // La foto se sube ANTES y sólo viaja su ruta. Si falla, el mensaje se
      // manda igual como texto: el cliente no vuelve a escribir lo que ya
      // escribió.
      let fotos = [];
      if (this.fotoPendiente) {
        try {
          const fd = new FormData();
          fd.append('session_id', sessionId);
          fd.append('foto', this.fotoPendiente, 'foto.jpg');
          // Hallazgo 2 (re-revisión 28-ago-2026): era la única de las
          // llamadas del widget al backend sin el test-secret. En
          // producción por App Proxy da igual (Shopify firma), pero en
          // dev/preview/staging sin proxy /api/upload ahora exige
          // checkAuth (hallazgo C2) y responde 401 sin este header —
          // mismo patrón que las otras seis llamadas de este archivo.
          const uploadHeaders = {};
          if (this.testSecret) {
            uploadHeaders['x-assistant-test-secret'] = this.testSecret;
          }
          const r = await fetch(this.backendUrl.replace(/\/$/, '') + '/api/upload', {
            method: 'POST',
            headers: uploadHeaders,
            body: fd,
          });
          const j = await r.json();
          if (j && j.ok) {
            fotos = [{ ruta: j.ruta, mime: j.mime }].slice(0, MAX_FOTOS_POR_MENSAJE);
            // Hallazgo 3 (re-revisión 28-ago-2026): "pero mandé tu mensaje"
            // sólo es cierto si HABÍA texto — un turno sólo-foto (posible
            // desde C4) no manda ningún mensaje si la subida falla. Este
            // toast es el único que se dispara cuando la subida SÍ falló;
            // el `if (text)` evita la mentira sin cambiar nada del caso
            // con texto (que sigue avisando igual que antes).
          } else if (text) {
            this._showToast('No pude subir la foto, pero mandé tu mensaje.', { error: true });
          }
        } catch {
          // El texto sigue de todos modos aunque la subida falle — igual se
          // manda como mensaje normal, sin foto. Mismo `if (text)` que
          // arriba: sin texto, "mandé tu mensaje" sería falso.
          if (text) {
            this._showToast('No pude subir la foto, pero mandé tu mensaje.', { error: true });
          }
        }
        this.fotoPendiente = null;
        this._pintarFotoPrevia();
      }

      // El carrito que viaja con el mensaje es el REAL de este instante: si
      // Paco lo movió, se espera a que termine y se vuelve a leer. Así,
      // cuando el cliente escribe «no están las cajas», el servidor compara
      // contra lo que de verdad hay y no contra una lectura vieja.
      await this._carritoAlDia();

      try {
        const res = await fetch(url, {
          method: 'POST',
          headers,
          // same-origin, no omit (30-sep-2026): el App Proxy sabe quién está
          // logueado por las cookies de la tienda, y sin ellas no firma
          // `logged_in_customer_id`. El omit venía de cuando se le hablaba
          // directo a Vercel desde otro dominio. Shopify NO le reenvía las
          // cookies al servidor de Paco. Ver tests/paco-chat-manda-la-sesion.
          credentials: 'same-origin',
          signal: this.currentAbortController.signal,
          body: JSON.stringify({
            session_id: sessionId,
            conversation_id: conversationId || undefined,
            // Modo prueba (ver STORAGE_KEY_PRUEBA): undefined si no aplica.
            prueba: banderaPrueba(),
            cotiza: cotizaDePrueba(),
            auth_user_id: authUserId || undefined,
            // F3 (presencia /chats): visitor_key del tracking standalone.
            // El backend (pp-assistant lib/crm) hace el claim non-blocking
            // en chat_visitors.client_visitor_key del CRM Main. Leida fresh
            // en cada send: si el tracking aun no corre (load+idle), el
            // siguiente mensaje la lleva.
            visitor_key: window._ppVisitorKey || undefined,
            message: text,
            // Sólo la ruta que devolvió /api/upload — nunca la foto en sí.
            fotos,
            // Snapshot del cart actual para que Paco pueda referenciarlo.
            cart: this._buildCartSnapshot(),
            // Este motor manda el acuse de cada add_to_cart (/api/cart-ack):
            // con esto el servidor lo espera antes de decir «ya quedó».
            cart_ack: true,
            // Origen del visitante (primer touch de la sesión). El backend
            // la persiste set-if-null en la conversación; mandarla en cada
            // turno cubre al que abre el chat varios turnos después de
            // aterrizar. undefined ⇒ JSON.stringify la omite.
            atribucion: getAtribucion(),
            // Dónde está parado el cliente dentro del área de cliente. Se
            // omite fuera del área para no mandar ruido.
            page: this.pageKey
              ? { key: this.pageKey, path: this.pagePath, handle: this.pageHandle }
              : undefined,
            // Customer logueado — Paco lo saluda por nombre, recuerda
            // entre sesiones, y persistimos customer_id en conversations
            // para Phase 3 (cross-DB con PP Main).
            customer: this.customerId
              ? {
                  logged_in: true,
                  customer_id: this.customerId,
                  first_name: this.customerFirstName || undefined,
                  email: this.customerEmail || undefined,
                }
              : { logged_in: false },
          }),
        });

        if (!res.ok) {
          const errText = await res.text().catch(() => '');
          throw new Error(`HTTP ${res.status}: ${errText.slice(0, 200)}`);
        }
        if (!res.body) {
          throw new Error('no response body');
        }

        bubbleText.textContent = '';
        bubbleText.classList.remove('pp-assistant__msg-bubble--typing');

        await this._consumeSse(res.body, bubbleText);
      } catch (err) {
        if (err && err.name === 'AbortError') {
          bubbleText.textContent = '(Cancelado)';
        } else {
          bubbleText.classList.add('pp-assistant__msg-bubble--error');
          while (bubbleText.firstChild) bubbleText.removeChild(bubbleText.firstChild);
          bubbleText.appendChild(
            document.createTextNode(
              'No pude conectarme. Revisa tu conexión y vuelve a intentar.'
            )
          );
          // Guarda el mensaje del user para el botón de retry
          this.lastFailedMessage = text;
          const retryBtn = el('button', {
            cls: 'pp-assistant__retry-btn',
            attrs: {
              type: 'button',
              'data-pp-msg-retry': true,
            },
            text: 'Reintentar',
          });
          bubbleText.appendChild(retryBtn);
          // Anuncia el error al screen reader
          this._announce(
            'Error de conexión. Hay un botón Reintentar disponible.'
          );
          console.error('[pp-assistant] error:', err);
        }
      } finally {
        this.isStreaming = false;
        this.input.removeAttribute('disabled');
        this.currentAbortController = null;
        // Fin de stream (incluye abort/close a medio turno, igual que el
        // aviso 'fin' de abajo): si quedó un aviso de progreso pegado
        // ('estado' o _toolStatus), que no sobreviva al turno.
        this._setStatus('');
        this._scrollToBottom();
        // Solo anunciamos la respuesta completa al final (no en cada delta)
        // para no spamear el screen reader.
        if (bubbleText && bubbleText._rawText) {
          this._announce(bubbleText._rawText);
        }
        this.input.focus();
        // Hallazgo final #1: el finally SIEMPRE corre — abort por close(),
        // fetch roto, HTTP no-ok o corte de stream sin 'done' — a diferencia
        // de _handleSseBlock, que sólo corre si el stream llegó a emitir
        // bloques. v3 pintaba 8 skeletons y se quedaban ahí para siempre si
        // el usuario cerraba Paco a medio stream (sin 'done'/'error'). Este
        // aviso 'fin' es inerte con v3 apagado: nadie lo escucha.
        this.dispatchEvent(new CustomEvent('pp-assistant:sse', { detail: { event: 'fin', data: null, bubble: null } }));
      }
    }

    /**
     * Anuncia texto al screen reader vía la live region. Limpia primero,
     * espera un tick, y vuelve a setear para forzar re-anuncio cuando el
     * texto se repite.
     */
    _announce(text) {
      if (!this.liveRegion || !text) return;
      this.liveRegion.textContent = '';
      // Pequeño defer para que AT detecte el cambio
      setTimeout(() => {
        this.liveRegion.textContent = String(text).slice(0, 500);
      }, 50);
    }

    async _consumeSse(body, bubbleText) {
      const reader = body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let blockEnd;
        while ((blockEnd = buffer.indexOf('\n\n')) !== -1) {
          const block = buffer.slice(0, blockEnd);
          buffer = buffer.slice(blockEnd + 2);
          this._handleSseBlock(block, bubbleText);
        }
      }
    }

    _handleSseBlock(block, bubbleText) {
      let eventName = 'message';
      let dataStr = '';
      for (const line of block.split('\n')) {
        if (line.startsWith('event:')) {
          eventName = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          dataStr += line.slice(5).trim();
        }
      }
      if (!dataStr) return;
      let data;
      try {
        data = JSON.parse(dataStr);
      } catch {
        return;
      }

      switch (eventName) {
        case 'ready':
          if (data.conversation_id) setConversationId(data.conversation_id);
          break;
        case 'estado':
          // Backend (Fase avisos de progreso): frases como «Buscando en el
          // catálogo…» que llegan ANTES de la respuesta, mientras el modelo
          // piensa o usa herramientas. Tienen prioridad sobre el mapeo local
          // _toolStatus — de ahí la bandera, para que 'tool_use' no la pise
          // y el texto no parpadee entre los dos.
          if (typeof data.texto === 'string') {
            this.estadoRecibidoEsteTurno = true;
            this._setStatus(data.texto);
          }
          break;
        case 'text_delta':
          // Primer texto de la respuesta: lo que fuera que decía la línea de
          // estado (un 'estado' del backend o un _toolStatus local) ya no
          // aplica.
          this._setStatus('');
          if (typeof data.text === 'string') {
            // Acumula el raw text en un buffer y re-renderiza con markdown
            // en cada delta. Necesario porque **bold** puede llegar partido
            // entre 2+ deltas — tener el raw acumulado garantiza que el
            // ** de cierre cuando llegue, se asocie correctamente con el
            // ** de apertura previo.
            bubbleText._rawText = (bubbleText._rawText || '') + data.text;
            while (bubbleText.firstChild) {
              bubbleText.removeChild(bubbleText.firstChild);
            }
            bubbleText.appendChild(renderMarkdown(bubbleText._rawText));
            this._scrollToBottom();
          }
          break;
        case 'tool_use':
          // Un 'estado' de este turno manda sobre _toolStatus (sin parpadeo).
          if (!this.estadoRecibidoEsteTurno) this._setStatus(this._toolStatus(data.name));
          if (TOOLS_INTENCION_COMERCIAL.has(data.name)) this.senalComercial = true;
          if (TOOLS_CONTACTO.has(data.name)) this.contactoEnElChat = true;
          break;
        case 'tool_result':
          this._setStatus('');
          break;
        case 'done':
          this._setStatus('');
          // Sprint 2AD: si el backend cortó porque bot_active=false, el
          // bubble de Paco quedó vacío con typing indicator. Reemplazar
          // por mensaje informativo UNA vez (no spammear).
          if (data && data.bot_inactive === true) {
            const vendorName = this.assignedVendorName || 'el asesor';
            if (!this.postHandoffInfoShown) {
              this.postHandoffInfoShown = true;
              if (bubbleText) {
                bubbleText.classList.remove('pp-assistant__msg-bubble--typing');
                while (bubbleText.firstChild) bubbleText.removeChild(bubbleText.firstChild);
                bubbleText.textContent = vendorName + ' recibió tu mensaje. Te responde aquí en un momento.';
              }
            } else if (assistantBubble && assistantBubble.parentNode) {
              // Mensajes posteriores: simplemente quitar el bubble vacío.
              // El cliente ya sabe que sus mensajes llegan.
              assistantBubble.parentNode.removeChild(assistantBubble);
            }
            break;
          }
          // Después de cada respuesta de Paco, evalúa si toca lead capture
          // (solo dispara si cumple todos los triggers: anónimo + 3+ turnos
          // + intent comercial + no dismissed/saved previo)
          this._maybeShowLeadCapture();
          break;
        case 'product_cards':
          if (Array.isArray(data.products)) {
            data.products.forEach((p) => this._addProductCard(p));
          }
          break;
        case 'volume_tier_info':
          // Por ahora solo log — Fase posterior puede meter un banner
          // "ahorras X% si llevas Y unidades" en el panel derecho.
          break;
        case 'panel':
          // El canvas (facturar, pedido, proveedor…) ya se usó en esta
          // page-load — ver canvasAbiertoEnConversacion / _maybeShowLeadCapture.
          this.canvasAbiertoEnConversacion = true;
          break;
        case 'client_action':
          // Paco emitió un add_to_cart. Backend ya validó allowlist;
          // nosotros ejecutamos contra /cart/add.js y refrescamos.
          this._handleClientAction(data);
          break;
        case 'error':
          this._setStatus('');
          bubbleText.classList.add('pp-assistant__msg-bubble--error');
          if (data.message) {
            bubbleText.textContent +=
              '\n\n[' + String(data.message).slice(0, 200) + ']';
          }
          break;
      }

      // Paco 3.0: la v3 se entera de cada evento DESPUÉS de que la v2 lo
      // manejó. La v2 no escucha esto; con la v3 apagada nadie lo oye.
      this.dispatchEvent(new CustomEvent('pp-assistant:sse', { detail: { event: eventName, data, bubble: bubbleText } }));
    }

    /**
     * Qué se le dice al cliente mientras Paco usa una herramienta.
     *
     * Importa más de lo que parece: Paco tarda 5 segundos de promedio y hasta
     * 12 en los peores casos, y casi todo ese tiempo se va ANTES de escribir
     * la primera palabra — buscando, cargando fichas, calculando. Sin esto el
     * cliente ve puntos suspensivos mudos y no sabe si lo están atendiendo o
     * si se trabó.
     *
     * De trece herramientas solo cuatro tenían texto; las demás caían en
     * "Trabajando…", incluidas las de los momentos que más importan: agregar
     * al carrito (la compra) y conectar con un asesor (la espera más larga).
     *
     * Se escriben en primera persona y en presente, como diría una persona
     * que está haciendo eso mismo del otro lado.
     */
    _toolStatus(name) {
      switch (name) {
        case 'search_products':
          return 'Buscando en el catálogo…';
        case 'get_product_details':
          return 'Viendo medidas y precios…';
        case 'get_volume_tiers':
          return 'Calculando tu descuento por volumen…';
        case 'get_company_info':
          return 'Consultando datos de la empresa…';
        case 'search_kb':
          return 'Revisando la información…';
        case 'add_to_cart':
          return 'Agregando a tu carrito…';
        case 'request_human_handoff':
          return 'Conectándote con un asesor…';
        case 'capture_visitor_info':
          return 'Guardando tus datos…';
        case 'note_for_vendor':
          return 'Anotando para el equipo…';
        case 'get_customer_profile':
          return 'Revisando tus compras anteriores…';
        case 'update_customer_profile':
          return 'Tomando nota…';
        case 'track_order':
          return 'Buscando tu pedido…';
        case 'format_quote':
          return 'Armando tu cotización…';
        default:
          // Red para una herramienta nueva que nadie recordó agregar aquí.
          return 'Un momento…';
      }
    }

    _setStatus(text) {
      let statusEl = this.messagesEl.querySelector('.pp-assistant__status');
      if (!text) {
        if (statusEl) statusEl.remove();
        return;
      }
      if (!statusEl) {
        statusEl = el('div', { cls: 'pp-assistant__status' });
        this.messagesEl.appendChild(statusEl);
      }
      statusEl.textContent = text;
      this._scrollToBottom();
    }

    _appendMessage(role, text, opts) {
      const bubble = el('div', { cls: 'pp-assistant__msg-bubble' });
      if (opts && opts.typing) {
        bubble.classList.add('pp-assistant__msg-bubble--typing');
        bubble.appendChild(el('span'));
        bubble.appendChild(el('span'));
        bubble.appendChild(el('span'));
      } else if (opts && opts.fotoFile) {
        // Hallazgo I3 (revisión final 27-ago-2026): el spec promete una
        // miniatura en la burbuja del cliente como entregable. Lo que había
        // era sólo la vista previa del compositor (this.fotoPrevia), que se
        // limpia al mandar — tras enviar, el cliente no veía ninguna
        // evidencia de que su foto viajó. La imagen va ANTES del texto,
        // mismo criterio que el backend (lib/fotos/bloques.ts, repo del
        // backend: "La imagen va ANTES del texto").
        const img = document.createElement('img');
        img.className = 'pp-assistant__msg-foto';
        img.src = URL.createObjectURL(opts.fotoFile);
        img.alt = 'Foto enviada';
        // Se libera al cargar, mismo patrón que _pintarFotoPrevia — si no,
        // el objectURL queda vivo toda la sesión.
        img.onload = () => URL.revokeObjectURL(img.src);
        bubble.appendChild(img);
        if (text) bubble.appendChild(document.createTextNode(String(text)));
      } else {
        bubble.textContent = String(text);
      }
      const wrap = el('div', {
        cls: 'pp-assistant__msg pp-assistant__msg--' + role,
        children: [bubble],
      });
      this.messagesEl.appendChild(wrap);
      // Auto-hide FAQ chips en cuanto haya un user message — mezclarlos
      // con la conversación real se ve raro. Reset los vuelve a mostrar.
      if (role === 'user' && this.faqChipsEl) {
        this.faqChipsEl.setAttribute('hidden', '');
      }
      this._scrollToBottom();
      return wrap;
    }

    _scrollToBottom() {
      this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
    }

    // ════════════════════════════════════════════════════════════
    // History rehydration — persiste entre páginas
    // ════════════════════════════════════════════════════════════

    async _restoreHistory() {
      const convId = getConversationId();
      if (!convId) return;
      const sessionId = getSessionId();

      const url =
        this.backendUrl.replace(/\/$/, '') +
        '/api/conversation/' +
        encodeURIComponent(convId) +
        '?session_id=' +
        encodeURIComponent(sessionId);

      const headers = { Accept: 'application/json' };
      if (this.testSecret) headers['x-assistant-test-secret'] = this.testSecret;

      let res;
      try {
        res = await fetch(url, {
          method: 'GET',
          headers,
          credentials: 'omit',
        });
      } catch (err) {
        console.warn('[pp-assistant] history fetch failed:', err);
        return;
      }

      if (res.status === 404) {
        // Conv expirada / inválida — limpia el localStorage y arranca fresco.
        clearConversation();
        return;
      }
      if (!res.ok) {
        console.warn('[pp-assistant] history HTTP', res.status);
        return;
      }

      let data;
      try {
        data = await res.json();
      } catch {
        return;
      }
      if (!data || !data.ok || !Array.isArray(data.messages) || data.messages.length === 0) {
        return;
      }

      // Reemplaza greeting con history rehidratado
      while (this.messagesEl.firstChild) {
        this.messagesEl.removeChild(this.messagesEl.firstChild);
      }

      data.messages.forEach((m) => {
        if (m.role !== 'user' && m.role !== 'assistant') return;
        if (!m.text) return;
        const bubble = el('div', { cls: 'pp-assistant__msg-bubble' });
        if (m.role === 'assistant') {
          bubble._rawText = m.text;
          bubble.appendChild(renderMarkdown(m.text));
        } else {
          bubble.textContent = m.text;
        }
        const wrap = el('div', {
          cls: 'pp-assistant__msg pp-assistant__msg--' + m.role,
          children: [bubble],
        });
        this.messagesEl.appendChild(wrap);
      });

      // Banner sutil "Continuamos la conversación"
      const banner = el('div', {
        cls: 'pp-assistant__history-banner',
        text: '↑ Conversación previa',
      });
      this.messagesEl.insertBefore(banner, this.messagesEl.firstChild);

      // Ocultar chips: hay conversación rehidratada, los chips iniciales
      // ya no aplican. El user puede usar el botón reset si los necesita.
      if (this.faqChipsEl) {
        this.faqChipsEl.setAttribute('hidden', '');
      }

      this._scrollToBottom();
    }

    // ════════════════════════════════════════════════════════════
    // Product cards — render canónico de productos vía SSE event
    // ════════════════════════════════════════════════════════════

    _addProductCard(product) {
      if (!product || !product.product_id) return;
      // Dedupe: si ya está en la grid, no la dupliques
      if (this.recommendedProducts.has(product.product_id)) {
        return;
      }
      this.recommendedProducts.set(product.product_id, product);
      this._persistRecommendedProducts();

      const card = this._renderProductCard(product);
      // Productos nuevos arriba (más visibles)
      this.productsList.insertBefore(card, this.productsList.firstChild);
      // Oculta el empty state cuando hay al menos 1 card
      this.productsEmpty.setAttribute('hidden', '');
      this._updateTabBadges();
      // Sync inmediato para reflejar qty en cart si el variant ya está ahí
      this._syncProductCardsWithCart();
      // Animación de "atención" en el tab Productos cuando llega una
      // recomendación nueva (solo si el user no está mirando ese tab).
      if (this.activeMobileTab !== 'products') {
        this._pulseTab('products');
      }
    }

    /**
     * Dispara una animación corta en el tab indicado para llamar la
     * atención del user — útil cuando llega contenido nuevo (recomendaciones
     * de Paco, items al carrito) y el tab no está activo.
     * Re-disparable: si llega otra recomendación durante la animación,
     * la reinicia.
     */
    _pulseTab(id) {
      const ref = this.tabBtns && this.tabBtns[id];
      if (!ref || !ref.btn) return;
      const btn = ref.btn;
      btn.classList.remove('pp-assistant__tab--pulse');
      // Force reflow para reiniciar la animación si ya estaba corriendo.
      // eslint-disable-next-line no-unused-expressions
      btn.offsetWidth;
      btn.classList.add('pp-assistant__tab--pulse');
      if (btn._pulseTimer) clearTimeout(btn._pulseTimer);
      btn._pulseTimer = setTimeout(() => {
        btn.classList.remove('pp-assistant__tab--pulse');
      }, 1400);
    }

    /**
     * Persiste recommendedProducts en localStorage para sobrevivir
     * navegaciones (el user reportó 2026-06-11 que al cambiar de página
     * desaparecían). Llamado después de cada _addProductCard y en clear.
     */
    _persistRecommendedProducts() {
      try {
        const arr = Array.from(this.recommendedProducts.values());
        localStorage.setItem(STORAGE_KEY_RECOMMENDED, JSON.stringify(arr));
      } catch (e) {
        // localStorage puede fallar (quota, private mode). Failure-tolerant:
        // los productos siguen visibles en esta sesión, solo no persisten.
        console.warn('[pp-assistant] persist recommended failed:', e);
      }
    }

    /**
     * Restaura recommendedProducts desde localStorage. Llamado al inicio
     * del custom element antes del _restoreHistory para que cuando el chat
     * rehidrate, los productos también estén en el panel derecho.
     */
    _restoreRecommendedProducts() {
      let arr;
      try {
        const raw = localStorage.getItem(STORAGE_KEY_RECOMMENDED);
        if (!raw) return;
        arr = JSON.parse(raw);
      } catch (e) {
        return;
      }
      if (!Array.isArray(arr) || arr.length === 0) return;

      // Re-renderizar usando _addProductCard SIN re-persistir cada uno
      // (ya están en storage). Reverse para mantener el orden visual
      // original (los más nuevos arriba — addProductCard usa insertBefore).
      for (let i = arr.length - 1; i >= 0; i--) {
        const product = arr[i];
        if (!product || !product.product_id) continue;
        if (this.recommendedProducts.has(product.product_id)) continue;
        this.recommendedProducts.set(product.product_id, product);
        const card = this._renderProductCard(product);
        this.productsList.insertBefore(card, this.productsList.firstChild);
      }
      if (this.recommendedProducts.size > 0 && this.productsEmpty) {
        this.productsEmpty.setAttribute('hidden', '');
      }
      this._updateTabBadges();
    }

    _renderProductCard(product) {
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const firstAvailable = variants.find((v) => v.available);
      const firstVariant = firstAvailable || variants[0] || null;
      const price = firstVariant ? firstVariant.price_mxn : product.price_min_mxn;
      const inStock = !!firstAvailable;
      const isPreVenta = product.tipo_venta === 'pre_venta';
      const hasMultipleVariants = variants.length > 1;

      // Image (Shopify URL transform: 240x para card)
      const img = el('img', {
        cls: 'pp-assistant__product-img',
        attrs: {
          src: imageUrl(product.image_url, 240),
          alt: truncate(product.title, 100),
          loading: 'lazy',
          decoding: 'async',
        },
      });

      const titleLink = el('a', {
        cls: 'pp-assistant__product-title',
        attrs: {
          href: product.url || '#',
          target: '_blank',
          rel: 'noopener',
        },
        text: truncate(product.title, 90),
      });

      // Specs row: NO dimensiones — el título de Shopify ya las trae
      // ("37 x 48 cm - Bolsas para Envíos Premium"). Antes inyectábamos
      // "37×48×48 cm" del backend (que para bolsas pone alto = ancho
      // como fallback dummy), lo que producía duplicación visual rara
      // tipo "37 x 48 cm ... 37×48×48 cm" y confundía al cliente.
      //
      // Aquí mostramos solo specs TÉCNICAS no presentes en el título:
      // flauta, calibre, material. Máximo 3 chips para no inflar el card.
      // Pedido por user 2026-06-11.
      const specs = product.specs || {};
      const specParts = [];
      // grosor_flauta es la llave viva en Shopify desde la migracion del
      // 2-sep-2026; tipo_de_flauta quedo vacia alla y su definicion sigue
      // existiendo solo por inercia. El indice de Paco (products_index.specs)
      // todavia sirve la llave VIEJA en 98 productos porque se construyo
      // antes de esa migracion, asi que hoy solo funciona la vieja y el dia
      // que el indice se reconstruya solo funcionara la nueva. Leemos las dos
      // para que el dato no se pierda en silencio en ninguno de los dos
      // momentos. Verificado el 7-sep-2026 contra Shopify y contra el indice.
      const flauta = specs.grosor_flauta || specs.tipo_de_flauta;
      if (flauta) {
        // Evita "Flauta Flauta Tipo B" si el valor ya empieza con "Flauta"
        const v = String(flauta);
        specParts.push(v.toLowerCase().startsWith('flauta') ? v : `Flauta ${v}`);
      }
      if (specs.calibre) specParts.push(`Cal. ${specs.calibre}`);
      if (specs.material && specParts.length < 2)
        specParts.push(String(specs.material));

      let specsRow = null;
      if (specParts.length > 0) {
        specsRow = el('div', {
          cls: 'pp-assistant__product-meta',
          text: specParts.slice(0, 3).join(' · '),
        });
      }

      // Pack info row: SKU + pack count + per-unit price — TODO en una
       // sola línea con separador "·". El per-unit no lleva " MXN"
       // porque el precio principal a la derecha ya lo indica.
      const pack = parsePack(
        firstVariant && firstVariant.title !== 'Default Title'
          ? firstVariant.title
          : specs.unidades
      );
      const packBaseParts = [];
      if (firstVariant && firstVariant.sku) {
        packBaseParts.push(`SKU ${firstVariant.sku}`);
      }
      if (pack && pack.count > 1) {
        packBaseParts.push(`${pack.count} ${pack.unitPlural}`);
        if (price && pack.count > 0) {
          const perUnit = price / pack.count;
          packBaseParts.push(
            `${fmtMoney(perUnit).replace(' MXN', '')} c/${pack.unit}`
          );
        }
      }
      let packRow = null;
      if (packBaseParts.length > 0) {
        packRow = el('div', {
          cls: 'pp-assistant__product-pack',
          text: packBaseParts.join(' · '),
        });
      }
      // perUnitRow eliminado — el per-unit ahora vive inline dentro de
      // packRow. Mantengo la var como null para que el children array
      // siga funcionando sin cambios estructurales.
      let perUnitRow = null;

      // Price line dinámica — al cambiar qty se recalcula como total
      // (qty × precio efectivo aplicando tier). Inicial: precio por 1 pack.
      const priceLine = el('div', {
        cls: 'pp-assistant__product-price',
        text: price != null ? fmtMoney(price) : '',
        attrs: { 'data-pp-price-display': true },
      });
      const priceBreakdown = el('div', {
        cls: 'pp-assistant__product-price-breakdown',
        attrs: { 'data-pp-price-breakdown': true, hidden: true },
      });

      // Best tier hint si hay tiers — destacado debajo del precio
      let tierHint = null;
      if (Array.isArray(product.volume_tiers) && product.volume_tiers.length > 0) {
        const best = product.volume_tiers[0];
        tierHint = el('div', {
          cls: 'pp-assistant__product-tier',
          children: [
            el('span', {
              cls: 'pp-assistant__product-tier-icon',
              text: '🏷️',
            }),
            document.createTextNode(
              ` ${best.min_qty}+ pzas: ${fmtMoney(best.price_mxn)} c/u`
            ),
          ],
        });
      }

      // Stock badge
      let stockBadge = null;
      if (isPreVenta) {
        stockBadge = el('span', {
          cls: 'pp-assistant__product-badge pp-assistant__product-badge--preventa',
          text: 'Pre-venta',
        });
      } else if (!inStock) {
        stockBadge = el('span', {
          cls: 'pp-assistant__product-badge pp-assistant__product-badge--out',
          text: 'Sin stock',
        });
      }

      // Variant picker — solo si hay >1 variant.
      // Default: primera variant disponible (no necesariamente la 0).
      let variantSelect = null;
      if (hasMultipleVariants) {
        variantSelect = el('select', {
          cls: 'pp-assistant__variant-select',
          attrs: {
            'data-pp-variant-select': true,
            'aria-label': 'Elige la presentación',
          },
        });
        variants.forEach((v) => {
          const labelParts = [];
          labelParts.push(v.title && v.title !== 'Default Title' ? v.title : 'Variante');
          if (v.price_mxn != null) labelParts.push(fmtMoney(v.price_mxn));
          if (!v.available) labelParts.push('Sin stock');
          const opt = el('option', {
            attrs: {
              value: String(v.variant_id),
              'data-price': v.price_mxn != null ? String(v.price_mxn) : '',
              'data-available': v.available ? 'true' : 'false',
              'data-sku': v.sku || '',
              'data-title': v.title || '',
              disabled: !v.available,
              selected: firstVariant && v.variant_id === firstVariant.variant_id,
            },
            text: labelParts.join(' — '),
          });
          variantSelect.appendChild(opt);
        });
      }

      // Qty stepper — − [n] +
      // Mínimo 1, máximo 99 (defensivo; el backend / Shopify validan stock).
      const qtyDecBtn = el('button', {
        cls: 'pp-assistant__qty-step pp-assistant__qty-step--minus',
        attrs: {
          type: 'button',
          'data-pp-qty-step': 'dec',
          'aria-label': 'Disminuir cantidad',
        },
        text: '−',
      });
      const qtyInput = el('input', {
        cls: 'pp-assistant__qty-input',
        attrs: {
          type: 'number',
          inputmode: 'numeric',
          min: '1',
          max: '99',
          value: '1',
          'data-pp-qty-input': true,
          'aria-label': 'Cantidad',
        },
      });
      const qtyIncBtn = el('button', {
        cls: 'pp-assistant__qty-step pp-assistant__qty-step--plus',
        attrs: {
          type: 'button',
          'data-pp-qty-step': 'inc',
          'aria-label': 'Aumentar cantidad',
        },
        text: '+',
      });
      const qtyStepper = el('div', {
        cls: 'pp-assistant__qty-stepper',
        attrs: { role: 'group', 'aria-label': 'Cantidad a agregar' },
        children: [qtyDecBtn, qtyInput, qtyIncBtn],
      });

      // Add to cart button (deshabilitado si no hay variante disponible)
      const addBtn = el('button', {
        cls: 'pp-assistant__product-add',
        attrs: {
          type: 'button',
          'data-pp-add-variant': firstVariant ? String(firstVariant.variant_id) : '',
          'data-pp-add-product': product.product_id,
          'data-pp-add-title': product.title,
          'aria-label': 'Agregar al carrito',
          disabled: !firstVariant || (!inStock && !isPreVenta),
        },
        children: [
          svgIcon('0 0 24 24', ICONS.plus),
          el('span', { text: 'Agregar' }),
        ],
      });

      // Badge mobile-only: cuando el card ya tiene qty en cart Y no hay
      // interacción reciente, sustituye al botón "+" mostrando el count.
      // Tap sobre el badge re-abre el stepper temporal.
      const qtyBadge = el('button', {
        cls: 'pp-assistant__qty-badge',
        attrs: {
          type: 'button',
          'data-pp-qty-badge': true,
          'aria-label': 'Cantidad en carrito (toca para ajustar)',
          hidden: true,
        },
        text: '0',
      });

      // Acciones a la derecha: precio + breakdown + variant + stepper + botón.
      // Si solo hay 1 variant, no metemos el select.
      const actionChildren = [priceLine, priceBreakdown];
      if (variantSelect) actionChildren.push(variantSelect);
      actionChildren.push(qtyStepper, addBtn, qtyBadge);
      const actions = el('div', {
        cls: 'pp-assistant__product-actions',
        children: actionChildren,
      });

      const info = el('div', {
        cls: 'pp-assistant__product-info',
        children: [
          titleLink,
          specsRow,
          packRow,
          perUnitRow,
          tierHint,
          stockBadge,
        ].filter(Boolean),
      });

      const card = el('div', {
        cls: 'pp-assistant__product-card',
        attrs: { 'data-pp-product-id': product.product_id },
        children: [img, info, actions],
      });

      // Guarda datos para el cálculo dinámico de precio al cambiar qty.
      // No los pongo en data-attrs (sensibles a innerHTML quoting); los
      // guardo en _pricingData como property del elemento.
      card._pricingData = {
        packPrice: price != null ? Number(price) : null,
        tiers: Array.isArray(product.volume_tiers) ? product.volume_tiers : [],
        pack,
      };

      return card;
    }

    /**
     * Recalcula y actualiza el precio mostrado en una card basándose en
     * la qty actual del stepper, aplicando volume_tiers si corresponde.
     *
     * - qty = 1: muestra el precio por pack como base
     * - qty > 1: muestra el TOTAL (qty × precio efectivo)
     * - Si la qty cruza un tier threshold, aplica el tier price
     * - Subtítulo breakdown: "5 × $620 c/u (ahorras 10%)"
     */
    _updateCardPriceDisplay(card) {
      if (!card || !card._pricingData) return;
      const { packPrice, tiers } = card._pricingData;
      if (packPrice == null) return;

      const qtyInput = card.querySelector('[data-pp-qty-input]');
      const qty = qtyInput
        ? Math.max(1, Math.min(99, parseInt(qtyInput.value, 10) || 1))
        : 1;

      // Tier aplicable: el de mayor min_qty cuyo umbral <= qty.
      let effectiveUnit = packPrice;
      let tierApplied = null;
      for (const t of tiers) {
        if (qty >= t.min_qty) {
          effectiveUnit = Number(t.price_mxn);
          tierApplied = t;
        }
      }
      const total = effectiveUnit * qty;

      const priceEl = card.querySelector('[data-pp-price-display]');
      const breakdownEl = card.querySelector('[data-pp-price-breakdown]');
      if (priceEl) priceEl.textContent = fmtMoney(total);

      if (breakdownEl) {
        if (qty <= 1) {
          breakdownEl.setAttribute('hidden', '');
          breakdownEl.textContent = '';
        } else {
          breakdownEl.removeAttribute('hidden');
          let txt = `${qty} × ${fmtMoney(effectiveUnit)} c/u`;
          if (tierApplied && effectiveUnit < packPrice) {
            const savingPct = ((packPrice - effectiveUnit) / packPrice) * 100;
            txt += ` · 🏷️ ahorras ${savingPct.toFixed(0)}%`;
          }
          breakdownEl.textContent = txt;
        }
      }
    }

    /**
     * Handler de click en botón Agregar — lee qty del stepper de la misma
     * card y dispara _addToCart con el valor correcto.
     */
    _handleAddClick(btn) {
      const card = btn.closest('.pp-assistant__product-card');
      const qtyInput = card && card.querySelector('[data-pp-qty-input]');
      const variantId = btn.getAttribute('data-pp-add-variant');
      const title = btn.getAttribute('data-pp-add-title') || 'producto';
      if (!variantId) return;
      // Primer click "+" / "Agregar": suma 1 al cart y abre stepper temporal.
      btn.disabled = true;
      btn.classList.add('pp-assistant__product-add--loading');
      this._addToCart(variantId, title, 1).finally(() => {
        btn.classList.remove('pp-assistant__product-add--loading');
        btn.disabled = false;
        // Si qty estaba en 0, ahora es 1; si ya estaba sumado por otra vía
        // (improbable acá pero por completitud) tomamos lo que dice el input.
        const cur = qtyInput ? parseInt(qtyInput.value, 10) : 1;
        if (qtyInput) qtyInput.value = String(Math.max(1, cur));
        card.classList.add('is-added');
        this._activateCardQtyControl(card);
        this._updateCardPriceDisplay(card);
      });
    }

    /**
     * +/− del qty stepper de product card.
     * Si el card está en `is-added` (post primer Agregar), cada +/- suma o
     * resta directamente al carrito (1 unidad). Si llega a 0, vuelve al
     * estado inicial (botón Agregar visible).
     * Si NO está `is-added` (no debería pasar porque CSS lo esconde), se
     * comporta como antes — solo ajuste visual.
     */
    _handleQtyStep(btn) {
      const dir = btn.getAttribute('data-pp-qty-step');
      const card = btn.closest('.pp-assistant__product-card');
      const input = card && card.querySelector('[data-pp-qty-input]');
      if (!input || !card) return;
      const cur = parseInt(input.value, 10) || 1;
      const isAdded = card.classList.contains('is-added');
      const addBtn = card.querySelector('[data-pp-add-variant]');
      const variantId = addBtn ? addBtn.getAttribute('data-pp-add-variant') : null;
      const title = addBtn ? (addBtn.getAttribute('data-pp-add-title') || 'producto') : 'producto';

      if (isAdded && variantId) {
        if (dir === 'inc') {
          const next = Math.min(99, cur + 1);
          input.value = String(next);
          this._updateCardPriceDisplay(card);
          this._activateCardQtyControl(card);
          this._addToCart(variantId, title, 1);
        } else if (dir === 'dec') {
          const next = cur - 1;
          if (next <= 0) {
            // No removemos desde el stepper: el user pidió que la única
            // forma de quitar un item sea desde la tab Carrito. Solo
            // dejamos un hint visual y mantenemos qty=1.
            this._activateCardQtyControl(card);
            this._showToast(
              'Para quitar un producto, abre la pestaña Carrito.',
              { error: false }
            );
          } else {
            input.value = String(next);
            this._updateCardPriceDisplay(card);
            this._activateCardQtyControl(card);
            // Buscamos line key real en el cart para cart/change.js — el
            // endpoint puede aceptar variant_id como id, pero si hay 2+
            // líneas con el mismo variant podría confundirse. Mejor
            // usar lineKey directo si lo encontramos.
            this._changeCartQtyForVariant(variantId, next);
          }
        }
        return;
      }

      // Fallback (no is-added): solo visual.
      let next = cur;
      if (dir === 'inc') next = Math.min(99, cur + 1);
      else if (dir === 'dec') next = Math.max(1, cur - 1);
      input.value = String(next);
      this._updateCardPriceDisplay(card);
    }

    /**
     * Rappi-style mobile UX para product cards:
     *
     * Estados de la card (vía clases):
     *   - default (qty=0): muestra botón "+"
     *   - .pp-qty-active: muestra stepper (− N +). Se inicia tras tap "+"
     *     o tras tap en el badge. Se autocierra a los 2.5s sin interacción.
     *   - .pp-qty-idle: muestra badge con la cantidad actual en carrito.
     *
     * Cada tap en "+" / "−" / badge invoca _activateCardQtyControl que
     * resetea el timer; al expirar pasa a idle (si qty>0) o a default
     * (si qty=0).
     */
    _activateCardQtyControl(card) {
      if (!card) return;
      card.classList.add('pp-qty-active');
      card.classList.remove('pp-qty-idle');
      // Cancelar timer previo
      if (card._qtyHideTimer) clearTimeout(card._qtyHideTimer);
      card._qtyHideTimer = setTimeout(() => {
        this._idleCardQtyControl(card);
      }, 2500);
    }

    _idleCardQtyControl(card) {
      if (!card) return;
      // Source of truth = cart real (dataset.cartQty), no el qtyInput visual.
      const cartQty = parseInt(card.dataset.cartQty || '0', 10) || 0;
      card.classList.remove('pp-qty-active');
      if (cartQty > 0) {
        card.classList.add('pp-qty-idle');
        card.classList.add('is-added');
        const badge = card.querySelector('[data-pp-qty-badge]');
        if (badge) {
          badge.textContent = String(cartQty);
          badge.removeAttribute('hidden');
        }
      } else {
        card.classList.remove('pp-qty-idle');
        card.classList.remove('is-added');
      }
    }

    /**
     * Lee this.cartData y para cada product card actualiza:
     *   - dataset.cartQty (qty REAL del carrito — 0 si no está)
     *   - el qty input visual (min 1 para el stepper)
     *   - el badge text
     *   - la clase qty-idle / default
     * Llamado tras cualquier add/change y al cargar el cart inicial.
     */
    _syncProductCardsWithCart() {
      if (!this.productsList) return;
      const items = (this.cartData && this.cartData.items) || [];
      const cardsByVariant = new Map();
      // Index: variant_id → qty total en carrito
      items.forEach((item) => {
        const vid = String(item.variant_id || item.id || '');
        if (!vid) return;
        cardsByVariant.set(vid, (cardsByVariant.get(vid) || 0) + (item.quantity || 0));
      });
      this.productsList.querySelectorAll('.pp-assistant__product-card').forEach((card) => {
        const addBtn = card.querySelector('[data-pp-add-variant]');
        const variantId = addBtn ? addBtn.getAttribute('data-pp-add-variant') : '';
        const cartQty = variantId ? (cardsByVariant.get(variantId) || 0) : 0;
        card.dataset.cartQty = String(cartQty);
        const qtyInput = card.querySelector('[data-pp-qty-input]');
        if (qtyInput) qtyInput.value = String(cartQty > 0 ? cartQty : 1);
        // Si está en active, solo refresca precio. NO cambiamos estado
        // (el user está interactuando con el stepper).
        if (card.classList.contains('pp-qty-active')) {
          this._updateCardPriceDisplay(card);
          return;
        }
        this._idleCardQtyControl(card);
        this._updateCardPriceDisplay(card);
      });
    }

    _clampQtyInput(input) {
      const v = parseInt(input.value, 10);
      if (!Number.isFinite(v) || v < 1) {
        // Permitir vacío mientras escribe — solo clamp en blur o cuando se usa
        return;
      }
      if (v > 99) input.value = '99';
    }

    /**
     * Cambio de variant en el select — actualiza precio, sku, disponibilidad
     * y el data-pp-add-variant del botón Agregar.
     */
    _handleVariantChange(select) {
      const card = select.closest('.pp-assistant__product-card');
      if (!card) return;
      const opt = select.options[select.selectedIndex];
      if (!opt) return;
      const variantId = opt.value;
      const price = parseFloat(opt.getAttribute('data-price'));
      const available = opt.getAttribute('data-available') === 'true';
      const addBtn = card.querySelector('[data-pp-add-variant]');
      const priceEl = card.querySelector('.pp-assistant__product-price');
      if (addBtn) {
        addBtn.setAttribute('data-pp-add-variant', variantId);
        addBtn.disabled = !available;
      }
      if (priceEl && Number.isFinite(price)) {
        priceEl.textContent = fmtMoney(price);
      }
    }

    // ════════════════════════════════════════════════════════════
    // Cart panel — sincroniza con /cart.js del storefront
    // ════════════════════════════════════════════════════════════

    /**
     * Varios productos al carrito en UN solo /cart/add.js (Paco v8.18).
     * `items`: [{ variant_id: GID | numérico, quantity: paquetes }].
     */
    async _addItemsToCart(items) {
      const lineas = (items || [])
        .map((it) => {
          const m = /(\d+)$/.exec(String((it && it.variant_id) || ''));
          const q = Math.max(1, Math.min(9999, parseInt(it && it.quantity, 10) || 1));
          return m ? { id: Number(m[1]), quantity: q } : null;
        })
        .filter(Boolean);
      if (lineas.length === 0) return false;
      this._showToast(`Paco agregando ${lineas.length} producto${lineas.length > 1 ? 's' : ''}…`);
      let success = true;
      let errorMessage = '';
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: lineas }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          success = false;
          errorMessage = err.description || err.message || `HTTP ${res.status}`;
        }
      } catch (err) {
        success = false;
        errorMessage = (err && err.message) || 'error de red';
      }
      // Siempre: un 422 puede dejar el carrito con parte de lo pedido.
      await this._loadCart().catch(() => {});
      document.dispatchEvent(new CustomEvent('pp:cart:updated'));
      if (success) this._showToast(`Agregados ${lineas.length} producto${lineas.length > 1 ? 's' : ''} a tu carrito`);
      else this._showToast(`No se pudieron agregar: ${truncate(errorMessage, 80)}`, { error: true });
      return success;
    }

    /**
     * La FILA de las acciones de carrito que pide Paco: una a la vez, en el
     * orden en que llegaron. Si una truena, la siguiente corre igual.
     * (Los botones de las tarjetas llaman a _addToCart directo: son clics de
     * una persona, uno por uno.)
     */
    _enFilaDeCarrito(tarea) {
      const anterior = this._filaDeCarrito || Promise.resolve();
      const turno = anterior.then(tarea, tarea);
      this._pacoMovioElCarrito = true;
      this._filaDeCarrito = turno.catch(() => {});
      return turno;
    }

    /**
     * Antes de mandar un mensaje: que termine lo que Paco esté agregando y, si
     * movió el carrito en esta página, leerlo de nuevo. Nunca lanza ni detiene
     * el mensaje.
     */
    async _carritoAlDia() {
      try {
        if (this._filaDeCarrito) await this._filaDeCarrito;
        if (!this._pacoMovioElCarrito) return;
        const real = await this._leerCarritoReal();
        if (real) {
          this._setCartData(real);
          this._renderCart();
        }
      } catch (err) {
        // el mensaje sale igual, con lo último que se sabía
      }
    }

    /** /cart.js leído AHORA, sin pasar por la caché ni por _loadCart. Null si falla. */
    async _leerCarritoReal() {
      try {
        const res = await fetch('/cart.js', {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return null;
        const cart = await res.json();
        return cart && Array.isArray(cart.items) ? cart : null;
      } catch (err) {
        return null;
      }
    }

    /** Paquetes de una variante en un carrito de /cart.js. Null si no hay carrito. */
    _paquetesDeVariante(cart, variantId) {
      if (!cart || !Array.isArray(cart.items)) return null;
      const id = extractNumericId(variantId);
      return cart.items.reduce((n, it) => n + (Number(it.variant_id) === id ? Number(it.quantity) || 0 : 0), 0);
    }

    /**
     * Un add_to_cart de Paco, CON ACUSE (13-oct-2026).
     *
     * Un «200» de /cart/add.js no prueba que entró (ver _handleClientAction):
     * se lee el carrito ANTES y DESPUÉS y se le manda al servidor cuántos
     * paquetes de esa variante había y cuántos hay, con el error de Shopify
     * si lo hubo. El turno de Paco espera ese acuse para decir la verdad
     * («ya quedaron 2» o «no entró, por esto»).
     *
     * Sin `action_id` (servidor anterior a este cambio) es el camino de
     * siempre, sin acuse.
     */
    async _agregarConAcuse(variantId, qty, actionId) {
      // Toast inmediato — el cliente ve que Paco está agregando
      this._showToast(`Paco agregando ${qty} paquete${qty > 1 ? 's' : ''}…`);
      if (!actionId) {
        await this._addToCart(variantId, 'producto', qty);
        return;
      }
      const antes = this._paquetesDeVariante(await this._leerCarritoReal(), variantId);
      this._ultimoAdd = null;
      const exito = await this._addToCart(variantId, 'producto', qty, { sinToastDeExito: true });
      const r = this._ultimoAdd || { ok: !!exito, status: exito ? 200 : 0, error: null };
      // _addToCart ya recargó el carrito, pero _loadCart comparte la petición
      // en curso y puede traer una lectura anterior al add: se lee otra vez.
      const real = await this._leerCarritoReal();
      if (real) {
        this._setCartData(real);
        this._renderCart();
      }
      const despues = this._paquetesDeVariante(real, variantId);
      const entraron = antes != null && despues != null ? despues - antes : null;
      if (entraron != null && entraron >= qty) {
        this._showToast(`Agregado${qty > 1 ? ` (×${qty})` : ''}`);
      } else if (entraron != null && entraron > 0) {
        this._showToast(`Sólo entraron ${entraron} de ${qty}${r.error ? ': ' + truncate(r.error, 60) : ''}`, { error: true });
      } else if (entraron === 0 && r.ok) {
        // Shopify dijo que sí y el carrito no lo trae. No es «Agregado».
        this._showToast('No quedó en tu carrito. Paco lo revisa.', { error: true });
      }
      await this._mandarAcuseDeCarrito({
        action_id: String(actionId),
        ok: !!r.ok,
        status: Number(r.status) || 0,
        error: r.error || null,
        pedido: qty,
        antes,
        despues,
      });
    }

    /**
     * POST /api/cart-ack: el acuse de una acción de carrito. Nunca lanza: si
     * no sale, el servidor deja de esperar a los 7 s y Paco no afirma nada.
     */
    async _mandarAcuseDeCarrito(acuse) {
      try {
        const conversationId = getConversationId();
        if (!conversationId) return false;
        const res = await fetch(this.backendUrl.replace(/\/$/, '') + '/api/cart-ack', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(this.testSecret ? { 'x-assistant-test-secret': this.testSecret } : {}),
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            session_id: getSessionId(),
            conversation_id: conversationId,
            prueba: banderaPrueba(),
            ...acuse,
            carrito: this._buildCartSnapshot(),
          }),
        });
        return !!res.ok;
      } catch (err) {
        console.warn('[pp-assistant] el acuse del carrito no salió:', err);
        return false;
      }
    }

    /**
     * Handler de SSE client_action — Paco emitió un add_to_cart desde
     * la conversación. Backend ya validó allowlist; nosotros ejecutamos
     * contra /cart/add.js, refrescamos cart panel, toast.
     */
    async _handleClientAction(data) {
      if (!data) return;

      if (data.action === 'add_to_cart' && Array.isArray(data.items)) {
        // 8-oct-2026 (Paco v8.18, personalize_products): VARIOS de una vez,
        // en UNA sola llamada — dos POST seguidos al mismo carrito pueden
        // pisarse y dejar uno fuera.
        await this._enFilaDeCarrito(() => this._addItemsToCart(data.items));
        return;
      }

      if (data.action === 'add_to_cart') {
        const variantId = data.variant_id;
        const qty = Number(data.quantity) || 1;
        if (!variantId) return;
        // EN FILA (13-oct-2026). Este handler se llama una vez por evento y
        // sin esperar al anterior: con 7 add_to_cart en un mensaje de Paco
        // salían 7 POST /cart/add.js A LA VEZ. Medido en Chrome real contra
        // un carrito nuevo: Shopify contesta 200 a los siete y el carrito se
        // queda con UNO (cada petición lee el carrito, suma lo suyo y lo
        // guarda encima de las demás). En fila: 7 de 7. Caso real: 5-oct,
        // «No están las cajas»; 8-oct, un kit de 5 productos del que sólo
        // entró uno. Ver _enFilaDeCarrito y _agregarConAcuse.
        await this._enFilaDeCarrito(() => this._agregarConAcuse(variantId, qty, data.action_id));
        return;
      }

      if (data.action === 'trigger_handoff') {
        // Sprint 2AH: Paco confirmó la categoría con el cliente y dispara
        // el handoff real al CRM. El category viene del LLM tool call.
        const category = data.category === 'servicio' ? 'servicio' : 'ventas';
        const reason = (data.reason && String(data.reason).slice(0, 300)) || null;
        // QA 4-oct-2026: Paco ya le dijo con quién y cuándo. Sin burbujas
        // aquí (ni «Conectar con asesor» ni el mensaje de /api/handoff):
        // sólo encabezado y polling. Ver tests/paco-pase-sin-doble-mensaje.
        this._handleHumanHandoff('Conectar con asesor', { category, reason, desdePaco: true });
        return;
      }
    }

    // ════════════════════════════════════════════════════════════
    // Human handoff (Sprint 1 = placeholder, Sprint 2 = real CRM)
    // ════════════════════════════════════════════════════════════

    /**
     * Trigger del handoff a asesor humano. POST a /api/handoff que:
     *   1. Marca chat_conversations.bot_active=false en CRM Main
     *   2. Asigna vendedor via round-robin (o Erick si 'servicio')
     *   3. Inserta system message en CRM (lo verá el vendedor en su panel)
     *   4. Inserta notification que dispara realtime al vendedor asignado
     *   5. Devuelve vendor_first_name para que el widget personalice
     *
     * UX: bubble inicial "Conectándote con un asesor..." → en cuanto
     * responde el backend lo reemplazamos por "Te conecto con María de
     * Para Paquetes. Te contactamos por WhatsApp en un momento."
     *
     * Failure tolerance: si CRM no responde, mostramos fallback con
     * WhatsApp directo. El cliente NUNCA ve error.
     *
     * Telemetry: emit pp-handoff-requested custom event para que cualquier
     * tracker (Shopify Pixel, GA, etc.) lo cuente.
     */
    _handleHumanHandoff(userText, opts) {
      // Sprint 2AH: opts.category = 'ventas' | 'servicio' (cuando viene
      // del tool LLM). opts.reason = resumen del por qué.
      const category = opts && opts.category;
      const reason = opts && opts.reason;
      // El pase lo pidió PACO (trigger_handoff): él ya lo anunció en su
      // respuesta, así que aquí no se pinta nada — sólo encabezado y polling.
      // Si lo pica el CLIENTE («¿Necesitas ayuda humana?»), sí hay mensajes.
      const desdePaco = !!(opts && opts.desdePaco);
      const display = userText || 'Necesito ayuda humana';
      if (!desdePaco) this._appendMessage('user', display);
      if (this.faqChipsEl) this.faqChipsEl.setAttribute('hidden', '');

      const conversationId = getConversationId();
      const sessionId = getSessionId();

      // Bubble placeholder (loading state implícito)
      const placeholderWrap = desdePaco
        ? null
        : this._appendMessage('assistant', 'Conectándote con un asesor de Para Paquetes…');
      const pintar = (texto) => {
        if (placeholderWrap) this._replaceMessageText(placeholderWrap, texto);
      };

      // Telemetry: emit ANTES del fetch — UTM/pixel listeners no esperan
      // la respuesta del backend para contar el intent.
      try {
        document.dispatchEvent(
          new CustomEvent('pp-handoff-requested', {
            detail: {
              session_id: sessionId,
              conversation_id: conversationId,
              source: desdePaco ? 'paco' : userText ? 'faq_chip' : 'composer_link',
            },
          })
        );
      } catch {
        // best-effort
      }

      // Sin conversation_id (turno cero) → fallback directo. El cliente
      // todavía no inició chat, así que no hay nada que escalar al CRM.
      if (!conversationId) {
        pintar('Te contactamos por WhatsApp al +52 81 7770 5981. Gracias por escribirnos.');
        return;
      }

      const url =
        this.backendUrl.replace(/\/$/, '') + '/api/handoff';

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Same auth pattern que /api/chat (App Proxy adds signature,
          // o test-secret en dev). Si neither, el backend rechaza.
          ...(this.testSecret
            ? { 'x-assistant-test-secret': this.testSecret }
            : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          session_id: sessionId,
          conversation_id: conversationId,
          prueba: banderaPrueba(),
          reason: 'user_request',
          user_text: reason || userText || null,
          category: category || null,
        }),
      })
        .then((r) => r.json())
        .then((data) => {
          let msg;
          if (data && data.ok === true) {
            msg =
              data.message ||
              `Te conecto con ${data.vendor_first_name || 'un asesor'} de Para Paquetes. Te responde ${cuandoRespondeAsesor()}, aquí mismo en el chat.`;
            // Marcar bot como inactivo + arrancar polling para que cuando
            // el vendedor responda en el CRM, el cliente lo vea aquí.
            this.botActive = false;
            this.assignedVendorName = data.vendor_first_name || null;
            // Sprint 2AC: cambiar el header al nombre del vendedor para
            // que el cliente sepa con quién habla.
            this._updateHeaderForHandoff(data.vendor_first_name);
            // Setear lastAgentSyncAt a now() para que el primer poll NO
            // traiga de vuelta el system message del handoff (ya lo
            // mostramos localmente vía el placeholderWrap). El polling
            // solo debe pintar mensajes futuros del vendedor.
            this.lastAgentSyncAt = new Date().toISOString();
            console.info('[pp-assistant] handoff confirmed → polling arrancado, vendor:', data.vendor_first_name);
            this._startAgentPolling();
            // Telemetry: handoff confirmado
            try {
              document.dispatchEvent(
                new CustomEvent('pp-handoff-confirmed', {
                  detail: {
                    session_id: sessionId,
                    conversation_id: conversationId,
                    vendor_first_name: data.vendor_first_name || null,
                    vendor_name: data.vendor_name || null,
                    already_handed_off: data.already_handed_off === true,
                  },
                })
              );
            } catch {
              // best-effort
            }
          } else {
            // ok=false con fallback_message del backend, o response shape inesperado.
            // Mensaje útil — NO regalar nuestro WhatsApp (ya lo tiene en
            // el footer del sitio si lo necesita).
            msg =
              (data && data.fallback_message) ||
              respaldoAsesor();
          }
          pintar(msg);
        })
        .catch((err) => {
          // Network failure: mensaje útil.
          console.warn('[pp-assistant] handoff fetch failed:', err);
          pintar(respaldoAsesor());
        });
    }

    /**
     * Sprint 2AC: cuando hay handoff activo (bot_active=false), el header
     * del widget muestra el nombre del vendedor en vez de "Asistente Para
     * Paquetes". Le da claridad al cliente: ya no habla con bot.
     *
     * No cambia el branding base del widget (el avatar sigue siendo
     * el chat icon — no abusamos la foto del vendedor para no causar
     * issues si el profile no tiene avatar).
     */
    _updateHeaderForHandoff(vendorFirstName) {
      if (!vendorFirstName || !this.titleEl || !this.subtitleEl) return;
      this.titleEl.textContent = vendorFirstName + ' · Para Paquetes';
      this.subtitleEl.textContent = 'Te responde ' + cuandoRespondeAsesor() + ', aquí mismo.';
      // Sprint 2AG: ocultar link "¿Necesitas ayuda humana?" cuando ya
      // hay vendedor asignado — el cliente ya está conectado.
      if (this.humanHandoffLink) {
        this.humanHandoffLink.setAttribute('hidden', '');
      }
    }

    _resetHeader() {
      if (this.titleEl) this.titleEl.textContent = this.titleText;
      if (this.subtitleEl) this.subtitleEl.textContent = this.subtitleText;
      if (this.humanHandoffLink) {
        this.humanHandoffLink.removeAttribute('hidden');
      }
    }

    /**
     * Reemplaza el contenido de texto de un bubble previamente appendeado.
     * Usado para llenar el placeholder del handoff con la respuesta real
     * del backend.
     */
    _replaceMessageText(wrapEl, newText) {
      if (!wrapEl) return;
      // El bubble interno es el primer .pp-assistant__msg-bubble dentro del wrap
      const bubble = wrapEl.querySelector('.pp-assistant__msg-bubble');
      if (bubble) {
        bubble.textContent = newText;
      } else {
        wrapEl.textContent = newText;
      }
      this._announce(newText);
    }

    // ════════════════════════════════════════════════════════════
    // Sprint 2L: polling vendedor → cliente post-handoff
    // ════════════════════════════════════════════════════════════
    //
    // Después del handoff, el vendedor responde en el CRM Main. El
    // widget pollea /api/conversation/:id/sync cada N segundos para
    // traer los mensajes nuevos (sender_type='agent' o 'system') y
    // pintarlos en el chat. El cliente NUNCA tiene que ir a WhatsApp
    // — la conversación cierra en el mismo chat web donde empezó.
    //
    // Cadencia:
    //   - Cada 5s mientras el tab esté visible y la conv esté en
    //     bot_active=false.
    //   - Pause cuando tab está hidden (visibilitychange).
    //   - Auto-stop si pasan 30 min sin actividad nueva (cliente
    //     probablemente ya se fue).

    /**
     * Inicia el polling. Idempotent — si ya está corriendo no duplica.
     * Hace un primer sync inmediato para dar feedback rápido.
     */
    _startAgentPolling() {
      if (this.agentPollTimer) return;
      this.agentPollLastMsgAt = Date.now();
      this._syncAgentMessages(false);
      this.agentPollTimer = setInterval(() => {
        if (document.hidden) return; // pausa silenciosa
        // Auto-stop por idle (clean up tabs olvidados)
        if (
          this.agentPollLastMsgAt &&
          Date.now() - this.agentPollLastMsgAt > AGENT_POLL_IDLE_TIMEOUT_MS
        ) {
          this._stopAgentPolling();
          return;
        }
        this._syncAgentMessages(false);
      }, AGENT_POLL_INTERVAL_MS);
    }

    _stopAgentPolling() {
      if (this.agentPollTimer) {
        clearInterval(this.agentPollTimer);
        this.agentPollTimer = null;
      }
    }

    /**
     * Sync con el backend para traer mensajes nuevos del CRM Main.
     * Si firstCheck=true, solo arranca polling si bot_active=false
     * (conv que ya hizo handoff antes de este page-load).
     */
    async _syncAgentMessages(firstCheck) {
      const convId = getConversationId();
      const sessionId = getSessionId();
      if (!convId || !sessionId) {
        if (firstCheck) console.debug('[pp-assistant] sync skip: no conv/session');
        return;
      }

      const url =
        this.backendUrl.replace(/\/$/, '') +
        '/api/conversation/' +
        encodeURIComponent(convId) +
        '/sync?session_id=' +
        encodeURIComponent(sessionId) +
        (this.lastAgentSyncAt
          ? '&after_iso=' + encodeURIComponent(this.lastAgentSyncAt)
          : '');

      const headers = { Accept: 'application/json' };
      if (this.testSecret) headers['x-assistant-test-secret'] = this.testSecret;

      let data;
      try {
        const res = await fetch(url, {
          method: 'GET',
          headers,
          credentials: 'omit',
        });
        if (!res.ok) {
          console.warn('[pp-assistant] sync HTTP', res.status);
          return;
        }
        data = await res.json();
      } catch (err) {
        // Network/CORS errors: silencioso, próximo poll retry.
        console.warn('[pp-assistant] sync fetch error:', err);
        return;
      }
      // 3-oct-2026: Paco 3.0 deja cambiar de conversación aunque haya
      // asesor; una respuesta que llega tarde es de la plática ANTERIOR y no
      // se pinta en la nueva (ni le cambia botActive ni el encabezado).
      if (getConversationId() !== convId) return;
      if (!data || data.ok !== true) {
        if (firstCheck) console.debug('[pp-assistant] sync response not ok:', data);
        return;
      }

      // Update conv state desde el response
      if (typeof data.bot_active === 'boolean') {
        this.botActive = data.bot_active;
      }
      if (data.assigned_vendor_name) {
        this.assignedVendorName = data.assigned_vendor_name;
      }
      if (data.last_seen_at) {
        this.lastAgentSyncAt = data.last_seen_at;
      }

      // En firstCheck (al rehidratar), solo arrancar polling si la
      // conv ya está en bot_active=false. Si bot_active=true, no
      // tiene sentido pollear (Paco sigue activo).
      if (firstCheck && data.bot_active === false && !this.agentPollTimer) {
        console.info('[pp-assistant] firstCheck detectó handoff previo → polling arrancado');
        // Si cliente cerró y reabrió post-handoff, restaurar header con
        // vendor name. Sprint 2AC.
        if (data.assigned_vendor_name) {
          this._updateHeaderForHandoff(data.assigned_vendor_name);
        }
        this._startAgentPolling();
      }

      // Render mensajes nuevos
      const msgs = Array.isArray(data.messages) ? data.messages : [];
      let renderedAny = false;
      for (const m of msgs) {
        if (!m || !m.id || this.renderedAgentMsgIds.has(m.id)) continue;
        this.renderedAgentMsgIds.add(m.id);
        this._appendAgentMessage(m);
        renderedAny = true;
      }

      if (msgs.length > 0) {
        console.info('[pp-assistant] sync recibió', msgs.length, 'msgs,', renderedAny ? 'renderedAny' : 'todos ya renderizados');
      }

      if (renderedAny) {
        this.agentPollLastMsgAt = Date.now();
        // Si el panel está cerrado, mostrar badge para llamar atención
        if (!this.isOpen) {
          if (this.fab) this.fab.classList.add('pp-assistant__fab--has-new');
          marcarChatNuevo(true);
        }
      }

      // Si bot_active volvió a true (raro: vendedor regresó la conv al bot),
      // detener polling.
      if (data.bot_active === true) {
        this._stopAgentPolling();
      }
    }

    /**
     * Renderea un mensaje del CRM Main (sender_type=agent|system) como
     * bubble en el chat. Distinto a Paco visualmente:
     *   - agent: header con nombre del vendedor ("María de Para Paquetes")
     *   - system: bubble centrado pequeño, no parece mensaje normal
     */
    _appendAgentMessage(m) {
      const role = m.sender_type === 'system' ? 'system' : 'agent';
      const texto = String(m.body || '');
      const adj = adjuntoDelAsesor(m.attachment, this.dataset && this.dataset.supabaseUrl);
      // Sin texto ni adjunto pintable no hay nada que enseñar: nunca una
      // burbuja vacía (14-oct-2026).
      if (!texto.trim() && !adj) return;

      const senderName = m.sender_name || this.assignedVendorName || 'Asesor';
      const wrap = el('div', {
        cls: 'pp-assistant__msg pp-assistant__msg--' + role,
      });

      // Bubble PRIMERO (pegado al borde izq vía CSS flex-column +
      // align-items:flex-start), nombre DEBAJO.
      const bubble = el('div', {
        cls: 'pp-assistant__msg-bubble' + (adj && !texto.trim() ? ' pp-assistant__msg-bubble--solo-adjunto' : ''),
      });
      if (texto.trim()) bubble.appendChild(document.createTextNode(texto));
      if (adj) bubble.appendChild(this._adjuntoDelAsesorEl(adj, senderName));
      wrap.appendChild(bubble);

      if (role === 'agent') {
        // Solo nombre — "Para Paquetes" es redundante (todos los agentes
        // son de Para Paquetes).
        const nameEl = el('div', {
          cls: 'pp-assistant__msg-sender',
          text: senderName,
        });
        wrap.appendChild(nameEl);
      }

      this.messagesEl.appendChild(wrap);
      this._scrollToBottom();
      const extra = adj ? (adj.kind === 'image' ? ' (imagen adjunta)' : ' (archivo adjunto)') : '';
      this._announce((role === 'agent' ? senderName + ': ' : '') + texto + extra);
    }

    /** La imagen (miniatura que abre la completa) o el enlace «Ver archivo»
     * de un adjunto del asesor. Sólo nodos y atributos: nada de innerHTML. */
    _adjuntoDelAsesorEl(adj, senderName) {
      const link = el('a', {
        cls: 'pp-assistant__adjunto pp-assistant__adjunto--' + (adj.kind === 'image' ? 'imagen' : 'archivo'),
        attrs: { href: adj.url, target: '_blank', rel: 'noopener noreferrer' },
      });
      if (adj.kind === 'image') {
        const alt = 'Imagen que te envió ' + senderName + (adj.name ? ' (' + adj.name + ')' : '');
        const img = el('img', {
          attrs: { src: adj.url, alt: alt, loading: 'lazy', decoding: 'async' },
        });
        link.setAttribute('aria-label', 'Ver imagen completa');
        // Llega después de pintar: al cargar, que siga viéndose el final.
        img.addEventListener('load', () => this._scrollToBottom());
        // Si no carga, que quede un enlace y no un cuadro roto.
        img.addEventListener('error', () => {
          if (img.parentNode !== link) return;
          link.removeChild(img);
          link.className = 'pp-assistant__adjunto pp-assistant__adjunto--archivo';
          link.removeAttribute('aria-label');
          link.appendChild(el('span', { cls: 'pp-assistant__adjunto-accion', text: 'Ver imagen' }));
        });
        link.appendChild(img);
      } else {
        link.appendChild(el('span', { cls: 'pp-assistant__adjunto-accion', text: 'Ver archivo' }));
        if (adj.name) link.appendChild(el('span', { cls: 'pp-assistant__adjunto-nombre', text: adj.name }));
      }
      return link;
    }

    _handleVisibilityChange() {
      // No hacemos nada en pausa — el interval check `if (document.hidden)`
      // ya skipea polls invisibles. Cuando el tab vuelve a visible
      // hacemos UN sync inmediato para no esperar 5s.
      if (!document.hidden && this.agentPollTimer) {
        this._syncAgentMessages(false);
      }
    }

    // ════════════════════════════════════════════════════════════
    // Lead capture (anónimos)
    // ════════════════════════════════════════════════════════════

    /**
     * Decide si mostrar el modal de lead capture al cliente anónimo.
     * Triggers (ALL must apply):
     *   - Cliente anónimo (no customerId)
     *   - userTurnCount >= LEAD_PROMPT_AFTER_TURNS (3 turnos del usuario)
     *   - No mostrado/dismissed previamente en este browser
     *   - Hay signal de intent comercial:
     *     * Cart con al menos 1 item, O
     *     * Recommendations panel con productos (Paco ya recomendó algo), O
     *     * Paco usó una herramienta comercial — ver TOOLS_INTENCION_COMERCIAL
     *
     * La tercera se agregó el 26-ago-2026. Las dos primeras miden RESULTADO
     * (hay algo en el carrito, hay algo pintado) y por eso dejaban fuera al
     * cliente cuyo producto no está en catálogo, que es justo el que más
     * necesita que ventas lo llame.
     */
    _maybeShowLeadCapture() {
      if (this.customerId) return; // logueado → no necesitamos lead capture
      // Ya dejó su contacto en el chat o ya está con un vendedor: el modal sobra.
      if (this.contactoEnElChat || this.botActive === false) return;
      // QA móvil 4-oct-2026: el canvas (facturar, pedido, proveedor…) ya se
      // abrió en esta page-load, o tiene una hoja abierta en celular — el
      // aviso no debe competir con él ni taparlo.
      if (this.canvasAbiertoEnConversacion) return;
      if (this.querySelector('.pp3-hoja:not([hidden])')) return;
      // En celular, un botón de acción del canvas colgando del último
      // mensaje («Facturar aquí», «Llenar forma»…) nunca debe quedar tapado.
      if (esCelularPaco() && this._ultimoMensajeEsBotonCanvas()) return;
      if (this.userTurnCount < LEAD_PROMPT_AFTER_TURNS) return;
      const leadState = localStorage.getItem(STORAGE_KEY_LEAD);
      if (leadState === 'saved' || leadState === 'dismissed') return;
      const hasIntent =
        (this.cartData && this.cartData.item_count > 0) ||
        this.recommendedProducts.size > 0 ||
        this.senalComercial;
      if (!hasIntent) return;
      // Trigger una sola vez por trigger natural
      this._showLeadCapture();
    }

    /** ¿El último mensaje del chat trae colgado un botón de acción del
     * canvas (botonVistaEnChat de pp-paco3.js, clase `pp3-accion`)? Esos
     * botones se insertan como hermanos justo después del mensaje de Paco
     * que los originó, así que mientras no haya turnos nuevos encima son
     * el último hijo de messagesEl. */
    _ultimoMensajeEsBotonCanvas() {
      const ultimo = this.messagesEl && this.messagesEl.lastElementChild;
      return !!(ultimo && ultimo.classList && ultimo.classList.contains('pp3-accion'));
    }

    _showLeadCapture() {
      if (!this.leadConsentLoaded) {
        this._loadLeadConsent();
      }
      if (esCelularPaco()) {
        // Celular: tarjeta compacta DENTRO del chat — nunca un overlay que
        // tape los botones de acción del canvas (QA 4-oct-2026).
        this.leadCard.classList.add('pp-assistant__lead-card--inline');
        this.messagesEl.appendChild(this.leadCard);
        this._scrollToBottom();
        return;
      }
      this.leadCard.classList.remove('pp-assistant__lead-card--inline');
      this.leadModal.appendChild(this.leadCard);
      this.leadModal.removeAttribute('hidden');
      // Foco al primer input
      requestAnimationFrame(() => {
        if (this.leadNameInput) this.leadNameInput.focus();
      });
    }

    /** Oculta la UI de lead capture, sea cual sea su forma actual (overlay
     * de escritorio o tarjeta dentro del chat en celular). */
    _closeLeadCaptureUi() {
      if (this.leadCard && this.leadCard.parentNode === this.messagesEl) {
        this.leadCard.remove();
      } else {
        this.leadModal.setAttribute('hidden', '');
      }
    }

    /** ✕ de la tarjeta (celular) o "Después" (escritorio): no se vuelve a
     * mostrar en este navegador — mismo candado que ya usaba el modal. */
    _dismissLeadCapture() {
      localStorage.setItem(STORAGE_KEY_LEAD, 'dismissed');
      this._closeLeadCaptureUi();
    }

    async _loadLeadConsent() {
      try {
        const url = this.backendUrl.replace(/\/$/, '') + '/api/lead/consent';
        const headers = { Accept: 'application/json' };
        if (this.testSecret)
          headers['x-assistant-test-secret'] = this.testSecret;
        const res = await fetch(url, {
          method: 'GET',
          headers,
          credentials: 'omit',
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data && data.ok && data.text && this.leadConsentText) {
          this.leadConsentText.textContent = data.text;
          this.leadConsentLoaded = true;
        }
      } catch {
        // Fallback: dejar texto generico
        if (this.leadConsentText) {
          this.leadConsentText.textContent =
            'Acepto que Para Paquetes guarde mis datos de contacto para seguimiento comercial. Puedo pedir borrarlos en cualquier momento.';
        }
      }
    }

    async _submitLead() {
      if (this.leadIsSubmitting) return;
      const name = (this.leadNameInput.value || '').trim();
      const whatsapp = (this.leadWhatsappInput.value || '').trim();
      const email = (this.leadEmailInput.value || '').trim();
      const consent = this.leadConsentCheckbox.checked;
      const setStatus = (text, isError) => {
        if (!this.leadStatusEl) return;
        this.leadStatusEl.textContent = text;
        this.leadStatusEl.classList.toggle(
          'pp-assistant__lead-status--error',
          !!isError
        );
      };
      if (!whatsapp && !email) {
        setStatus('Déjanos al menos un WhatsApp o email para contactarte.', true);
        return;
      }
      if (!consent) {
        setStatus('Necesitamos tu consentimiento para guardar tus datos.', true);
        return;
      }
      this.leadIsSubmitting = true;
      this.leadSubmitBtn.disabled = true;
      setStatus('Guardando…');
      try {
        const url = this.backendUrl.replace(/\/$/, '') + '/api/lead';
        const headers = {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        };
        if (this.testSecret)
          headers['x-assistant-test-secret'] = this.testSecret;
        const res = await fetch(url, {
          method: 'POST',
          headers,
          credentials: 'omit',
          body: JSON.stringify({
            session_id: getSessionId(),
            prueba: banderaPrueba(),
            name: name || undefined,
            whatsapp: whatsapp || undefined,
            email: email || undefined,
            consent_marketing: true,
            source_page: window.location.href,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data && data.ok) {
          localStorage.setItem(STORAGE_KEY_LEAD, 'saved');
          setStatus('¡Listo! Te contactamos pronto.');
          setTimeout(() => {
            this._closeLeadCaptureUi();
          }, 1200);
          this._showToast(
            name
              ? `Gracias ${truncate(name, 30)}, te contactamos`
              : 'Gracias, te contactamos pronto'
          );
        } else {
          setStatus(
            (data && data.error) ||
              'No se pudo guardar. Intenta de nuevo en un momento.',
            true
          );
          this.leadSubmitBtn.disabled = false;
        }
      } catch (err) {
        setStatus('Error de red. Intenta de nuevo.', true);
        this.leadSubmitBtn.disabled = false;
      } finally {
        this.leadIsSubmitting = false;
      }
    }

    /**
     * Construye un snapshot compacto del carrito para mandar al backend
     * con cada turno. El backend lo inyecta como contexto al system prompt
     * para que Paco pueda referenciar lo que ya está en el carrito.
     *
     * Siempre devuelve un objeto con `estado`, nunca null. Antes colapsaba
     * a null tres cosas distintas — todavía no leo el carrito, lo leí y
     * está vacío, e intenté leerlo y tronó — y al backend le llegaban
     * idénticas. Como el prompt leía esa ausencia como "el cliente no
     * tiene nada", un add_to_cart que fallaba se veía igual que un
     * carrito vacío legítimo: nada contradecía a Paco cuando afirmaba
     * "ya está agregada al carrito" (caso Emiliano, 25-ago-2026).
     *
     * Los cuatro estados son el contrato que consume interpretarCarrito()
     * en el backend (pp-assistant, lib/agent/carrito.ts) — si cambias uno
     * aquí, cámbialo allá.
     *
     * Trunca a 20 items max para mantener contexto controlado.
     */
    _buildCartSnapshot() {
      const cart = this.cartData;

      // El fetch de /cart.js tronó. No sabemos qué trae el carrito, y decir
      // "vacío" sería inventar un hecho — que es justo lo que causó el bug.
      //
      // OJO con lo que NO mandamos: en los dos estados de "no sé" (`error` y
      // `desconocido`) se OMITEN `items` y los totales a propósito. Mandarlos
      // en cero, como hacíamos antes, dejaba el contrato colgando de un solo
      // string: los tres estados sin renglones (`error`, `desconocido`,
      // `vacio`) viajaban con el MISMO payload y solo los separaba `estado`.
      // Si alguien renombra o pierde ese campo en un refactor, el fallback
      // del backend (interpretarCarrito: `items !== null ? 'vacio' : ...`)
      // los colapsaría a "vacío" — el bug original, otra vez. Omitiendo la
      // lista, la ausencia de evidencia es la ausencia del campo, y el
      // fallback aterriza en `desconocido` solo. Seguro por construcción.
      if (this.cartLoadError) {
        return { estado: 'error' };
      }

      // cartData arranca en null y sigue en null hasta el primer fetch. Eso
      // no es una falla: es "todavía no sé". Tampoco es "no tiene nada".
      if (!cart || !Array.isArray(cart.items)) {
        return { estado: 'desconocido' };
      }

      return {
        // Vacío CONFIRMADO por lectura: es un hecho que Paco sí puede usar
        // (y con el que puede cachar que su add_to_cart no entró).
        estado: cart.items.length === 0 ? 'vacio' : 'con_items',
        item_count: cart.item_count,
        total_mxn: Math.round(((cart.total_price ?? 0) / 100) * 100) / 100,
        discount_mxn:
          Math.round(((cart.total_discount ?? 0) / 100) * 100) / 100,
        items: cart.items.slice(0, 20).map((it) => ({
          title: truncate(it.product_title || it.title, 80),
          sku: it.sku || null,
          // El servidor compara lo que Paco pidió agregar contra esto.
          variant_id: it.variant_id != null ? String(it.variant_id) : null,
          variant_title:
            it.variant_title && it.variant_title !== 'Default Title'
              ? it.variant_title
              : null,
          quantity: it.quantity,
          line_price_mxn: Math.round((it.line_price / 100) * 100) / 100,
          unit_price_mxn: Math.round((it.price / 100) * 100) / 100,
        })),
      };
    }

    /**
     * ÚNICA puerta para guardar un carrito recién leído.
     *
     * `cartLoadError` es pegajoso: una vez prendido, `_buildCartSnapshot()`
     * devuelve `{estado:'error'}` y Paco contesta "no puedo leer tu carrito"
     * aunque el panel se lo esté mostrando. Antes solo `_loadCart` lo
     * apagaba, así que un blip de red seguido de un stepper de cantidad
     * (que refresca `cartData` con la respuesta 200 de /cart/change.js)
     * dejaba la bandera prendida el resto de la sesión.
     *
     * Cualquier respuesta buena de Shopify con el carrito completo ES una
     * lectura exitosa. Guardarla sin apagar la bandera es la contradicción;
     * por eso los dos pasos viven aquí juntos y nadie asigna `this.cartData`
     * por su cuenta.
     */
    _setCartData(data) {
      this.cartData = data;
      this.cartLoadError = false;
    }

    async _loadCart() {
      // Dedupe concurrent loads
      if (this.cartLoadingPromise) return this.cartLoadingPromise;
      this.cartLoadingPromise = (async () => {
        try {
          const res = await fetch('/cart.js', {
            method: 'GET',
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          });
          if (!res.ok) throw new Error('cart fetch failed');
          this._setCartData(await res.json());
          this._renderCart();
        } catch (err) {
          this.cartLoadError = true;
          console.warn('[pp-assistant] cart load failed:', err);
        } finally {
          this.cartLoadingPromise = null;
        }
      })();
      return this.cartLoadingPromise;
    }

    _renderCart() {
      if (!this.cartSection) return; // _render aún no corrió
      const cart = this.cartData;
      const items = cart && cart.items ? cart.items : [];
      const itemCount = cart ? cart.item_count : 0;
      const subtotal = cart ? cart.total_price / 100 : 0;
      const totalDiscount = cart ? (cart.total_discount || 0) / 100 : 0;

      // Sincroniza cards de productos con el carrito (counts en badges + qty)
      this._syncProductCardsWithCart();

      // Carrito vacío → hidden entero (libera todo el espacio para productos)
      if (itemCount === 0) {
        this.cartSection.setAttribute('hidden', '');
        this._updateTabBadges();
        return;
      }

      this.cartSection.removeAttribute('hidden');
      // Format "402 productos" (singular si 1) en lugar de solo número
      this.cartCountBadge.textContent =
        itemCount === 1 ? '1 producto' : `${itemCount.toLocaleString('es-MX')} productos`;
      this.cartHeaderSubtotal.textContent = fmtMoney(subtotal);
      this._updateTabBadges();

      // ─── Expanded body: items + ahorros + checkout ───
      while (this.cartExpandedBody.firstChild) {
        this.cartExpandedBody.removeChild(this.cartExpandedBody.firstChild);
      }

      // Limpia items previos en el contenedor interno
      const itemsWrap = el('div', { cls: 'pp-assistant__cart-items' });

      const displayLimit = 8;
      const displayed = items.slice(0, displayLimit);
      displayed.forEach((item, idx) => {
        const itemImg = item.image
          ? el('img', {
              cls: 'pp-assistant__cart-item-img',
              // width/height fijos (el CSS las pinta a 48×48 con object-fit:
              // contain): reservan el espacio y evitan el salto de layout
              // (CLS) al cargar. Las primeras 4 (visibles sin scroll) cargan
              // eager para que no se vean en blanco al abrir el carrito.
              attrs: {
                src: item.image,
                alt: '',
                loading: idx < 4 ? 'eager' : 'lazy',
                width: '48',
                height: '48',
              },
            })
          : null;
        // Sin truncate — el nombre completo se muestra con clamp:3 si es
        // muy largo, vía CSS line-clamp.
        const itemTitle = el('div', {
          cls: 'pp-assistant__cart-item-title',
          text: String(item.product_title || item.title || ''),
        });

        // Pack info parseada del variant title — "25 Cajas" → {25, "caja"}
        const pack = parsePack(item.variant_title || item.variant_options?.[0]);
        // Precios efectivos (post-descuento) vs originales (pre-descuento)
        // del cart line de Shopify. final_price / final_line_price ya
        // refleja los descuentos automáticos aplicados.
        const effectivePackPrice = (item.final_price ?? item.price) / 100;
        const originalPackPrice = (item.original_price ?? item.price) / 100;
        const effectiveLine = (item.final_line_price ?? item.line_price) / 100;
        const originalLine = (item.original_line_price ?? item.line_price) / 100;
        const hasDiscount = originalLine > effectiveLine + 0.01;

        // Línea 1 (meta principal): SIEMPRE incluir la cantidad para
        // que el cliente vea cuántos pidió. Format estilo V3 search:
        //   "SKU C-2662 · 11 paquetes × $309 MXN" (con pack > 1)
        //   "SKU CA-278-222 · 63 × $41.40 MXN"   (sin pack)
        const metaParts = [];
        if (item.sku) metaParts.push(`SKU ${item.sku}`);
        const qtyLabel =
          pack && pack.count > 1
            ? `${item.quantity} paquete${item.quantity > 1 ? 's' : ''}`
            : String(item.quantity);
        metaParts.push(`${qtyLabel} × ${fmtMoney(effectivePackPrice)}`);
        const itemMeta = el('div', {
          cls: 'pp-assistant__cart-item-meta',
          text: metaParts.join(' · '),
        });

        // Línea 2 (si hay pack): "1,100 bolsas · $2.81 MXN por bolsa"
        let itemBreakdown = null;
        if (pack && pack.count > 1) {
          const totalUnits = pack.count * item.quantity;
          const perUnit = effectivePackPrice / pack.count;
          itemBreakdown = el('div', {
            cls: 'pp-assistant__cart-item-breakdown',
            text:
              `${totalUnits.toLocaleString('es-MX')} ${pack.unitPlural}` +
              ` · ${fmtMoney(perUnit)} por ${pack.unit}`,
          });
        }

        // Line price column: total efectivo en grande,
        // si hay descuento, original tachado debajo en pequeño
        const itemLinePrice = el('div', {
          cls: 'pp-assistant__cart-item-line-price',
          text: fmtMoney(effectiveLine),
        });
        const itemLineKids = [itemLinePrice];
        if (hasDiscount) {
          itemLineKids.push(
            el('div', {
              cls: 'pp-assistant__cart-item-line-original',
              text: fmtMoney(originalLine),
            })
          );
        }
        const itemLine = el('div', {
          cls: 'pp-assistant__cart-item-line',
          children: itemLineKids,
        });

        // Trash icon arriba-derecha del item (junto al line price).
        // Más sutil que el círculo X anterior — un ícono de basurero
        // 14px con hover suave. Para ajustar cantidades el cliente va
        // a /cart vía el botón "Ver carrito y ajustar cantidades →".
        const lineKey = item.key;
        const removeBtn = el('button', {
          cls: 'pp-assistant__cart-remove',
          attrs: {
            type: 'button',
            'data-pp-cart-remove': lineKey,
            'aria-label': 'Quitar del carrito',
            title: 'Quitar del carrito',
          },
          children: [svgIcon('0 0 24 24', ICONS.trash)],
        });
        // El trash se mete dentro del itemLine (a la derecha del precio)
        // para que tome menos ancho horizontal del item.
        itemLine.insertBefore(removeBtn, itemLine.firstChild);

        const itemRow = el('div', {
          cls: 'pp-assistant__cart-item',
          children: [
            itemImg,
            el('div', {
              cls: 'pp-assistant__cart-item-body',
              children: [itemTitle, itemMeta, itemBreakdown].filter(Boolean),
            }),
            itemLine,
          ].filter(Boolean),
        });
        itemsWrap.appendChild(itemRow);
      });

      if (items.length > displayLimit) {
        itemsWrap.appendChild(
          el('div', {
            cls: 'pp-assistant__cart-more',
            text: `+ ${items.length - displayLimit} producto(s) más`,
          })
        );
      }
      this.cartExpandedBody.appendChild(itemsWrap);

      // Ahorros destacados
      if (totalDiscount > 0) {
        this.cartExpandedBody.appendChild(
          el('div', {
            cls: 'pp-assistant__cart-discount',
            children: [
              el('span', {
                cls: 'pp-assistant__cart-discount-icon',
                text: '🏷️',
              }),
              el('span', {
                cls: 'pp-assistant__cart-discount-text',
                children: [
                  document.createTextNode('Ahorras '),
                  el('strong', { text: fmtMoney(totalDiscount) }),
                  document.createTextNode(' en descuentos por volumen'),
                ],
              }),
            ],
          })
        );
      }

      // Row Subtotal — el monto real del cart antes de envío. Va arriba
      // del row Envío para que sea claro qué monto se está mostrando.
      this.cartExpandedBody.appendChild(
        el('div', {
          cls: 'pp-assistant__cart-subtotal',
          children: [
            el('span', { text: 'Subtotal' }),
            el('strong', { text: fmtMoney(subtotal) }),
          ],
        })
      );

      // Row "Envío" — placeholder hasta que tengamos rates en el cart.
      this.cartExpandedBody.appendChild(
        el('div', {
          cls: 'pp-assistant__cart-subtotal pp-assistant__cart-shipping-row',
          children: [
            el('span', { text: 'Envío' }),
            el('span', {
              cls: 'pp-assistant__cart-shipping-note',
              text: 'Calculado en checkout',
            }),
          ],
        })
      );

      // CTA primaria: Ver carrito completo (para ajustar cantidades —
      // el assistant solo agrega qty=1, hace falta editar antes de
      // checkout). Checkout queda como link secundario.
      this.cartExpandedBody.appendChild(
        el('a', {
          cls: 'pp-assistant__cart-cta',
          attrs: { href: '/cart' },
          text: 'Ver carrito y ajustar cantidades →',
        })
      );

      this.cartExpandedBody.appendChild(
        el('a', {
          cls: 'pp-assistant__cart-secondary',
          attrs: { href: '/checkout' },
          text: 'Ir al checkout directo',
        })
      );

      // Nota fiscal — los precios mostrados en PP ya incluyen IVA.
      this.cartExpandedBody.appendChild(
        el('p', {
          cls: 'pp-assistant__cart-iva-note',
          text: 'Todos los precios incluyen IVA.',
        })
      );
    }

    _toggleCart() {
      // En mobile, tap en la barra mini cambia a la tab Carrito (full
      // screen) en vez de expandir parcial. El drawer parcial se perdía
      // pegado al fondo y mermaba el área visible del cart.
      if (window.matchMedia('(max-width: 749px)').matches) {
        this._switchTab('cart');
        return;
      }

      const isExpanded = this.cartToggleBtn.getAttribute('aria-expanded') === 'true';
      if (isExpanded) {
        this.cartToggleBtn.setAttribute('aria-expanded', 'false');
        this.cartExpandedBody.setAttribute('hidden', '');
        this.cartSection.classList.remove('pp-assistant__cart-dock--expanded');
      } else {
        this.cartToggleBtn.setAttribute('aria-expanded', 'true');
        this.cartExpandedBody.removeAttribute('hidden');
        this.cartSection.classList.add('pp-assistant__cart-dock--expanded');
        // Scroll a top de expanded para que se vea
        requestAnimationFrame(() => {
          this.cartExpandedBody.scrollTop = 0;
        });
      }
    }

    async _addToCart(variantId, productTitle, quantity, opciones) {
      if (!variantId) return;
      // Shopify /cart/add.js requiere ID numérico (no GID). El SSE
      // event nos manda variant_id como gid://shopify/ProductVariant/N.
      const numericId = extractNumericId(variantId);
      if (!numericId) {
        this._showToast('Variante inválida (no se pudo extraer ID)', { error: true });
        return false;
      }
      // Max 9999 (matches el tool schema del backend). El stepper visual
      // de las cards aún limita a 99, pero Paco vía add_to_cart puede
      // mandar más grande para B2B con pedidos grandes.
      const qty = Math.max(1, Math.min(9999, parseInt(quantity, 10) || 1));
      let success = true;
      let errorMessage = '';
      let estado = 0;

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          // Shape recomendado moderno: items array. Compatible con
          // multi-currency, sections refresh, etc.
          body: JSON.stringify({
            items: [{ id: numericId, quantity: qty }],
          }),
        });
        estado = res.status;
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          success = false;
          errorMessage = err.description || err.message || `HTTP ${res.status}`;
        }
      } catch (err) {
        success = false;
        errorMessage = err.message || 'error de red';
      }
      // Lo que de verdad contestó Shopify, para el acuse (_agregarConAcuse).
      this._ultimoAdd = { ok: success, status: estado, error: success ? null : errorMessage };

      // SIEMPRE refresca cart, incluso si Shopify devolvió error. Razón:
      // un 422 "Solo se añadieron 2 de 34 por stock" deja al cart con
      // items nuevos parcialmente. Sin refresh el user no los ve hasta
      // hacer reload de la página. Reportado por el usuario.
      await this._loadCart().catch(() => {});

      if (success) {
        const qtyLabel = qty > 1 ? ` (×${qty})` : '';
        // Con acuse, «Agregado» lo dice quien comprobó el carrito, no este 200.
        if (!(opciones && opciones.sinToastDeExito)) {
          this._showToast(`Agregado: ${truncate(productTitle, 50)}${qtyLabel}`);
        }
        document.dispatchEvent(new CustomEvent('pp:cart:updated'));
        return true;
      } else {
        this._showToast('No se pudo agregar: ' + errorMessage, { error: true });
        return false;
      }
    }

    /**
     * Cambia la cantidad de un line item via /cart/change.js. Acepta el
     * line "key" (string opaco que Shopify usa para identificar la línea).
     * Optimistic UI: marca la fila como loading inmediatamente.
     *
     * Returns true en éxito.
     */
    async _changeCartQty(lineKey, quantity) {
      if (!lineKey) return false;
      const qty = Math.max(0, Math.min(999, parseInt(quantity, 10) || 0));
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ id: lineKey, quantity: qty }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || `HTTP ${res.status}`);
        }
        // /cart/change.js responde con el carrito completo: esto es una
        // lectura buena, y como tal apaga `cartLoadError` (vía _setCartData).
        this._setCartData(await res.json());
        this._renderCart();
        document.dispatchEvent(new CustomEvent('pp:cart:updated'));
        return true;
      } catch (err) {
        this._showToast(
          'No se pudo actualizar: ' + (err.message || 'error desconocido'),
          { error: true }
        );
        // Re-sincroniza por si quedó en estado inconsistente
        this._loadCart();
        return false;
      }
    }

    // ════════════════════════════════════════════════════════════
    // Product detail modal — bottom sheet con tabs (mobile primary)
    // ════════════════════════════════════════════════════════════

    _buildProductModal() {
      this.productModalState = { product: null, qty: 1, activeTab: 'desc' };

      const handle = el('div', {
        cls: 'pp-assistant__product-modal-handle',
        attrs: { 'aria-hidden': 'true' },
      });

      const closeBtn = el('button', {
        cls: 'pp-assistant__product-modal-close',
        attrs: { type: 'button', 'aria-label': 'Cerrar' },
        children: [svgIcon('0 0 24 24', ICONS.close)],
      });

      this.productModalImg = el('img', {
        cls: 'pp-assistant__product-modal-img',
        attrs: { alt: '', decoding: 'async' },
      });
      const gallery = el('div', {
        cls: 'pp-assistant__product-modal-gallery',
        children: [this.productModalImg],
      });

      this.productModalPrice = el('div', { cls: 'pp-assistant__product-modal-price' });
      this.productModalTitle = el('h3', { cls: 'pp-assistant__product-modal-title' });
      this.productModalSku = el('div', { cls: 'pp-assistant__product-modal-sku' });
      const modalHeader = el('div', {
        cls: 'pp-assistant__product-modal-header',
        children: [this.productModalPrice, this.productModalTitle, this.productModalSku],
      });

      const tabs = [
        { id: 'desc', label: 'Descripción' },
        { id: 'uso', label: 'Modo de uso' },
        { id: 'ficha', label: 'Ficha técnica' },
        { id: 'precios', label: 'Precios por volumen' },
      ];
      this.productModalTabs = {};
      this.productModalPanels = {};
      const tabsRow = el('div', {
        cls: 'pp-assistant__product-modal-tabs',
        attrs: { role: 'tablist' },
        children: tabs.map((t) => {
          const btn = el('button', {
            cls: 'pp-assistant__product-modal-tab',
            attrs: { type: 'button', role: 'tab', 'data-pp-modal-tab': t.id },
            text: t.label,
          });
          this.productModalTabs[t.id] = btn;
          return btn;
        }),
      });

      const tabContent = el('div', {
        cls: 'pp-assistant__product-modal-tab-content',
        children: tabs.map((t) => {
          const panel = el('div', {
            cls: 'pp-assistant__product-modal-panel',
            attrs: { 'data-pp-modal-panel': t.id, hidden: t.id !== 'desc' },
          });
          this.productModalPanels[t.id] = panel;
          return panel;
        }),
      });

      this.productModalQtyDec = el('button', {
        cls: 'pp-assistant__product-modal-qty-btn',
        attrs: { type: 'button', 'data-pp-modal-qty-dir': 'dec', 'aria-label': 'Restar' },
        text: '−',
      });
      this.productModalQtyVal = el('span', {
        cls: 'pp-assistant__product-modal-qty-val',
        text: '1',
      });
      this.productModalQtyInc = el('button', {
        cls: 'pp-assistant__product-modal-qty-btn',
        attrs: { type: 'button', 'data-pp-modal-qty-dir': 'inc', 'aria-label': 'Sumar' },
        text: '+',
      });
      const footerStepper = el('div', {
        cls: 'pp-assistant__product-modal-stepper',
        children: [this.productModalQtyDec, this.productModalQtyVal, this.productModalQtyInc],
      });
      this.productModalAddBtn = el('button', {
        cls: 'pp-assistant__product-modal-add',
        attrs: { type: 'button' },
        text: 'Agregar',
      });
      const footer = el('div', {
        cls: 'pp-assistant__product-modal-footer',
        children: [footerStepper, this.productModalAddBtn],
      });

      const body = el('div', {
        cls: 'pp-assistant__product-modal-body',
        children: [gallery, modalHeader, tabsRow, tabContent],
      });

      this.productModalSheet = el('div', {
        cls: 'pp-assistant__product-modal-sheet',
        children: [handle, closeBtn, body, footer],
      });

      const backdrop = el('div', {
        cls: 'pp-assistant__product-modal-backdrop',
        attrs: { 'data-pp-modal-backdrop': true, 'aria-hidden': 'true' },
      });

      const modal = el('div', {
        cls: 'pp-assistant__product-modal',
        attrs: { hidden: true, role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Detalle del producto' },
        children: [backdrop, this.productModalSheet],
      });

      // Wire-up
      closeBtn.addEventListener('click', () => this._closeProductModal());
      backdrop.addEventListener('click', () => this._closeProductModal());
      tabsRow.addEventListener('click', (e) => {
        const tab = e.target.closest('[data-pp-modal-tab]');
        if (tab) this._switchModalTab(tab.getAttribute('data-pp-modal-tab'));
      });
      footerStepper.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-pp-modal-qty-dir]');
        if (btn) this._handleModalQtyStep(btn.getAttribute('data-pp-modal-qty-dir'));
      });
      this.productModalAddBtn.addEventListener('click', () => this._handleModalAdd());
      this._wireModalSwipeDown(this.productModalSheet);

      return modal;
    }

    _switchModalTab(tabId) {
      Object.entries(this.productModalTabs).forEach(([id, btn]) => {
        btn.classList.toggle('pp-assistant__product-modal-tab--active', id === tabId);
      });
      Object.entries(this.productModalPanels).forEach(([id, panel]) => {
        if (id === tabId) panel.removeAttribute('hidden');
        else panel.setAttribute('hidden', '');
      });
      this.productModalState.activeTab = tabId;
    }

    /**
     * Mapea el JSON crudo de `/products/<handle>.js` (Shopify storefront
     * native endpoint) al shape interno que usa el modal del Paco. Solo
     * para entradas externas (cart drawer / future bridges) — los
     * productos recomendados del backend Paco ya vienen en este shape.
     * No incluye metafields (specs/tipo_venta) — esos los carga
     * _openProductModal async via _populateModalTabs.
     */
    _shopifyJsonToPacoShape(data) {
      const variants = (data.variants || []).map((v) => ({
        id: String(v.id),
        // _handleModalAdd lee firstVariant.variant_id (el backend Paco
        // usa esa key). Sin esto el "Agregar" del modal lanza undefined
        // como variantId y el POST a /cart/add falla.
        variant_id: String(v.id),
        title: v.title || '',
        sku: v.sku || '',
        price_mxn: (v.price || 0) / 100,
        available: !!v.available,
        options: v.options || [],
      }));
      const priceMin = variants.length
        ? Math.min.apply(null, variants.map((v) => v.price_mxn))
        : (data.price || 0) / 100;
      return {
        id: String(data.id),
        title: data.title || '',
        handle: data.handle || '',
        url: '/products/' + (data.handle || ''),
        image_url:
          data.featured_image ||
          (data.images && data.images[0]) ||
          '',
        price_min_mxn: priceMin,
        tipo_venta: 'en_almacen',
        specs: {},
        variants: variants,
      };
    }

    async _openProductModal(product) {
      if (!product) return;
      this.productModalState.product = product;
      this.productModalState.qty = 1;

      const firstVariant = (product.variants || []).find((v) => v.available)
        || (product.variants || [])[0]
        || null;
      const price = firstVariant ? firstVariant.price_mxn : product.price_min_mxn;

      this.productModalImg.src = imageUrl(product.image_url, 600);
      this.productModalImg.alt = product.title || '';
      this.productModalTitle.textContent = product.title || '';
      this.productModalPrice.textContent = price != null ? fmtMoney(price) : '';
      // Línea SKU · count · precio por unidad — mismo formato que el
      // pack-row de la card.
      const skuPackParts = [];
      const specsForPack = product.specs || {};
      const pack = parsePack(
        firstVariant && firstVariant.title && firstVariant.title !== 'Default Title'
          ? firstVariant.title
          : specsForPack.unidades
      );
      if (firstVariant && firstVariant.sku) {
        skuPackParts.push(`SKU ${firstVariant.sku}`);
      }
      if (pack && pack.count > 1) {
        skuPackParts.push(`${pack.count} ${pack.unitPlural}`);
        if (price != null && pack.count > 0) {
          const perUnit = price / pack.count;
          skuPackParts.push(`${fmtMoney(perUnit)} c/${pack.unit}`);
        }
      }
      this.productModalSku.textContent = skuPackParts.join(' · ');
      this.productModalQtyVal.textContent = '1';
      this.productModalAddBtn.disabled = !firstVariant || (!firstVariant.available && product.tipo_venta !== 'pre_venta');

      // Reset tab
      this._switchModalTab('desc');

      // Pre-poblar pestañas con lo que tenemos sin fetch
      this._populateModalTabs(product, null);

      // Show
      this.productModal.hidden = false;
      requestAnimationFrame(() => {
        this.productModal.classList.add('is-open');
      });
      document.body.classList.add('pp-assistant-modal-open');

      const baseUrl = product.url
        ? product.url.split('?')[0].split('#')[0]
        : '';

      // Fetch en paralelo: descripción HTML + tiers desde metafield (si no
      // viene resuelto). Ambos llenan UI cuando lleguen.
      const tasks = [];
      let shopifyProductData = null;
      let fetchedTiers = null;

      if (!product.description && baseUrl) {
        tasks.push(
          fetch(baseUrl + '.js', { credentials: 'same-origin' })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => { shopifyProductData = data; })
            .catch(() => null)
        );
      }

      // Tiers fallback: si product no trae volume_tiers, leerlos desde el
      // template alternate /products/handle?view=tiers que devuelve el JSON
      // crudo del metafield custom.volume_tiers.
      const noTiers = !Array.isArray(product.volume_tiers) || !product.volume_tiers.length;
      if (noTiers && baseUrl) {
        tasks.push(
          fetch(baseUrl + '?view=tiers', { credentials: 'same-origin' })
            .then((r) => (r.ok ? r.text() : ''))
            .then((txt) => {
              const cleaned = (txt || '').trim();
              if (!cleaned || cleaned === '[]') return;
              try {
                const arr = JSON.parse(cleaned);
                if (Array.isArray(arr) && arr.length) fetchedTiers = arr;
              } catch (e) { /* invalid JSON */ }
            })
            .catch(() => null)
        );
      }

      if (tasks.length) {
        await Promise.all(tasks);
        // Si encontramos tiers via fetch, los inyectamos al product para
        // que el normalize loop los recoja.
        if (fetchedTiers && (!product.volume_tiers || !product.volume_tiers.length)) {
          product.volume_tiers = fetchedTiers;
        }
        this._populateModalTabs(product, shopifyProductData);
      }
    }

    _populateModalTabs(product, shopifyProduct) {
      const rawHtml = (shopifyProduct && shopifyProduct.description)
        || product.description
        || product.body_html
        || '';

      // Parsea el body_html y lo divide en 3 buckets — descripción, modo
      // de uso y ficha técnica — buscando headings con keywords. El
      // body_html típico de PP trae secciones con strong/h3 titulados
      // "Especificaciones", "Características", "Modo de uso", etc.
      const split = this._splitBodyHtml(rawHtml);

      this.productModalPanels.desc.innerHTML = split.desc.trim()
        ? `<div class="pp-assistant__product-modal-html">${split.desc}</div>`
        : '<p class="pp-assistant__product-modal-empty">Sin descripción disponible.</p>';

      const modoUsoExtra = (product.modo_de_uso || product.uso || '').toString();
      const usoCombined = (split.uso + modoUsoExtra).trim();
      this.productModalPanels.uso.innerHTML = usoCombined
        ? `<div class="pp-assistant__product-modal-html">${usoCombined}</div>`
        : '<p class="pp-assistant__product-modal-empty">Información de uso por confirmar — pregúntale a Paco si tienes dudas específicas.</p>';

      // Ficha técnica: prioriza el HTML del body (más rico, trae Calibre,
      // Apertura, Color, Cierre, etc.), pero lo CONVIERTE de bullets
      // <ul><li><strong>Key:</strong> value</li></ul> a tabla key/value
      // bonita (dl con rows alternados). Texto suelto fuera de bullets
      // queda como nota debajo. Cae a fallback con specs estructurados
      // si el body no tiene ficha.
      const firstVariant = (product.variants || []).find((v) => v.available)
        || (product.variants || [])[0]
        || null;
      const specs = product.specs || {};
      const fichaHtml = split.ficha.trim();
      const parsedFicha = fichaHtml ? this._parseSpecsHtml(fichaHtml) : null;
      if (parsedFicha && (parsedFicha.pairs.length || parsedFicha.notesHtml)) {
        const dl = parsedFicha.pairs.length
          ? `<dl class="pp-assistant__product-modal-specs">${parsedFicha.pairs.map(([k, vHtml]) =>
              `<div><dt>${escapeHtml(k)}</dt><dd>${vHtml}</dd></div>`).join('')}</dl>`
          : '';
        const notes = parsedFicha.notesHtml
          ? `<div class="pp-assistant__product-modal-specs-notes">${parsedFicha.notesHtml}</div>`
          : '';
        this.productModalPanels.ficha.innerHTML = dl + notes;
      } else {
        const specRows = [];
        if (firstVariant && firstVariant.sku) specRows.push(['SKU', firstVariant.sku]);
        if (specs.unidades) specRows.push(['Unidades por paquete', specs.unidades]);
        if (specs.material) specRows.push(['Material', specs.material]);
        if (specs.calibre) specRows.push(['Calibre', specs.calibre]);
        // Ver nota en la tarjeta de producto: la llave viva es grosor_flauta,
        // la vieja sigue en el indice de Paco. Se leen las dos.
        const flautaFicha = specs.grosor_flauta || specs.tipo_de_flauta;
        if (flautaFicha) specRows.push(['Grosor / flauta', flautaFicha]);
        if (specs.dimensiones) specRows.push(['Dimensiones', specs.dimensiones]);
        if (specs.peso) specRows.push(['Peso', specs.peso]);
        this.productModalPanels.ficha.innerHTML = specRows.length
          ? `<dl class="pp-assistant__product-modal-specs">${specRows.map(([k, v]) =>
              `<div><dt>${escapeHtml(k)}</dt><dd>${escapeHtml(String(v))}</dd></div>`).join('')}</dl>`
          : '<p class="pp-assistant__product-modal-empty">Sin ficha técnica disponible.</p>';
      }

      // Precios por volumen — acepta dos formatos:
      //   1) Backend Paco resolved: [{ min_qty, price_mxn }, ...]
      //   2) Metafield raw custom.volume_tiers: [{ min, off }, ...]
      // donde `off` es % de descuento sobre el precio base.
      const basePrice = firstVariant ? firstVariant.price_mxn : product.price_min_mxn;
      const rawTiers = Array.isArray(product.volume_tiers) ? product.volume_tiers : [];
      const tiers = this._normalizeTiers(rawTiers, basePrice);
      this._renderModalPrecios(tiers, basePrice);
    }

    /**
     * Normaliza una lista de tiers a {min, price, off} sin importar
     * si viene como {min_qty, price_mxn} o {min, off}.
     */
    _normalizeTiers(rawTiers, basePrice) {
      if (!Array.isArray(rawTiers) || !rawTiers.length) return [];
      const out = rawTiers.map((t) => {
        const min = Number(t.min_qty != null ? t.min_qty : t.min);
        let price = t.price_mxn != null ? Number(t.price_mxn) : null;
        let off = t.off != null ? Number(t.off) : (t.discount_pct != null ? Number(t.discount_pct) : null);
        if (price == null && off != null && basePrice) {
          price = basePrice * (1 - off / 100);
        } else if (off == null && price != null && basePrice) {
          off = Math.round(((basePrice - price) / basePrice) * 100);
        }
        return { min, price, off };
      }).filter((t) => Number.isFinite(t.min) && t.min > 0);
      // Ordena ascendente por qty
      out.sort((a, b) => a.min - b.min);
      return out;
    }

    _renderModalPrecios(tiers, basePrice) {
      const hasPrecios = tiers.length > 0;
      this.productModalTabs.precios.hidden = !hasPrecios;
      if (!hasPrecios) {
        this.productModalPanels.precios.innerHTML = '';
        if (this.productModalState.activeTab === 'precios') {
          this._switchModalTab('desc');
        }
        return;
      }
      const tierRows = tiers.map((t) => {
        const priceCell = t.price != null
          ? `${fmtMoney(t.price)} c/u`
          : '—';
        const offCell = t.off != null ? `−${Math.round(t.off)}%` : '';
        return `<tr>
          <td>${t.min}+ pzas</td>
          <td>${priceCell}</td>
          <td>${offCell}</td>
        </tr>`;
      }).join('');
      this.productModalPanels.precios.innerHTML = `
        <table class="pp-assistant__product-modal-tiers">
          <thead><tr><th>Desde</th><th>Precio c/u</th><th>Ahorro</th></tr></thead>
          <tbody>${tierRows}</tbody>
        </table>
        <p class="pp-assistant__product-modal-tiers-note">
          El descuento por volumen se aplica automáticamente al sumar la cantidad
          en el carrito.
        </p>`;
    }

    /**
     * Convierte un fragmento HTML de ficha técnica (típicamente <ul> con
     * <li><strong>Clave:</strong> Valor</li>) en {pairs, notesHtml} donde
     * pairs es un array [['Clave','Valor'], ...] y notesHtml es el HTML
     * suelto (párrafos fuera del UL).
     *
     * Robusto a:
     *   - Strong/b al inicio del li como label
     *   - ":" o "." trailing en el label (los limpia)
     *   - Múltiples ULs/OLs en el mismo fragment
     *   - Texto suelto entre listas (se acumula en notesHtml)
     */
    _parseSpecsHtml(html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const pairs = [];
      const noteParts = [];
      Array.from(tmp.childNodes).forEach((node) => {
        if (node.nodeType === 3) {
          const txt = (node.textContent || '').trim();
          if (txt) noteParts.push(txt);
          return;
        }
        if (node.nodeType !== 1) return;
        if (node.tagName === 'UL' || node.tagName === 'OL') {
          Array.from(node.querySelectorAll(':scope > li')).forEach((li) => {
            const strong = li.querySelector('strong, b');
            if (strong) {
              const key = (strong.textContent || '').replace(/[:.\s]+$/, '').trim();
              const clone = li.cloneNode(true);
              const strongClone = clone.querySelector('strong, b');
              if (strongClone) strongClone.remove();
              const valueHtml = (clone.innerHTML || '').replace(/^[:.\s]+/, '').trim();
              if (key && valueHtml) {
                pairs.push([key, valueHtml]);
              } else if (key && !valueHtml) {
                pairs.push([key, '']);
              }
            } else {
              const txt = (li.innerHTML || '').trim();
              if (txt) noteParts.push(`<p>${txt}</p>`);
            }
          });
        } else if (/^H[1-6]$/.test(node.tagName)) {
          // Headings extra dentro del bucket ficha — los conservamos como
          // nota porque pueden ser sub-secciones.
          noteParts.push(node.outerHTML);
        } else {
          // P / DIV / etc. que no son lista: nota.
          const txt = (node.textContent || '').trim();
          if (txt) noteParts.push(node.outerHTML);
        }
      });
      return { pairs, notesHtml: noteParts.join('') };
    }

    /**
     * Toma un body_html y lo divide en {desc, uso, ficha} buscando headings
     * (h1-h6 o párrafos cuyo único contenido es <strong>/<b>). Las
     * keywords que dirigen el switch:
     *   - "modo de uso" / "uso" / "cómo usar" / "instrucciones" → uso
     *   - "especificaciones" / "características" / "ficha técnica" /
     *     "detalles técnicos" → ficha
     *   - todo lo demás se queda en el bucket actual (default desc)
     * Los headings que disparan switch NO se incluyen en el output (la pill
     * de la pestaña ya identifica la sección).
     */
    _splitBodyHtml(html) {
      const out = { desc: '', uso: '', ficha: '' };
      if (!html) return out;
      const tmp = document.createElement('div');
      tmp.innerHTML = html;

      const isHeadingTag = (tag) => /^H[1-6]$/.test(tag);
      const sectionFromText = (text) => {
        const lower = (text || '').toLowerCase().replace(/[:.]+$/, '').trim();
        if (!lower) return null;
        if (/(modo de uso|c[oó]mo usar|como usar|instruccion|aplicaci[oó]n|aplicar)/.test(lower)) {
          return 'uso';
        }
        if (/(especificaci|caracter[íi]stica|ficha t[ée]cnica|detalle.*t[ée]cnico|datos t[ée]cnico)/.test(lower)) {
          return 'ficha';
        }
        if (/^descripci/.test(lower)) {
          return 'desc';
        }
        return null;
      };
      const detectSection = (node) => {
        if (node.nodeType !== 1) return null;
        const tag = node.tagName;
        if (isHeadingTag(tag)) {
          return sectionFromText(node.textContent);
        }
        if (tag === 'P') {
          const strong = node.querySelector('strong, b');
          if (strong) {
            const pText = (node.textContent || '').trim();
            const sText = (strong.textContent || '').trim();
            // Si el strong cubre prácticamente todo el párrafo, lo
            // tratamos como heading.
            if (sText && pText && pText.length - sText.length <= 2) {
              return sectionFromText(sText);
            }
          }
        }
        return null;
      };

      let current = 'desc';
      Array.from(tmp.childNodes).forEach((node) => {
        if (node.nodeType === 3) {
          // text node — si tiene contenido lo agregamos al bucket actual
          const txt = (node.textContent || '').trim();
          if (txt) out[current] += node.textContent;
          return;
        }
        if (node.nodeType !== 1) return;
        const section = detectSection(node);
        if (section) {
          current = section;
          return; // saltamos el heading, no lo emitimos
        }
        out[current] += node.outerHTML;
      });

      return out;
    }

    _closeProductModal() {
      this.productModal.classList.remove('is-open');
      this.productModalSheet.style.transform = '';
      document.body.classList.remove('pp-assistant-modal-open');
      const wasStandalone = this._modalStandalone;
      this._modalStandalone = false;
      setTimeout(() => {
        this.productModal.hidden = true;
        // Si el modal vino del bridge externo (cart drawer), también
        // hide del overlay — el chat no estaba abierto, no queremos
        // dejarlo asomando atrás. Dispatch event para que el cart
        // drawer pueda reabrirse en el contexto donde estaba.
        if (wasStandalone) {
          this.overlay.classList.remove('pp-assistant__overlay--modal-only');
          this.overlay.setAttribute('hidden', '');
          document.dispatchEvent(
            new CustomEvent('pp:product-modal-closed', {
              detail: { fromCartDrawer: true },
            })
          );
        }
      }, 220);
    }

    _handleModalQtyStep(dir) {
      let qty = parseInt(this.productModalQtyVal.textContent, 10) || 1;
      if (dir === 'inc') qty = Math.min(99, qty + 1);
      else if (dir === 'dec') qty = Math.max(1, qty - 1);
      this.productModalState.qty = qty;
      this.productModalQtyVal.textContent = String(qty);
    }

    _handleModalAdd() {
      const product = this.productModalState.product;
      if (!product) return;
      const firstVariant = (product.variants || []).find((v) => v.available)
        || (product.variants || [])[0]
        || null;
      if (!firstVariant) return;
      const qty = this.productModalState.qty || 1;
      const variantId = String(firstVariant.variant_id);
      this.productModalAddBtn.disabled = true;
      this._addToCart(variantId, product.title, qty).finally(() => {
        this.productModalAddBtn.disabled = false;
        this._closeProductModal();
      });
    }

    /**
     * Swipe-down to close.
     *
     * Reglas:
     *   - Drag SIEMPRE arrastra desde el handle (la barrita arriba) o el
     *     header (precio/título/sku) o las tabs — esas zonas no scrollean.
     *   - Drag desde el body solo si el scrollTop está en 0 (sino el user
     *     está scrolleando contenido interno y no queremos interferir).
     *   - Footer (stepper + Agregar) no inicia drag — son botones críticos.
     *   - Threshold de cierre: 100px o velocidad > 0.5px/ms.
     */
    _wireModalSwipeDown(sheet) {
      let startY = null;
      let startT = 0;
      let currentY = null;
      let dragging = false;

      const shouldStartDrag = (target) => {
        // El footer NO arrastra (botones de acción).
        if (target.closest && target.closest('.pp-assistant__product-modal-footer')) return false;
        // Handle, header y tabs siempre permiten drag.
        if (target.closest && (
          target.closest('.pp-assistant__product-modal-handle')
          || target.closest('.pp-assistant__product-modal-header')
          || target.closest('.pp-assistant__product-modal-tabs')
          || target.closest('.pp-assistant__product-modal-close')
        )) {
          return true;
        }
        // Body interno: solo si está en top del scroll.
        const body = sheet.querySelector('.pp-assistant__product-modal-body');
        if (body && body.contains(target)) {
          return body.scrollTop <= 0;
        }
        // Resto del sheet (gallery, etc.): permitir.
        return true;
      };

      sheet.addEventListener('touchstart', (e) => {
        if (!shouldStartDrag(e.target)) return;
        const t = e.touches[0];
        startY = t.clientY;
        currentY = t.clientY;
        startT = performance.now();
        dragging = true;
        sheet.style.transition = 'none';
      }, { passive: true });

      sheet.addEventListener('touchmove', (e) => {
        if (!dragging) return;
        currentY = e.touches[0].clientY;
        const delta = currentY - startY;
        if (delta < 0) {
          sheet.style.transform = '';
          return;
        }
        // Resistencia leve después de 200px para que se sienta "elastic".
        const eased = delta < 200 ? delta : 200 + (delta - 200) * 0.4;
        sheet.style.transform = `translateY(${eased}px)`;
      }, { passive: true });

      const finishDrag = () => {
        if (!dragging) return;
        dragging = false;
        sheet.style.transition = '';
        const delta = (currentY || 0) - (startY || 0);
        const elapsed = performance.now() - startT;
        const velocity = elapsed > 0 ? delta / elapsed : 0;
        if (delta > 100 || velocity > 0.5) {
          this._closeProductModal();
        } else {
          sheet.style.transform = '';
        }
        startY = currentY = null;
      };
      sheet.addEventListener('touchend', finishDrag);
      sheet.addEventListener('touchcancel', finishDrag);
    }

    /**
     * Resuelve el lineKey real para un variantId dado (a partir del
     * cartData actual) y llama _changeCartQty con él. Si no encuentra
     * line item con ese variant, sale silencioso (probablemente está
     * desincronizado — el caller debe sincronizar después).
     */
    async _changeCartQtyForVariant(variantId, newQty) {
      if (!variantId || !this.cartData || !Array.isArray(this.cartData.items)) {
        return false;
      }
      const vidStr = String(variantId);
      const item = this.cartData.items.find(
        (i) => String(i.variant_id || i.id || '') === vidStr
      );
      if (!item || !item.key) return false;
      return this._changeCartQty(item.key, newQty);
    }

    _handleCartStep(btn) {
      const lineKey = btn.getAttribute('data-pp-cart-step');
      const dir = btn.getAttribute('data-pp-cart-dir');
      if (!lineKey || !this.cartData) return;
      const item = this.cartData.items.find((i) => i.key === lineKey);
      if (!item) return;
      const next = dir === 'inc' ? item.quantity + 1 : item.quantity - 1;
      if (next < 1) {
        // Si baja de 1, mejor llamarlo remove
        this._changeCartQty(lineKey, 0);
        return;
      }
      this._changeCartQty(lineKey, next);
    }

    _handleCartRemove(btn) {
      const lineKey = btn.getAttribute('data-pp-cart-remove');
      if (!lineKey) return;
      this._changeCartQty(lineKey, 0);
    }

    _showToast(message, opts) {
      if (!this.toastEl) return;
      this.toastEl.textContent = message;
      this.toastEl.classList.toggle(
        'pp-assistant__toast--error',
        !!(opts && opts.error)
      );
      this.toastEl.removeAttribute('hidden');
      if (this._toastTimer) clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => {
        this.toastEl.setAttribute('hidden', '');
      }, 3500);
    }
  }

  if (!customElements.get('pp-assistant')) {
    customElements.define('pp-assistant', PpAssistant);
  }
})();
